// packages/shared/utils/createDayTimeline.js
// Ported from web/src/WanderAppComplete.jsx:10891-15169 (single-source-of-truth timeline builder)
// Pure function — all React state must be passed via `context` argument.
// NOTE: mutates dayData.schedule and dayData.meals (preserves web's behavior).

import { parseDurationToMinutes, toTitleCase, stripEmoji, getActivityEmoji, getMealEmoji } from './itineraryHelpers.js';
import {
  parseTimeToMinutes, formatTime12, minutesToTime, calculateEndTime,
  doesCrossMidnight, minutesBetween, findTransitData
} from './timeUtils.js';
import {
  getCityCountrySync, getContinentForCountry, isIslandCity,
  getContinentHintFromText, COUNTRY_TO_CONTINENT, ISLAND_LOCATION_KEYWORDS
} from './geographyConstants.js';
import {
  DEPARTURE_AIRPORT_CITY_MAP, TRAIN_FRIENDLY_AIRPORT_ROUTES,
  safeAirportCity, getSmartTransportMode as _getSmartTransportMode, CITY_TO_AIRPORT_CODE,
  lookupCityAirportCode, validateTransportMode, resolveStation
} from './transportConstants.js';
import {
  getBreakfastHotelContext, isMealBlockedByTransport,
  cleanOvernightArrivalDay, checkOvernightTransport,
  findMealSlot, filterPreDawnItems, timelineSortComparator,
  normalizeCityName, isExplicitOvernightArrival, hasMealType,
  isResortStay, isCruiseOnlyDestination, CRUISE_SHIP_NAME_KEYWORDS,
  extractAirportCode, validateAirportCode
} from './displayGuardrails.js';
import {
  estimateFlightDurationHeuristic, FLIGHT_TIMES, HOME_CITY_MAP,
  getStaticFlightEstimate, STATIC_FLIGHT_DURATIONS
} from './flightEstimates.js';
import {
  getCityTimezoneOffset, calculateFlightArrival, CITY_TO_IANA_TIMEZONE
} from './timezoneConstants.js';
import { estimateDriveHours, shouldForceDrive, getCityCoords, greatCircleMiles } from './travelTimeEstimates.js';
import { reverseLookupAirportCity } from './transportConstants.js';
import { areSameHotel, sanitizeBaseCity } from './itineraryNormalizer.js';
import { isHotelActivity as isHotelActivityValidator } from './itineraryValidators.js';
import { extractLocationFromHotelName } from './destinationConstants.js';
import { CITY_TO_COUNTRY } from './itineraryConstants.js';
import {
  COUNTRY_TO_CURRENCY, SCHENGEN_COUNTRIES, VISA_REQUIREMENTS_US
} from './travelInfoConstants.js';

/**
 * Port of web's createDayTimeline. Pure function — all state passed via context.
 * MUTATES: dayData.schedule, dayData.meals (matches web behavior for caller compat).
 *
 * @param {Object} dayData - Single day's data
 * @param {Object} opts - { isFirstDay, isLastDay, context }
 * @param {Object} opts.context - All React state the function reads (see below)
 * @returns {Array} validatedTimeline - Flat sorted array of timeline items
 */
export function createDayTimeline(dayData, { isFirstDay, isLastDay, context } = {}) {
  const {
    // Core itinerary
    itinerary,
    selectedDestination,
    // User/profile
    userProfile,
    profile,
    // Transport
    arrivalFlight,
    driveDetails,
    transportationMode,
    transitTimes = {},
    transportModes = {},
    dynamicTransportModes = {},
    destinationAirports = [],
    effectiveReturnMode,
    departureFlight,
    quickTransportResult,
    airportDistances = {},
    calculatedDriveTime,
    customTransportDesc,
    transitTimesRef = { current: {} },
    validatedFlightDurations = { current: {} },
    // Accommodation
    selectedHotel,
    accommodationType,
    generatedHotelName,
    customAccommodationName,
    tripType,
    // Builder
    tripBuilderStops = [],
    hasOvernightOutbound = false,
    // TASK B: per-leg transport picks from trip builder (keyed by `${fromId}→${toId}`,
    // `dept→${firstStopId}`, or `${lastStopId}→return`). Each entry may carry
    // { mode, departureAirport: {code,name}, arrivalAirport: {code,name}, isUserOverride }.
    // When the user explicitly picked an airport in the builder, honor it in the
    // flight card and transit labels instead of the LLM/default airport.
    perLegTransport = {},
    // Other
    dynamicGeographyCache = {},
    // Logger
    logger = { log: () => {}, warn: () => {}, error: () => {} }
  } = context || {};

  const debugLog = logger.log;
  const debugWarn = logger.warn;
  const debugError = logger.error;
  const timelineLog = logger.log;
  const parseTime = parseTimeToMinutes;

  // Inline port of checkIfDomestic from WanderAppComplete.jsx:7753-7805
  const checkIfDomestic = (destination, homeCity = null) => {
    if (!destination) return false;
    const home = homeCity || userProfile?.preferences?.homeCity || 'New York';
    const homeCountry = getCityCountrySync(home);
    let destName = destination.name || destination || '';
    if (typeof destName !== 'string') destName = '';
    if (destination.isMultiStop && destination.stopNames?.length > 0) {
      destName = destination.stopNames[0];
    }
    const hotelName = destination.hotel || destination.hotelName || '';
    let destCountry = getCityCountrySync(destName);
    if (!destCountry && hotelName) {
      destCountry = getCityCountrySync(hotelName);
    }
    if (!destCountry && itinerary?.days?.[0]?.baseCity) {
      destCountry = getCityCountrySync(itinerary.days[0].baseCity);
    }
    if (!destCountry && itinerary?.arrivalAirport) {
      const arrAirport = typeof itinerary.arrivalAirport === 'string'
        ? itinerary.arrivalAirport
        : (itinerary.arrivalAirport?.name || itinerary.arrivalAirport?.code || '');
      const cityFromAirport = arrAirport
        .replace(/^[A-Z]{2,4}\s*[-–]\s*/, '')
        .replace(/\s*(international|regional|municipal|domestic)?\s*airport\s*$/i, '')
        .trim();
      if (cityFromAirport) {
        destCountry = getCityCountrySync(cityFromAirport);
      }
    }
    if (homeCountry && destCountry) {
      const isDomestic = homeCountry === destCountry;
      debugLog(`🏠 Domestic check: "${destName}" (${destCountry}) vs "${home}" (${homeCountry}) → ${isDomestic ? 'DOMESTIC' : 'INTERNATIONAL'}`);
      return isDomestic;
    }
    debugLog(`🏠 Domestic check: "${destName}" - could not determine country, defaulting to INTERNATIONAL`);
    return false;
  };

  // ============================================================
  // Begin port of web/src/WanderAppComplete.jsx:10891-15169
  // ============================================================

  let timeline = [];
  let cutoffStartTime = 0;
  let cutoffEndTime = 24 * 60;
  let isOvernightDepartureDay = false;

    // parseDurationToMinutes — using the shared version imported from @wander/shared via itineraryHelpers

    // IMPORTANT: Calculate dayIndex FIRST - needed throughout the function
    // The day number (1-based) is the most reliable identifier since normalization preserves it.
    // Object reference comparison (===) fails on normalized data (deep clones).
    let dayIndex = -1;
    if (itinerary?.days) {
      // Primary: match by day number (unique, reliable after normalization)
      if (dayData?.day != null) {
        dayIndex = itinerary.days.findIndex(d => d.day === dayData.day);
      }
      // Fallback: try object reference (works for non-normalized data)
      if (dayIndex === -1) {
        dayIndex = itinerary.days.findIndex(d => d === dayData);
      }
      // Fallback: match by baseCity + position in that city's sequence
      if (dayIndex === -1) {
        dayIndex = itinerary.days.findIndex(d =>
          d.baseCity === dayData.baseCity &&
          d.day === dayData.day
        );
      }
      if (dayIndex === -1) {
        debugWarn(`⚠️ createDayTimeline: Could not find dayIndex for dayData`, dayData?.baseCity, dayData?.day);
        // Use day number (1-based) to derive 0-based index as last resort
        dayIndex = dayData?.day ? dayData.day - 1 : Math.max(0, isFirstDay ? 0 : 1);
      }
    }

    timelineLog(`📅 createDayTimeline: dayIndex=${dayIndex}, isFirstDay=${isFirstDay}, baseCity=${dayData?.baseCity}`);

    // For inter-city travel days: track separate windows for pre-checkout and post-checkin activities
    let preCheckoutCutoffEnd = null; // Set on inter-city travel days to allow morning activities
    let postCheckinCutoffStart = null; // Set on inter-city travel days
    // D3: Track transport departure/arrival minutes for meal blocking
    let icTransportDepartureMinutes = null;
    let icTransportArrivalMinutes = null;

    // Wrapper for inter-city travel duration parsing with smart fallback
    const parseDurationToMinutesTravel = (duration, fromCity = null, toCity = null) => {
      if (duration) return parseDurationToMinutes(duration);
      // No duration string — try Haiku-cached transport data for this city pair
      if (fromCity && toCity) {
        const pairKey = `${fromCity}|${toCity}`.toLowerCase();
        const dynamicMins = dynamicTransportModes[pairKey]?.durationMinutes;
        if (dynamicMins && dynamicMins > 0) return dynamicMins;
      }
      return 120; // Ultimate fallback if no data source available
    };

    // Get accommodation name for this day — adapts to accommodation type
    const dayHotelName = accommodationType === 'custom' && customAccommodationName
      ? customAccommodationName
      : accommodationType === 'cruise'
      ? 'Cruise Ship'
      : accommodationType === 'campervan'
      ? 'Campsite'
      : (dayData?.hotel?.name || generatedHotelName || 'City Center Hotel');
    // Accommodation-aware labels
    // Cruise distinction: Day 1 is the FIRST time boarding ("Board Ship" / "Embark"),
    // subsequent cruise days use "Return to Ship" after a day ashore. Keeping
    // them separate prevents "Return to Ship" showing on arrival day when the
    // user has never been on the ship before.
    // Also applies to cruise-only destinations (Antarctica, Galapagos, etc.) where
    // the "hotel" is actually the expedition ship even when accommodationType is
    // unset — the user's Heritage Adventurer trip was hitting this exact gap.
    const isShipAccommodation = accommodationType === 'cruise'
      || isCruiseOnlyDestination(dayData?.baseCity || primaryDestination)
      || CRUISE_SHIP_NAME_KEYWORDS.some(k => (dayData?.hotel?.name || '').toLowerCase().includes(k));
    const initialCheckinLabel = isShipAccommodation ? 'Board Ship'
      : accommodationType === 'campervan' ? 'Set Up Camp'
      : accommodationType === 'custom' ? `Check in to ${customAccommodationName || 'Accommodation'}`
      : 'Hotel Check-in';
    const checkinLabel = isShipAccommodation ? 'Return to Ship'
      : accommodationType === 'campervan' ? 'Set Up Camp'
      : accommodationType === 'custom' ? `Check in to ${customAccommodationName || 'Accommodation'}`
      : 'Hotel Check-in';
    const checkoutLabel = isShipAccommodation ? 'Disembark for Day'
      : accommodationType === 'campervan' ? 'Pack Up & Depart'
      : 'Hotel Checkout';
    const returnLabel = isShipAccommodation ? 'Return to Ship'
      : accommodationType === 'campervan' ? 'Arrive at Campsite'
      : `End of Day at ${dayHotelName}`;
    // Consistent boarding/check-in description for cruise trips
    const initialCheckinDescription = isShipAccommodation
      ? `Board ${dayData?.hotel?.name || 'the expedition ship'}, settle into your stateroom, and attend the mandatory safety briefing.`
      : null; // null = fall through to the existing per-callsite description

    // Get PREVIOUS day's hotel (for morning activities - user woke up there)
    const prevDayData = !isFirstDay && dayIndex > 0 ? itinerary?.days?.[dayIndex - 1] : null;
    const prevDayHotelName = prevDayData?.hotel?.name || dayHotelName; // Fallback to current if no previous

    // On departure day, if no hotel is set for this day, use previous day's hotel
    // (e.g., user sleeps in Ericeira Day 2 hotel, Day 3 baseCity is Porto but checkout is from Ericeira)
    const departureHotelName = isLastDay && !dayData?.hotel?.name && prevDayData?.hotel?.name
      ? prevDayData.hotel.name
      : dayHotelName;

    // Get city name for adding context to locations (improves Google Maps accuracy)
    const _rawCityContext = dayData?.baseCity || selectedDestination?.name || itinerary?.destination?.split('&')[0]?.trim() || '';
    const cityContext = sanitizeBaseCity(_rawCityContext, selectedDestination?.name || itinerary?.destination?.split('&')[0]?.trim() || _rawCityContext);

    // Helper to add city context to a location string for better Google Maps results
    const addLocationContext = (location) => {
      if (!location || !cityContext) return location;
      // If location already has city/country info, don't add
      const locLower = location.toLowerCase();
      const cityLower = cityContext.toLowerCase();
      if (locLower.includes(cityLower) || locLower.includes(',')) {
        return location;
      }
      return `${location}, ${cityContext}`;
    };

    // Add arrival logistics for Day 1 (consolidated)
    if (isFirstDay) {
      // Check if the first stop is in the user's home city — skip all arrival logistics
      const homeCityPref = userProfile?.preferences?.homeCity || '';
      const baseCityName = sanitizeBaseCity(dayData?.baseCity || '', selectedDestination?.name || '');
      const firstStopBuilderData = tripBuilderStops?.[0];
      const resolvedLocStr = (firstStopBuilderData?.resolvedLocation || '').toLowerCase().trim();
      const homeLower = homeCityPref.toLowerCase().trim();
      const baseLower = baseCityName.toLowerCase().trim();
      const resolvedFirstPart = (resolvedLocStr.split(',')[0]?.trim()) || '';
      const isHomeCityFirstStop = homeLower && baseLower && (
        baseLower.includes(homeLower) || homeLower.includes(baseLower) ||
        (resolvedLocStr.length > 0 && resolvedLocStr.includes(homeLower)) ||
        (resolvedFirstPart.length > 0 && homeLower.includes(resolvedFirstPart))
      );

      if (isHomeCityFirstStop) {
        // User is already in their home city — no flight/drive arrival logistics needed
        debugLog(`🏠 First stop "${baseCityName}" is home city "${homeCityPref}" — skipping arrival logistics`);
        let hotelDisplay = dayHotelName;
        if (selectedHotel && !['City Center', 'Near Airport', 'Beach/Waterfront', 'Tourist District', 'Budget Area', 'Let arrivo Choose'].includes(selectedHotel)) {
          hotelDisplay = selectedHotel;
        }
        timeline.push({
          type: 'hotel-checkin',
          time: '14:00',
          activity: initialCheckinLabel,
          description: `Check in to ${hotelDisplay}`,
          location: hotelDisplay,
          duration: '30 minutes',
          sortTime: 14 * 60
        });
        cutoffStartTime = 14 * 60 + 30;
      } else {
      // Use correct time based on transportation mode
      const startTime = transportationMode === 'fly' ? arrivalFlight.time : driveDetails.departureTime;
      const [hours, minutes] = startTime.split(':').map(Number);
      const arrivalMinutes = hours * 60 + minutes;

      // Check transportation mode - driving vs flying
      if (transportationMode === 'fly') {
        // FLYING: Check if this is a domestic or international flight
        const isDomestic = checkIfDomestic(selectedDestination);

        // Get arrival airport info for display (ensure code is uppercase)
        // Fallback chain: itinerary airport → user pref → destinationAirports state → first stop transport info
        const arrivalAirportCode = (
          itinerary?.arrivalAirport?.code ||
          userProfile?.preferences?.arrivalAirport ||
          (destinationAirports?.length > 0 && destinationAirports[0].code !== '???' ? destinationAirports[0].code : '') ||
          ''
        ).toUpperCase();
        const arrivalAirportName = itinerary?.arrivalAirport?.name || safeAirportCity(itinerary?.arrivalAirport) ||
          (destinationAirports?.length > 0 ? destinationAirports[0].name : '') || '';
        // Get home airport for pricing route (home airport → arrival airport)
        // Priority: trip-specific departure city → permanent profile home airport
        const homeAirportsForArr = (() => { const raw = profile?.home_airports; if (Array.isArray(raw)) return raw; if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return []; } } return []; })();
        const homeAirportCodeForArr = lookupCityAirportCode(userProfile?.preferences?.homeCity) || homeAirportsForArr?.[0]?.code || '';

        // Add arrival card (different for domestic vs international)
        // Cross-day dedup: when Day 0 (Travel Day) already shows the outbound flight,
        // don't render Day 1 arrival as a flight card — keep as logistics-only to avoid duplication
        const showAsFlightCard = !hasOvernightOutbound;

        if (!isDomestic) {
          // International: Customs + Immigration (1.5 hours)
          const customsEndMinutes = arrivalMinutes + 90;
          const customsEndHours = Math.floor(customsEndMinutes / 60);
          const customsEndMins = customsEndMinutes % 60;

          timeline.push({
            type: 'arrival-customs',
            time: startTime,
            endTime: `${String(customsEndHours).padStart(2, '0')}:${String(customsEndMins).padStart(2, '0')}`,
            activity: arrivalAirportName ? `Arrival at ${arrivalAirportName} & Immigration` : (arrivalAirportCode ? `Arrival at ${arrivalAirportCode} & Immigration` : 'Arrival & Immigration'),
            duration: '1.5 hours',
            description: homeAirportCodeForArr && arrivalAirportCode
              ? `Flight from ${homeAirportCodeForArr} to ${arrivalAirportCode}${arrivalAirportName ? ` (${arrivalAirportName})` : ''} - immigration and customs process`
              : (arrivalAirportCode
                ? `International arrival at ${arrivalAirportCode}${arrivalAirportName ? ` (${arrivalAirportName})` : ''} - immigration and customs process`
                : 'International arrival and immigration process'),
            location: homeAirportCodeForArr && arrivalAirportCode
              ? `${homeAirportCodeForArr} → ${arrivalAirportCode}`
              : (arrivalAirportName ? `${arrivalAirportName}` : (arrivalAirportCode ? `${arrivalAirportCode} Airport` : undefined)),
            sortTime: arrivalMinutes,
            airportCode: arrivalAirportCode || null,
            homeAirportCode: homeAirportCodeForArr || null,
            isArrivalFlightCard: showAsFlightCard
          });
        } else {
          // Domestic: Baggage claim + optional car rental (30-45 min)
          const domesticArrivalMinutes = arrivalMinutes + 30; // 30 min for baggage
          const domesticEndHours = Math.floor(domesticArrivalMinutes / 60);
          const domesticEndMins = domesticArrivalMinutes % 60;

          // Check if destination benefits from car rental (beach, national parks, road trips, etc)
          const destName = selectedDestination?.name?.toLowerCase() || '';
          const needsCar = destName.includes('key') || destName.includes('hawaii') ||
                          destName.includes('park') || destName.includes('beach') ||
                          destName.includes('coast') || destName.includes('route');

          timeline.push({
            type: 'arrival-domestic',
            time: startTime,
            endTime: `${String(domesticEndHours).padStart(2, '0')}:${String(domesticEndMins).padStart(2, '0')}`,
            activity: arrivalAirportName ? `Arrival at ${arrivalAirportName}` : (arrivalAirportCode ? `Arrival at ${arrivalAirportCode}` : 'Baggage Claim'),
            duration: '30 minutes',
            description: homeAirportCodeForArr && arrivalAirportCode
              ? `Flight from ${homeAirportCodeForArr} to ${arrivalAirportCode}. Collect checked bags.${needsCar ? ' Tip: Consider renting a car at the airport for easier exploration.' : ''}`
              : (needsCar
                ? `Collect checked bags${arrivalAirportCode ? ` at ${arrivalAirportCode}` : ''}. Tip: Consider renting a car at the airport for easier exploration.`
                : `Collect checked bags${arrivalAirportCode ? ` at ${arrivalAirportCode}` : ''}`),
            location: homeAirportCodeForArr && arrivalAirportCode
              ? `${homeAirportCodeForArr} → ${arrivalAirportCode}`
              : (arrivalAirportName ? `${arrivalAirportName}` : (arrivalAirportCode ? `${arrivalAirportCode} Airport` : undefined)),
            sortTime: arrivalMinutes,
            airportCode: arrivalAirportCode || null,
            homeAirportCode: homeAirportCodeForArr || null,
            isArrivalFlightCard: showAsFlightCard
          });
        }

        const customsTime = isDomestic ? 30 : 90; // 30 min domestic baggage, 90 min international customs

        // Get selected transport mode for hotel transit and use real cached time if available
        const hotelTransitKey = 'day0-hotel-transit';
        const selectedMode = transportModes[hotelTransitKey] || 'driving';
        const cachedHotelTransit = transitTimes[hotelTransitKey];

        timelineLog(`🛫 Airport to Hotel Transit: key=${hotelTransitKey}, hasCachedData=${!!cachedHotelTransit}, mode=${selectedMode}`);
        if (cachedHotelTransit) {
          timelineLog(`   Cached driving: ${cachedHotelTransit.driving?.duration || 'N/A'}`);
          timelineLog(`   Cached transit: ${cachedHotelTransit.transit?.duration || 'N/A'}`);
          timelineLog(`   Cached walking: ${cachedHotelTransit.walking?.duration || 'N/A'}`);
        }

        // Parse real transit time from cache, fallback to estimates
        let transitTime = selectedMode === 'transit' ? 60 : 30; // Default estimates
        if (cachedHotelTransit) {
          const modeData = cachedHotelTransit[selectedMode];
          if (modeData?.duration) {
            // Parse duration string like "45 mins" or "1 hr 20 mins"
            const durationStr = modeData.duration;
            const hoursMatch = durationStr.match(/(\d+)\s*hr/);
            const minsMatch = durationStr.match(/(\d+)\s*min/);
            const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
            const mins = minsMatch ? parseInt(minsMatch[1]) : 0;
            transitTime = hours * 60 + mins;
            if (transitTime === 0) transitTime = selectedMode === 'transit' ? 60 : 30; // Fallback if parse fails
          }
        }

        // Transit to hotel (starts after arrival + customs time)
        const customsEndMinutes = arrivalMinutes + customsTime;
        const hotelTransitStartMinutes = customsEndMinutes;
        const hotelArrivalMinutes = hotelTransitStartMinutes + transitTime;

        // Calculate day offset if time goes past midnight
        let transitDayOffset = 0;
        let arrivalDayOffset = 0;

        if (hotelTransitStartMinutes >= 1440) { // 24 hours = 1440 minutes
          transitDayOffset = Math.floor(hotelTransitStartMinutes / 1440);
        }
        if (hotelArrivalMinutes >= 1440) {
          arrivalDayOffset = Math.floor(hotelArrivalMinutes / 1440);
        }

        const hotelTransitHours = Math.floor((hotelTransitStartMinutes % 1440) / 60);
        const hotelTransitMins = (hotelTransitStartMinutes % 1440) % 60;
        const hotelArrivalHours = Math.floor((hotelArrivalMinutes % 1440) / 60);
        const hotelArrivalMins = (hotelArrivalMinutes % 1440) % 60;

        // Use hotel name from this day's data
        let hotelDisplay = dayHotelName;

        // If selectedHotel is a custom name, use it (but this should rarely happen)
        if (selectedHotel && !['City Center', 'Near Airport', 'Beach/Waterfront', 'Tourist District', 'Budget Area', 'Let arrivo Choose'].includes(selectedHotel)) {
          hotelDisplay = selectedHotel;
        }

        // Check if arrival airport is in a different city from the hotel/destination
        // Uses shared DEPARTURE_AIRPORT_CITY_MAP (superset of arrival airports too)
        const airportCode = itinerary?.arrivalAirport?.code || '';
        const resolvedAirportCity = DEPARTURE_AIRPORT_CITY_MAP[airportCode] || safeAirportCity(itinerary?.arrivalAirport) || '';
        const arrivalAirportCity = resolvedAirportCity.toLowerCase();
        const hotelCity = (dayData?.baseCity || selectedDestination?.name || '').toLowerCase();
        const isLongDistanceTransfer = arrivalAirportCity && hotelCity &&
          !arrivalAirportCity.includes(hotelCity) && !hotelCity.includes(arrivalAirportCity.split(',')[0]);

        if (isLongDistanceTransfer) {
          // Long distance transfer from airport to destination (e.g., MIA to Key West, Milan to Lake Como)
          const airportCode = itinerary?.arrivalAirport?.code || 'Airport';
          const destinationName = dayData?.baseCity || selectedDestination?.name || 'destination';
          const destLower = destinationName.toLowerCase();
          const airportLower = arrivalAirportCity.toLowerCase();

          // Determine best transport mode and estimate time for common routes
          let estimatedHours = 2; // Default estimate
          let transportMode = 'car';
          let transportEmoji = '🚗';
          let transportDescription = `Transfer from ${airportCode} airport`;

          // Train-friendly airport to destination routes (European)
          const trainRoutes = {
            'milan-lake como': { hours: 1, mode: 'train' },
            'milan-como': { hours: 1, mode: 'train' },
            'milan-bellagio': { hours: 1.5, mode: 'train' },
            'rome-florence': { hours: 1.5, mode: 'train' },
            'rome-naples': { hours: 1.25, mode: 'train' },
            'paris-versailles': { hours: 0.5, mode: 'train' },
            'paris-champagne': { hours: 1, mode: 'train' },
            'geneva-chamonix': { hours: 1.5, mode: 'train' },
            'zurich-lucerne': { hours: 0.75, mode: 'train' },
            'zurich-interlaken': { hours: 2, mode: 'train' },
            'barcelona-girona': { hours: 0.75, mode: 'train' },
            'london-oxford': { hours: 1, mode: 'train' },
            'london-brighton': { hours: 1, mode: 'train' },
          };

          // Car-preferred routes
          const carRoutes = {
            'miami-key west': { hours: 3.5 },
            'miami-keys': { hours: 3.5 },
            'miami-naples': { hours: 2.5 },
            'miami-fort myers': { hours: 2.5 },
            'los angeles-palm springs': { hours: 2 },
            'los angeles-san diego': { hours: 2 },
            'san francisco-napa': { hours: 1.5 },
            'san francisco-carmel': { hours: 2.5 },
            'denver-aspen': { hours: 3.5 },
            'denver-vail': { hours: 2 },
          };

          // Check for train routes
          const trainRouteKey = Object.keys(trainRoutes).find(key => {
            const [from, to] = key.split('-');
            return airportLower.includes(from) && destLower.includes(to);
          });

          if (trainRouteKey) {
            const route = trainRoutes[trainRouteKey];
            estimatedHours = route.hours;
            transportMode = 'train';
            transportEmoji = '🚆';
            transportDescription = `Train from ${airportCode} to ${destinationName} - fast and scenic!`;
          } else {
            // Check for car routes
            const carRouteKey = Object.keys(carRoutes).find(key => {
              const [from, to] = key.split('-');
              return airportLower.includes(from) && destLower.includes(to);
            });

            if (carRouteKey) {
              estimatedHours = carRoutes[carRouteKey].hours;
            }
            transportDescription = `Scenic drive from ${airportCode} airport. Consider renting a car for this trip!`;
          }

          const transferMinutes = estimatedHours * 60;
          const transferEndMinutes = hotelTransitStartMinutes + transferMinutes;
          const transferEndHours = Math.floor((transferEndMinutes % 1440) / 60);
          const transferEndMins = (transferEndMinutes % 1440) % 60;

          timeline.push({
            type: 'long-distance-transfer',
            time: `${String(hotelTransitHours).padStart(2, '0')}:${String(hotelTransitMins).padStart(2, '0')}`,
            arrivalTime: `${String(transferEndHours).padStart(2, '0')}:${String(transferEndMins).padStart(2, '0')}`,
            activity: `${transportEmoji} ${transportMode === 'train' ? 'Train' : 'Drive'} to ${destinationName}`,
            description: transportDescription,
            location: `${airportCode} → ${destinationName}`,
            from: `${airportCode} Airport, ${arrivalAirportCity}`,
            to: hotelDisplay || destinationName,
            duration: `~${estimatedHours} ${estimatedHours === 1 ? 'hour' : 'hours'}`,
            sortTime: hotelTransitStartMinutes,
            dayOffset: transitDayOffset,
            isLongDistance: true,
            travelMode: transportMode
          });

          // Update check-in time for long distance transfer
          const newCheckinMinutes = transferEndMinutes;
          const newCheckinHours = Math.floor((newCheckinMinutes % 1440) / 60);
          const newCheckinMins = (newCheckinMinutes % 1440) % 60;
          const newCheckinDayOffset = Math.floor(newCheckinMinutes / 1440);

          timeline.push({
            type: 'hotel-checkin',
            time: `${String(newCheckinHours).padStart(2, '0')}:${String(newCheckinMins).padStart(2, '0')}`,
            activity: initialCheckinLabel,
            description: initialCheckinDescription || 'Check in, get settled, freshen up',
            location: hotelDisplay,
            duration: '30 minutes',
            sortTime: newCheckinMinutes,
            dayOffset: newCheckinDayOffset
          });

          cutoffStartTime = newCheckinMinutes + 30;
        } else {
          // Normal short transit to hotel - use same style as Transit to First Stop
          // Prefer airport code format (e.g., "DPS Airport") over full name which may be unreliable
          const airportCode = itinerary?.arrivalAirport?.code;
          const airportCity = itinerary?.arrivalAirport?.city || sanitizeBaseCity(dayData.baseCity || '', '');
          const airportFromName = airportCode
            ? `${airportCode} Airport${airportCity ? `, ${airportCity.split(',')[0]}` : ''}`
            : (itinerary?.arrivalAirport?.name || 'Airport');
          const transitKey = `day${dayIndex}-hotel-transit`;

          // Get real duration from cache if available
          let hotelTransitDuration = selectedMode === 'transit' ? '~60 mins' : '~30 mins';
          const cachedTransit = transitTimes[transitKey];
          if (cachedTransit && cachedTransit[selectedMode]?.duration) {
            hotelTransitDuration = cachedTransit[selectedMode].duration;
          }

          timeline.push({
            type: 'transit',
            time: `${String(hotelTransitHours).padStart(2, '0')}:${String(hotelTransitMins).padStart(2, '0')}`,
            arrivalTime: `${String(hotelArrivalHours).padStart(2, '0')}:${String(hotelArrivalMins).padStart(2, '0')}`,
            activity: 'Transit to Hotel',
            from: addLocationContext(airportFromName),
            to: addLocationContext(hotelDisplay),
            duration: hotelTransitDuration,
            selectedMode: selectedMode,
            transitKey: transitKey,
            sortTime: hotelTransitStartMinutes,
            dayOffset: transitDayOffset,
            arrivalDayOffset: arrivalDayOffset
          });

          // Add hotel check-in and settle block for normal transit
          const checkinMinutes = hotelArrivalMinutes;
          const checkinHours = Math.floor((checkinMinutes % 1440) / 60);
          const checkinMins = (checkinMinutes % 1440) % 60;
          const checkinDayOffset = Math.floor(checkinMinutes / 1440);

          timeline.push({
            type: 'hotel-checkin',
            time: `${String(checkinHours).padStart(2, '0')}:${String(checkinMins).padStart(2, '0')}`,
            activity: initialCheckinLabel,
            description: initialCheckinDescription || 'Check in, get settled, freshen up',
            location: hotelDisplay,
            duration: '30 minutes',
            sortTime: checkinMinutes,
            dayOffset: checkinDayOffset
          });

          cutoffStartTime = hotelArrivalMinutes + 30; // Activities start 30 min after check-in
        }

      } else if (transportationMode === 'train' || transportationMode === 'ferry') {
        // TRAIN/FERRY: Transit arrival
        const transitMinutes = quickTransportResult?.durationMinutes || 180;
        const departureMinutes = arrivalMinutes; // departure time from station/port
        const arrivalAtDestMinutes = departureMinutes + transitMinutes;
        const departHours = Math.floor(departureMinutes / 60);
        const departMins = departureMinutes % 60;
        const arriveHours = Math.floor((arrivalAtDestMinutes % 1440) / 60);
        const arriveMins = (arrivalAtDestMinutes % 1440) % 60;
        const homeCity = userProfile.preferences.homeCity || 'Home';
        const emoji = transportationMode === 'train' ? '🚆' : '⛴️';
        const modeLabel = transportationMode === 'train' ? 'Train' : 'Ferry';
        const transitDuration = quickTransportResult?.duration || `${Math.round(transitMinutes / 60)}h`;

        timeline.push({
          type: 'transit-arrival',
          time: `${String(departHours).padStart(2, '0')}:${String(departMins).padStart(2, '0')}`,
          arrivalTime: `${String(arriveHours).padStart(2, '0')}:${String(arriveMins).padStart(2, '0')}`,
          activity: `${emoji} ${modeLabel} to ${selectedDestination?.name || 'Destination'}`,
          duration: transitDuration,
          description: `${modeLabel} from ${homeCity} to ${selectedDestination?.name || 'Destination'} (${transitDuration})`,
          location: `${homeCity} → ${selectedDestination?.name || 'Destination'}`,
          sortTime: departureMinutes
        });

        // Hotel check-in after arrival
        let hotelDisplay = dayHotelName;
        if (selectedHotel && !['City Center', 'Near Airport', 'Beach/Waterfront', 'Tourist District', 'Budget Area', 'Let arrivo Choose'].includes(selectedHotel)) {
          hotelDisplay = selectedHotel;
        }
        const checkinMinutes = arrivalAtDestMinutes + 15;
        const checkinHours = Math.floor((checkinMinutes % 1440) / 60);
        const checkinMins = (checkinMinutes % 1440) % 60;
        const checkinDayOffset = Math.floor(checkinMinutes / 1440);

        timeline.push({
          type: 'hotel-checkin',
          time: `${String(checkinHours).padStart(2, '0')}:${String(checkinMins).padStart(2, '0')}`,
          activity: initialCheckinLabel,
          description: initialCheckinDescription || `Check in, get settled, freshen up after the ${modeLabel.toLowerCase()}`,
          location: hotelDisplay,
          duration: '30 minutes',
          sortTime: checkinMinutes,
          dayOffset: checkinDayOffset
        });

        cutoffStartTime = checkinMinutes + 30;

      } else if (transportationMode === 'custom') {
        // CUSTOM: User-defined transport
        const customArrivalMinutes = arrivalMinutes; // user sets arrival time directly
        const homeCity = userProfile.preferences.homeCity || 'Home';

        timeline.push({
          type: 'transit-arrival',
          time: `${String(Math.floor(customArrivalMinutes / 60)).padStart(2, '0')}:${String(customArrivalMinutes % 60).padStart(2, '0')}`,
          activity: `🚌 Arrive at ${selectedDestination?.name || 'Destination'}`,
          duration: '',
          description: customTransportDesc || `Travel from ${homeCity} to ${selectedDestination?.name || 'Destination'}`,
          location: selectedDestination?.name || 'Destination',
          sortTime: customArrivalMinutes
        });

        // Hotel check-in after arrival
        let hotelDisplay = dayHotelName;
        if (selectedHotel && !['City Center', 'Near Airport', 'Beach/Waterfront', 'Tourist District', 'Budget Area', 'Let arrivo Choose'].includes(selectedHotel)) {
          hotelDisplay = selectedHotel;
        }
        const checkinMinutes = customArrivalMinutes + 30;
        const checkinHours = Math.floor((checkinMinutes % 1440) / 60);
        const checkinMins = (checkinMinutes % 1440) % 60;

        timeline.push({
          type: 'hotel-checkin',
          time: `${String(checkinHours).padStart(2, '0')}:${String(checkinMins).padStart(2, '0')}`,
          activity: initialCheckinLabel,
          description: initialCheckinDescription || 'Check in, get settled, freshen up after your journey',
          location: hotelDisplay,
          duration: '30 minutes',
          sortTime: checkinMinutes
        });

        cutoffStartTime = checkinMinutes + 30;

      } else {
        // DRIVING: Road trip arrival
        // Priority: 1) Google Maps calculated time, 2) destination driveHours, 3) estimate from flight hours
        const driveHours = calculatedDriveTime?.hours || selectedDestination?.driveHours || estimateDriveHours(selectedDestination?.flightHours || 3);
        const departureMinutes = arrivalMinutes; // "arrivalTime" is actually departure time for driving
        const arrivalAtDestMinutes = departureMinutes + (driveHours * 60);

        // Road trip from home
        const departHours = Math.floor(departureMinutes / 60);
        const departMins = departureMinutes % 60;
        const arriveHours = Math.floor((arrivalAtDestMinutes % 1440) / 60);
        const arriveMins = (arrivalAtDestMinutes % 1440) % 60;

        const homeCity = userProfile.preferences.homeCity || 'Home';
        const isRental = transportationMode === 'drive-rental';

        // Detect intercontinental drive — can't drive NYC to Matosinhos!
        const homeContinentCheck = getContinentHintFromText(homeCity);
        const destContinentCheck = getContinentHintFromText(selectedDestination?.name || '');
        const isIntercontinentalDrive = homeContinentCheck && destContinentCheck && homeContinentCheck !== destContinentCheck;

        // Add rental car pickup if applicable
        if (isRental) {
          timeline.push({
            type: 'rental-pickup',
            time: `${String(departHours).padStart(2, '0')}:${String(departMins).padStart(2, '0')}`,
            activity: '🚗 Rental Car Pickup',
            duration: '30 minutes',
            description: `Pick up rental car near ${homeCity}`,
            location: `Rental Location near ${homeCity}`,
            sortTime: departureMinutes
          });
        }

        // Road trip activity
        const roadTripStartMinutes = isRental ? departureMinutes + 30 : departureMinutes;
        const roadTripStartHours = Math.floor(roadTripStartMinutes / 60);
        const roadTripStartMins = roadTripStartMinutes % 60;

        // For multi-stop trips, use the first stop name instead of the overall trip name
        const roadTripDestName = selectedDestination?.isMultiStop && selectedDestination?.stopNames?.length > 0
          ? selectedDestination.stopNames[0].split(',')[0].trim()
          : (selectedDestination?.name || 'Destination');

        timeline.push({
          type: 'road-trip',
          time: `${String(roadTripStartHours).padStart(2, '0')}:${String(roadTripStartMins).padStart(2, '0')}`,
          arrivalTime: `${String(arriveHours).padStart(2, '0')}:${String(arriveMins).padStart(2, '0')}`,
          activity: `🚗 Road Trip to ${roadTripDestName}`,
          duration: isIntercontinentalDrive ? '' : `${driveHours} hours`,
          description: isIntercontinentalDrive
            ? `⚠️ Driving from ${homeCity} to ${roadTripDestName} isn't possible — they're on different continents. Update your departure city to a nearby location, or switch to flying.`
            : `Scenic drive from ${homeCity}${isRental ? ' (rental car)' : ''}. Enjoy the journey!`,
          location: `${homeCity} → ${roadTripDestName}`,
          from: homeCity,
          to: roadTripDestName,
          sortTime: roadTripStartMinutes
        });

        // Use hotel name from this day's data
        let hotelDisplay = dayHotelName;
        if (selectedHotel && !['City Center', 'Near Airport', 'Beach/Waterfront', 'Tourist District', 'Budget Area', 'Let arrivo Choose'].includes(selectedHotel)) {
          hotelDisplay = selectedHotel;
        }

        // Hotel check-in after arrival
        const checkinMinutes = arrivalAtDestMinutes + 15; // 15 min buffer after arriving
        const checkinHours = Math.floor((checkinMinutes % 1440) / 60);
        const checkinMins = (checkinMinutes % 1440) % 60;
        const checkinDayOffset = Math.floor(checkinMinutes / 1440);

        timeline.push({
          type: 'hotel-checkin',
          time: `${String(checkinHours).padStart(2, '0')}:${String(checkinMins).padStart(2, '0')}`,
          activity: initialCheckinLabel,
          description: initialCheckinDescription || 'Check in, get settled, freshen up after the drive',
          location: hotelDisplay,
          duration: '30 minutes',
          sortTime: checkinMinutes,
          dayOffset: checkinDayOffset
        });

        cutoffStartTime = checkinMinutes + 30; // Activities start 30 min after check-in
      }
      } // end of !isHomeCityFirstStop else
    }

    // Calculate cutoff for last day - activities should END before checkout
    // This prevents meals/activities from being scheduled after airport transit
    if (isLastDay) {
      // Use effectiveReturnMode (not transportationMode) — departure is based on how user RETURNS home
      const departTime = effectiveReturnMode === 'fly'
        ? (departureFlight.time || '15:00')
        : (driveDetails.returnDepartureTime || '10:00');
      const [hours, minutes] = departTime.split(':').map(Number);
      const departMinutes = hours * 60 + minutes;

      // Use dynamically calculated departure distance if available
      const hasCalculatedDepartureDistance = airportDistances?.departure?.durationMinutes > 0;
      let transitDuration = hasCalculatedDepartureDistance
        ? airportDistances.departure.durationMinutes
        : 45; // Default 45 minutes if not calculated

      // Calculate checkout time (work backwards from flight)
      const isDomesticCutoff = checkIfDomestic(selectedDestination);
      const airportBufferCutoff = isDomesticCutoff ? 120 : 180; // 2hr domestic / 3hr international
      const totalLeadTime = airportBufferCutoff + transitDuration + 30; // airport buffer + transit + checkout
      const checkoutMinutes = departMinutes - totalLeadTime;

      // Cutoff should be checkout time - activities must end before checkout
      // Clamp to 0 (midnight) minimum — very early flights (e.g., 4 AM + long transit)
      // can produce negative values that would incorrectly filter ALL activities
      cutoffEndTime = Math.max(0, checkoutMinutes);
      if (checkoutMinutes < 0) {
        debugWarn(`⚠️ Very early flight produces negative checkout time (${checkoutMinutes} mins) — clamped to midnight`);
      }
      debugLog(`🛫 Last day cutoff: checkout at ${Math.floor(cutoffEndTime/60)}:${String(cutoffEndTime%60).padStart(2,'0')}, cutoffEndTime=${cutoffEndTime}`);
    }

    // Check if this is a hotel change day (not first day, hotel is different from previous day)
    // This indicates inter-city travel (e.g., Lima Day 1 → Cusco Day 2)
    // NOTE: dayIndex is calculated at the top of createDayTimeline function

    // Safety check: if day not found in array, skip hotel/city comparisons
    if (dayIndex < 0) {
      debugLog('⚠️ Day not found in itinerary.days array, skipping hotel/city comparison');
    }

    // Debug: Log hotel comparison (only if day found and not first day)
    const prevHotelName = dayIndex > 0 ? itinerary?.days?.[dayIndex - 1]?.hotel?.name : null;
    const currentHotelName = dayData?.hotel?.name;
    if (dayIndex >= 0) {
      debugLog(`🏨 Day ${dayIndex + 1} hotel check: current="${currentHotelName}" vs prev="${prevHotelName}"`);
    }

    // Use fuzzy matching to detect if hotels are actually different
    // This uses the global areSameHotel function which handles:
    // - "Anantara Bazaruto Island Resort" vs "Anantara Bazaruto Island Resort & Spa" (same)
    // - "Four Seasons Hotel" vs "Four Seasons Resort" (same)
    const isHotelChangeDay = dayIndex > 0 &&
      !areSameHotel(prevHotelName, currentHotelName) &&
      prevHotelName && currentHotelName;

    // Also check if baseCity changed (more reliable than hotel name)
    const prevBaseCity = dayIndex > 0
      ? sanitizeBaseCity(itinerary?.days?.[dayIndex - 1]?.baseCity || '', itinerary?.days?.[dayIndex - 1]?.baseCity || null)
      : null;
    const currentBaseCity = sanitizeBaseCity(dayData?.baseCity || '', dayData?.baseCity || null);

    // normalizeCityName now imported from shared displayGuardrails
    const baseCityChanged = dayIndex > 0 &&
      dayData?.baseCity && itinerary?.days?.[dayIndex - 1]?.baseCity &&
      normalizeCityName(dayData.baseCity) !== normalizeCityName(itinerary?.days?.[dayIndex - 1]?.baseCity);

    debugLog(`🌆 Day ${dayIndex + 1} city check: current="${currentBaseCity}" vs prev="${prevBaseCity}", changed=${baseCityChanged}`);

    // Flag if this is an inter-city travel day
    // IMPORTANT: Only show inter-city travel when baseCity actually changes
    // Hotel changes within the same city should NOT trigger travel cards
    // NOTE: We now include lastDay IF the baseCity changed - user may travel to a new city
    // and then fly home from there (e.g., Sevilla Day 6 → Barcelona Day 7 → Fly home from BCN)
    //
    // SAFETY NET: If hotels are the SAME (fuzzy match), NEVER show inter-city travel
    // This prevents absurd "checkout→drive→checkin to same hotel" scenarios
    // (e.g., "Tuscany" Day 4 → "Fiesole" Day 5 with same Villa San Michele)
    //
    // FALLBACK: If hotels changed but baseCity didn't, check if we have cached distance data
    // If hotels are > 50km apart, treat as inter-city travel (catches large metro areas)
    const sameHotelAsPrev = dayIndex > 0 && prevHotelName && currentHotelName &&
      areSameHotel(prevHotelName, currentHotelName);

    // Check if server explicitly marked inter-city transport for this day
    const hasServerTransport = itinerary?.interCityTransport?.some(t => t.day === dayIndex + 1);

    let isInterCityTravelDay = baseCityChanged && !isFirstDay && (!sameHotelAsPrev || hasServerTransport);

    if (sameHotelAsPrev && baseCityChanged && !hasServerTransport) {
      debugLog(`🏨 Day ${dayIndex + 1}: Same hotel "${currentHotelName}" as prev day — suppressing inter-city travel despite baseCity change (${prevBaseCity} → ${currentBaseCity})`);
    } else if (sameHotelAsPrev && baseCityChanged && hasServerTransport) {
      debugLog(`🏨 Day ${dayIndex + 1}: Same hotel but server has interCityTransport — showing inter-city travel (${prevBaseCity} → ${currentBaseCity})`);
    }

    // Distance-based fallback for hotel changes within "same city"
    if (!isInterCityTravelDay && isHotelChangeDay && !isFirstDay) {
      const prevHotelName = itinerary?.days?.[dayIndex - 1]?.hotel?.name;
      const currentHotelName = dayData?.hotel?.name;

      // Check if we have pre-fetched distance data between hotels
      // This is populated by the transit time fetching logic
      const hotelDistanceKey = `hotel-${dayIndex - 1}-to-${dayIndex}`;
      const cachedDistance = transitTimes?.[hotelDistanceKey];

      // If distance > 50km (50000m), treat as inter-city even if baseCity is "same"
      // This catches cases like "Miami Beach" hotel to "Key Largo" hotel both tagged as "Florida Keys"
      if (cachedDistance?.distanceMeters > 50000) {
        debugLog(`📏 Hotels are ${Math.round(cachedDistance.distanceMeters / 1000)}km apart - treating as inter-city travel`);
        isInterCityTravelDay = true;
      } else if (prevHotelName && currentHotelName && prevHotelName !== currentHotelName) {
        // No cached distance, but hotels changed - log for debugging
        debugLog(`🏨 Hotel changed (${prevHotelName} → ${currentHotelName}) but baseCity same - no distance data available`);
      }
    }

    debugLog(`📍 Day ${dayIndex + 1}: isHotelChangeDay=${isHotelChangeDay}, baseCityChanged=${baseCityChanged}, isFirstDay=${isFirstDay}, isLastDay=${isLastDay}, isInterCityTravelDay=${isInterCityTravelDay}`);

    // SYSTEMIC FIX: Detect if this is an ARRIVAL day after an overnight flight from the previous day
    // This happens when the previous day had a flight that arrives the next day (arrivalDayOffset > 0)
    // In this case, we need to add arrival/recovery items to TODAY's timeline, not yesterday's
    let isArrivalDayAfterOvernightTravel = false;
    let overnightTravelArrivalData = null;

    if (!isFirstDay && dayIndex > 0) {
      // Check if this day's inter-city transport is an overnight flight arriving today
      // IMPORTANT: interCityTransport.day uses the ARRIVAL day convention (1-indexed)
      // So for Day 5 (dayIndex=4), we look for transport with day === 5 (dayIndex + 1)
      // that has overnight indicators (isOvernight, late departure, or long-haul evening flight)
      const thisDayTransport = itinerary?.interCityTransport?.find(t => t.day === dayIndex + 1);
      const thisDayFlight = itinerary?.flights?.find(f => f.day === dayIndex + 1);
      const thisDayLeg = thisDayTransport || thisDayFlight;

      // Also check the previous convention (t.day === dayIndex) as a fallback
      // Some legacy data may use departure-day tagging
      const prevDayTransport = itinerary?.interCityTransport?.find(t => t.day === dayIndex);
      const prevDayFlight = itinerary?.flights?.find(f => f.day === dayIndex);
      const prevDayLeg = prevDayTransport || prevDayFlight;

      // isExplicitOvernightArrival now imported from shared displayGuardrails
      // Only trusts EXPLICIT overnight markers (isOvernight flag or arrivalDayOffset).

      // For prevDayLeg: use shared overnight detection with full heuristics
      // Fix 7: Replaced local checkOvernightDeparture with shared checkOvernightTransport
      // — now both web and mobile use identical heuristics (including hours >= 10 catch-all)

      // Check prevDayLeg FIRST (more reliable — it's the actual departure from previous day)
      // Only fall back to thisDayLeg if it has explicit overnight markers
      const wasOvernightFromPrevDay = checkOvernightTransport(prevDayLeg, { dynamicGeographyCache, calculateFlightArrival });
      const wasOvernightFromThisDay = isExplicitOvernightArrival(thisDayLeg);
      let overnightLeg = wasOvernightFromPrevDay ? prevDayLeg : (wasOvernightFromThisDay ? thisDayLeg : null);

      // Fallback: if no overnight leg found but cities changed AND it's a long-haul route
      // (different continents), synthesize an overnight arrival entry from the day data.
      // This handles cases where interCityTransport data lacks overnight flags.
      if (!overnightLeg && isInterCityTravelDay) {
        const prevBaseCity = sanitizeBaseCity(itinerary?.days?.[dayIndex - 1]?.baseCity || '', itinerary?.days?.[dayIndex - 1]?.baseCity || '');
        const currBaseCity = sanitizeBaseCity(dayData?.baseCity || '', dayData?.baseCity || '');
        if (prevBaseCity && currBaseCity && prevBaseCity.toLowerCase() !== currBaseCity.toLowerCase()) {
          const prevCountry = CITY_TO_COUNTRY[prevBaseCity.toLowerCase()] || '';
          const currCountry = CITY_TO_COUNTRY[currBaseCity.toLowerCase()] || '';
          // Check if cross-continental (implies long-haul, likely overnight)
          const prevContinent = prevCountry ? getContinentForCountry(prevCountry) : '';
          const currContinent = currCountry ? getContinentForCountry(currCountry) : '';
          if (prevContinent && currContinent && prevContinent !== currContinent) {
            debugLog(`🌙 Fallback overnight detection: ${prevBaseCity} (${prevContinent}) → ${currBaseCity} (${currContinent}) — cross-continental, treating as overnight arrival`);
            overnightLeg = {
              from: { city: prevBaseCity },
              to: { city: currBaseCity },
              mode: 'flight',
              travelMode: 'flight',
              isOvernight: true,
              arrivalTime: '07:00',
              duration: '',
            };
          }
        }
      }

      if (overnightLeg) {
        isArrivalDayAfterOvernightTravel = true;
        // Determine transport mode from the leg data
        const overnightMode = overnightLeg?.mode || overnightLeg?.travelMode ||
          (overnightLeg?.carrier?.toLowerCase?.()?.includes('train') ? 'train' :
           overnightLeg?.carrier?.toLowerCase?.()?.includes('bus') ? 'bus' : 'flight');
        const _sanitizedCurrentCity = sanitizeBaseCity(dayData?.baseCity || '', dayData?.baseCity || '');
        overnightTravelArrivalData = {
          fromCity: overnightLeg?.from?.city || sanitizeBaseCity(itinerary?.days?.[dayIndex - 1]?.baseCity || '', itinerary?.days?.[dayIndex - 1]?.baseCity || '') || 'Previous City',
          toCity: overnightLeg?.to?.city || _sanitizedCurrentCity || 'Current City',
          arrivalTime: overnightLeg?.arrivalTime || '07:00', // Default early morning for overnight travel
          duration: overnightLeg?.duration || '',
          carrier: overnightLeg?.carrier || '',
          airline: overnightLeg?.airline || '',
          travelMode: overnightMode,
          toAirport: overnightLeg?.to?.airport || (overnightMode === 'flight' ? `${_sanitizedCurrentCity || ''} Airport` : ''),
          toStation: overnightLeg?.to?.station || (overnightMode !== 'flight' ? `${_sanitizedCurrentCity || ''} Station` : '')
        };
        debugLog(`🌅 Day ${dayIndex + 1}: Arrival day after overnight ${overnightMode} from ${overnightTravelArrivalData.fromCity} (transport day=${overnightLeg.day}, source=${wasOvernightFromThisDay ? 'thisDay' : 'prevDay'})`);
      }
    }

    // If this is an arrival day after overnight travel (flight, train, or bus), add arrival timeline items
    if (isArrivalDayAfterOvernightTravel && overnightTravelArrivalData) {
      const arrivalTime = overnightTravelArrivalData.arrivalTime || '07:00';
      const [arrHours, arrMins] = arrivalTime.split(':').map(Number);
      const arrivalMinutes = (arrHours || 7) * 60 + (arrMins || 0);
      const currentCity = overnightTravelArrivalData.toCity;
      const travelMode = overnightTravelArrivalData.travelMode || 'flight';
      const isFlight = travelMode === 'flight';
      const toAirport = overnightTravelArrivalData.toAirport;
      const toStation = overnightTravelArrivalData.toStation;
      const arrivalLocation = isFlight ? toAirport : toStation;
      const travelDuration = overnightTravelArrivalData.duration;
      const carrier = overnightTravelArrivalData.carrier || overnightTravelArrivalData.airline;

      // Mode-specific labels
      const arrivalEmoji = isFlight ? '🛬' : travelMode === 'train' ? '🚆' : '🚌';
      const modeLabel = isFlight ? 'Flight' : travelMode === 'train' ? 'Train' : 'Bus';
      const modeVerb = isFlight ? 'lands' : 'arrives';

      // Add arrival card
      timeline.push({
        type: 'overnight-arrival',
        time: arrivalTime,
        activity: `${arrivalEmoji} Arrival at ${currentCity}`,
        description: `${carrier ? `${carrier} ${modeLabel.toLowerCase()}` : modeLabel} ${modeVerb} after overnight journey${travelDuration ? ` (${travelDuration})` : ''}. Welcome to ${currentCity}!`,
        location: arrivalLocation,
        duration: isFlight ? 'Landing' : 'Arrival',
        sortTime: arrivalMinutes,
        isOvernightArrival: true,
        travelMode: travelMode
      });

      // Add customs/immigration for international arrivals
      // Flights always get customs; ferries/trains get customs if crossing non-Schengen borders
      let postArrivalOffset = 15;
      const arrivalPrevCountry = CITY_TO_COUNTRY[overnightTravelArrivalData.fromCity?.toLowerCase()] || '';
      const arrivalCurrCountry = CITY_TO_COUNTRY[overnightTravelArrivalData.toCity?.toLowerCase()] || '';
      const isInternationalOvernightArrival = arrivalPrevCountry && arrivalCurrCountry
        ? arrivalPrevCountry !== arrivalCurrCountry
        : overnightTravelArrivalData.fromCity?.toLowerCase() !== overnightTravelArrivalData.toCity?.toLowerCase();
      const isSchengenInternal = arrivalPrevCountry && arrivalCurrCountry &&
        SCHENGEN_COUNTRIES?.includes(arrivalPrevCountry) && SCHENGEN_COUNTRIES?.includes(arrivalCurrCountry);
      const needsCustoms = isInternationalOvernightArrival && !isSchengenInternal;

      if (isFlight && needsCustoms) {
        const customsMinutes = arrivalMinutes + 15;
        timeline.push({
          type: 'arrival-customs',
          time: `${String(Math.floor(customsMinutes / 60)).padStart(2, '0')}:${String(customsMinutes % 60).padStart(2, '0')}`,
          activity: '🛂 Customs & Immigration',
          description: `International arrival processing at ${toAirport}. Have passport and any required documents ready.`,
          location: toAirport,
          duration: '45 minutes',
          sortTime: customsMinutes
        });
        postArrivalOffset = 60;
      } else if (!isFlight && needsCustoms) {
        // International ferry/train crossing (e.g., Turkey→Greece, UK→France by ferry)
        const customsMinutes = arrivalMinutes + 10;
        const portOrStation = travelMode === 'ferry' ? `${overnightTravelArrivalData.toCity} Port` : toStation;
        timeline.push({
          type: 'arrival-customs',
          time: `${String(Math.floor(customsMinutes / 60)).padStart(2, '0')}:${String(customsMinutes % 60).padStart(2, '0')}`,
          activity: '🛂 Border Control',
          description: `Passport control at ${portOrStation}. Have documents ready.`,
          location: portOrStation,
          duration: '30 minutes',
          sortTime: customsMinutes
        });
        postArrivalOffset = 45;
      }

      // Add transit to hotel
      const transitMinutes = arrivalMinutes + postArrivalOffset;
      const dayHotelNameForTransit = dayData?.hotel?.name || 'Hotel';
      const transitFrom = isFlight ? toAirport : toStation;
      timeline.push({
        type: isFlight ? 'airport-transit' : 'station-transit',
        time: `${String(Math.floor(transitMinutes / 60)).padStart(2, '0')}:${String(transitMinutes % 60).padStart(2, '0')}`,
        activity: '🚕 Transit to Hotel',
        description: `Transfer from ${transitFrom} to your hotel`,
        location: `${transitFrom} → ${dayHotelNameForTransit}`,
        from: transitFrom,
        to: dayHotelNameForTransit,
        duration: isFlight ? '~45 min' : '~30 min',
        sortTime: transitMinutes,
        transitKey: `day${dayIndex}-from-overnight-arrival`
      });

      // ─── Hotel check-in timing (deterministic rule) ─────────────
      //
      // Problem: for early arrivals (e.g. 7am overnight flight landing, transit
      // done by 9am), the previous logic injected hotel check-in immediately
      // at ~9am and pushed the entire rest of the day into a "rest & recovery"
      // block. Two issues:
      //   1. Most hotels don't allow check-in until 15:00. Users show up at
      //      9am with bags and are told to come back.
      //   2. Even if they could check in, blocking 6+ hours as "rest" wastes
      //      the day. They could do a luggage drop + café + light stroll +
      //      lunch + sightseeing near the hotel until the room is ready.
      //
      // Deterministic rule (applies to flight arrivals and overnight
      // train/bus arrivals alike):
      //   - Natural check-in time = transit_end + 30min
      //   - If natural < 15:00, we clamp check-in to 15:00 AND insert a
      //     "Luggage drop" card at transit_end+30min so users know they
      //     CAN stop by the hotel to drop bags before formal check-in.
      //   - The gap between luggage-drop and check-in is left open for
      //     LLM-generated lighter activities (café, lunch, light sightseeing).
      //   - If natural >= 15:00, skip the luggage-drop — just check in
      //     immediately (late afternoon/evening arrival).
      //
      // Override: if the day's hotel is a known resort/premium property
      // (isResortStay === true), trust their early-check-in policy and
      // skip the clamp. Resorts typically accommodate arriving guests.
      const transitDuration = isFlight ? 45 : 30;
      const naturalCheckinMinutes = transitMinutes + transitDuration;
      const HOTEL_CHECKIN_POLICY_MINUTES = 15 * 60; // 15:00
      const isResortStayFlagForCheckin = (() => {
        try {
          return isResortStay(dayHotelNameForTransit, accommodationType);
        } catch { return false; }
      })();
      const shouldClampCheckin = !isResortStayFlagForCheckin
        && naturalCheckinMinutes < HOTEL_CHECKIN_POLICY_MINUTES;
      const checkinMinutes = shouldClampCheckin
        ? HOTEL_CHECKIN_POLICY_MINUTES
        : naturalCheckinMinutes;

      if (shouldClampCheckin) {
        // Add a LUGGAGE DROP card at natural transit end — user can stop by
        // the hotel to offload bags before formal check-in is available.
        const luggageDropMinutes = naturalCheckinMinutes;
        timeline.push({
          type: 'luggage-drop',
          time: `${String(Math.floor(luggageDropMinutes / 60)).padStart(2, '0')}:${String(luggageDropMinutes % 60).padStart(2, '0')}`,
          activity: '🧳 Luggage Drop',
          description: `Drop bags at ${dayHotelNameForTransit}. Formal check-in is typically at 3pm — explore the neighborhood until then.`,
          location: dayHotelNameForTransit,
          duration: '15 minutes',
          sortTime: luggageDropMinutes,
          isArrivalLogistic: true,
        });
      }

      timeline.push({
        type: 'hotel-checkin',
        time: `${String(Math.floor(checkinMinutes / 60)).padStart(2, '0')}:${String(checkinMinutes % 60).padStart(2, '0')}`,
        activity: initialCheckinLabel,
        description: shouldClampCheckin
          ? `Check in to ${dayHotelNameForTransit} once the room is ready.`
          : `Check in to ${dayHotelNameForTransit}, get settled after the overnight ${modeLabel.toLowerCase()}`,
        location: dayHotelNameForTransit,
        duration: '30 minutes',
        sortTime: checkinMinutes
      });

      // Recovery / freshen-up note placed AFTER check-in (not before).
      //
      // Rationale: if we clamped check-in to 15:00 because of hotel policy
      // but arrival was at 07:00, the user has a 7-hour window for
      // sightseeing, lunch, coffee, etc. — they don't need to "recover"
      // for 3 hours before they're even allowed to check in. Recovery is
      // now an advisory item that sits right after the formal check-in
      // card so LLM-generated activities can freely fill the morning
      // and early afternoon gap.
      const recoveryMinutes = checkinMinutes + 30;
      if (isFlight) {
        // Calculate timezone shift for jet lag severity
        const fromOffset = getCityTimezoneOffset(overnightTravelArrivalData.fromCity) || 0;
        const toOffset = getCityTimezoneOffset(currentCity) || 0;
        const tzShiftHours = Math.abs(toOffset - fromOffset);

        let recoveryDuration, recoveryDesc;
        if (tzShiftHours >= 8) {
          // Major jet lag (e.g., NYC→Tokyo, London→Sydney)
          recoveryDuration = '2-3 hours';
          recoveryDesc = `${tzShiftHours}-hour timezone shift detected. Consider lighter activities tonight, hydrate, and get sunlight earlier today to reset your circadian rhythm.`;
        } else if (tzShiftHours >= 4) {
          // Moderate jet lag (e.g., NYC→London, LA→NYC)
          recoveryDuration = '1-2 hours';
          recoveryDesc = `${tzShiftHours}-hour timezone change. Stay hydrated and aim for an earlier dinner to adjust.`;
        } else {
          // Minor or no jet lag
          recoveryDuration = '1 hour';
          recoveryDesc = `Freshen up and settle into ${currentCity}. Minimal jet lag for this route.`;
        }

        timeline.push({
          type: 'recovery-note',
          time: `${String(Math.floor(recoveryMinutes / 60)).padStart(2, '0')}:${String(recoveryMinutes % 60).padStart(2, '0')}`,
          activity: '💤 Rest & Recovery',
          description: recoveryDesc,
          location: dayHotelNameForTransit,
          duration: recoveryDuration,
          sortTime: recoveryMinutes,
          isRecoveryNote: true,
          timezoneShift: tzShiftHours
        });
      } else {
        // Trains/buses: no jet lag, shorter recovery
        timeline.push({
          type: 'recovery-note',
          time: `${String(Math.floor(recoveryMinutes / 60)).padStart(2, '0')}:${String(recoveryMinutes % 60).padStart(2, '0')}`,
          activity: '☕ Freshen Up & Settle In',
          description: `After the overnight ${modeLabel.toLowerCase()}, take some time to freshen up and get oriented in ${currentCity}.`,
          location: dayHotelNameForTransit,
          duration: '30 minutes',
          sortTime: recoveryMinutes,
          isRecoveryNote: true
        });
      }

      // Activities can start right after the luggage drop (or after transit
      // if no clamp was needed). No more "block the first 3 hours as
      // recovery" behavior — users arriving at 9 AM should see café / brunch
      // / light stroll / museum suggestions filling the morning, not
      // enforced rest until noon.
      cutoffStartTime = shouldClampCheckin
        ? naturalCheckinMinutes + 30  // after luggage drop: free to explore
        : recoveryMinutes + 30;        // after recovery when check-in was immediate

      preCheckoutCutoffEnd = 0; // No pre-checkout activities (user just arrived)
      // Post-checkin activities are a separate window the LLM should fill
      // with the main afternoon plan. When the check-in was clamped to 15:00
      // we still want the afternoon populated, so this stays at 15:30-ish.
      postCheckinCutoffStart = checkinMinutes + 30;

      debugLog(`🌅 Day ${dayIndex + 1}: Added overnight ${travelMode} arrival items. Activities start after ${Math.floor(cutoffStartTime/60)}:${String(cutoffStartTime%60).padStart(2,'0')}`);

      // Fix 2: Set transport minutes for overnight arrival days so D3 meal blocking works.
      // When isArrivalDayAfterOvernightTravel is true, line ~10340 skips the inter-city block,
      // leaving icTransportDepartureMinutes/icTransportArrivalMinutes null → isMealBlockedByTransport
      // returns false → meals injected during flight. Fix by setting from overnightTravelArrivalData.
      if (overnightTravelArrivalData) {
        const ovrDepTime = overnightTravelArrivalData.departureTime || '00:00';
        const [ovrDepH, ovrDepM] = ovrDepTime.split(':').map(Number);
        icTransportDepartureMinutes = (ovrDepH || 0) * 60 + (ovrDepM || 0);
        const ovrArrTime = overnightTravelArrivalData.arrivalTime || '07:00';
        const [ovrArrH, ovrArrM] = ovrArrTime.split(':').map(Number);
        icTransportArrivalMinutes = (ovrArrH || 0) * 60 + (ovrArrM || 0);
        // For overnight flights, if arrival < departure it means next day — use 1440 for blocking
        if (icTransportArrivalMinutes < icTransportDepartureMinutes) {
          icTransportArrivalMinutes = 1440; // Flight spans entire day
        }
        debugLog(`✈️ Fix 2: Set overnight transport minutes: dep=${icTransportDepartureMinutes}, arr=${icTransportArrivalMinutes}`);
      }
    }

    // Check if there's ALSO a same-day onward transport (separate from the overnight arrival)
    // e.g., arrive NY from overnight flight + afternoon train NY→Boston
    const hasOnwardTransportSameDay = isArrivalDayAfterOvernightTravel && isInterCityTravelDay && (() => {
      const thisDayTransportEntry = itinerary?.interCityTransport?.find(t => t.day === dayIndex + 1);
      // If the transport entry for this day is DIFFERENT from the overnightLeg, it's onward travel
      return thisDayTransportEntry && overnightTravelArrivalData &&
        thisDayTransportEntry.from?.city?.toLowerCase() !== overnightTravelArrivalData.fromCity?.toLowerCase();
    })();

    // Don't render inter-city travel on the last day if the user is flying home —
    // the departure logistics section (below) handles the hotel-to-airport transit directly.
    // Without this, Day 3 shows "Train to Porto" / "Transit to Train Station" which is wrong
    // when the user should just drive from Ericeira hotel to OPO Airport.
    const skipInterCityForDeparture = isLastDay && effectiveReturnMode !== 'drive-own';

    if (!skipInterCityForDeparture && ((isInterCityTravelDay && !isArrivalDayAfterOvernightTravel) || hasOnwardTransportSameDay)) {
      // Render inter-city travel block. Skip when overnight arrival already handled this day
      // UNLESS there's a separate onward transport (e.g., arrive NY, then train to Boston).
      const prevHotel = itinerary?.days?.[dayIndex - 1]?.hotel;
      // BUG 8 FIX: Resolve resort/hotel names to geographic cities EARLY so ALL downstream
      // lookups (country, continent, timezone, airport, island detection) receive real city names
      const rawPrevCity = sanitizeBaseCity(itinerary?.days?.[dayIndex - 1]?.baseCity || '',
                        prevHotel?.address?.split(',').slice(-2, -1)[0]?.trim() || 'Previous City');
      const rawCurrentCity = sanitizeBaseCity(dayData?.baseCity || '',
                         dayData?.hotel?.address?.split(',').slice(-2, -1)[0]?.trim() || 'New City');
      const prevCity = extractLocationFromHotelName(rawPrevCity) !== rawPrevCity
        ? extractLocationFromHotelName(rawPrevCity) : rawPrevCity;
      const currentCity = extractLocationFromHotelName(rawCurrentCity) !== rawCurrentCity
        ? extractLocationFromHotelName(rawCurrentCity) : rawCurrentCity;

      // Day trip detection: if user visits a different city but returns to the same base city next day
      // e.g., Day trip from Kyoto to Nara — no hotel checkout/checkin needed
      const isDayTrip = (() => {
        const prevBaseCity = itinerary?.days?.[dayIndex - 1]?.baseCity?.toLowerCase()?.trim();
        const nextBaseCity = itinerary?.days?.[dayIndex + 1]?.baseCity?.toLowerCase()?.trim();
        const currentBaseCity = dayData?.baseCity?.toLowerCase()?.trim();
        // Day trip = visiting different city but returning to previous city next day
        if (prevBaseCity && nextBaseCity && currentBaseCity &&
            prevBaseCity === nextBaseCity &&
            currentBaseCity !== prevBaseCity) {
          debugLog(`🎯 Day ${dayIndex + 1}: Day trip detected (${prevBaseCity} → ${currentBaseCity} → ${nextBaseCity})`);
          return true;
        }
        return false;
      })();

      // Check if there's travel data in the itinerary (from AI inter-city detection)
      const travelData = itinerary?.interCityTravel?.find(t => t.day === dayIndex + 1);
      // Support both old "flights" array and new "interCityTransport" array
      const transportData = itinerary?.interCityTransport?.find(t => t.day === dayIndex + 1);
      const flightData = itinerary?.flights?.find(f => f.day === dayIndex + 1);
      const legData = transportData || flightData;

      // Dynamic transport lookup via API (async - use for route cards when user clicks)
      // This uses Haiku for fast, accurate transport recommendations
      const getDynamicTransport = async (fromCity, toCity) => {
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/quick-transport`, {
            method: 'POST',
            headers: await getApiHeaders(),
            body: JSON.stringify({ fromCity, toCity })
          });
          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              return data;
            }
          }
        } catch (error) {
          console.error('Dynamic transport lookup failed:', error);
        }
        return null;
      };

      // getSmartTransportMode — thin wrapper over shared, injecting closure-scoped helpers
      const getSmartTransportMode = (fromCity, toCity) => {
        const mode = _getSmartTransportMode(fromCity, toCity, {
          getCityCountrySync, getContinentForCountry: getContinentForCountry, isIslandCity,
        });
        debugLog(`🚗 Smart transport: ${fromCity} → ${toCity} = ${mode.toUpperCase()}`);
        return mode;
      };

      // Use transport mode from AI-provided data, haiku lookup, or smart fallback
      // BUG 3 FIX: Prefer the mode with shortest travel time when Haiku data is available
      // Haiku's /api/quick-transport gives accurate door-to-door travel time for the optimal mode.
      // If the AI suggested a different mode but Haiku found a faster one, prefer Haiku.
      const getDynamicOrSmartTransport = () => {
        if (flightData) return 'flight';

        // Check if we have a haiku-determined transport mode for this city pair
        const pairKey = `${prevCity}|${currentCity}`.toLowerCase();
        const dynamicData = dynamicTransportModes[pairKey];
        if (dynamicData?.mode) {
          debugLog(`🚀 Using Haiku transport: ${prevCity} → ${currentCity} = ${dynamicData.mode} (${dynamicData.durationMinutes} min)`);
          return dynamicData.mode;
        }

        // Fall back to static rules
        return getSmartTransportMode(prevCity, currentCity);
      };

      // BUG 3 FIX: If both AI and Haiku provide modes, prefer Haiku's choice since it
      // accounts for actual travel time, train availability, and island geography.
      // AI's mode is a guess from itinerary generation; Haiku's is a dedicated transport analysis.
      const aiMode = legData?.mode || travelData?.recommendedMode;
      const pairKeyForMode = `${prevCity}|${currentCity}`.toLowerCase();
      const haikuData = dynamicTransportModes[pairKeyForMode];

      let transportMode;
      if (flightData) {
        transportMode = 'flight';
      } else if (haikuData?.mode && aiMode && haikuData.mode !== aiMode) {
        // Haiku disagrees with AI — prefer Haiku (it has travel time context)
        debugLog(`🔄 Transport override: AI said "${aiMode}" but Haiku says "${haikuData.mode}" (${haikuData.durationMinutes} min) for ${prevCity} → ${currentCity}. Using Haiku.`);
        transportMode = haikuData.mode;
      } else {
        transportMode = aiMode || getDynamicOrSmartTransport();
      }

      // ISSUE 2 FIX: Frontend validation guard — now uses shared validateTransportMode
      // Forces to flight for cross-continental or island routes (except ferry)
      const validatedMode = validateTransportMode(transportMode, prevCity, currentCity, {
        getCityCountrySync, getContinentForCountry, isIslandCity,
      });
      if (validatedMode !== transportMode) {
        debugLog(`⚠️ Transport validation: "${transportMode}" → "${validatedMode}" for ${prevCity} → ${currentCity}`);
        transportMode = validatedMode;
      }

      // TASK C: Suppress impossible flights between nearby stops.
      // When the LLM hallucinates a "flight" between cities that are <250 mi
      // apart AND at least one side isn't a real major-airport city
      // (e.g., "Auberge du Soleil → Taos"), force a drive card instead.
      // DEPARTURE_AIRPORT_CITY_MAP values are the lowercased cities served
      // by the hardcoded IATA→city map — a solid proxy for "has commercial
      // airline service worth flying to/from."
      if (transportMode === 'flight' && !flightData) {
        const majorAirportCitiesSet = new Set(
          Object.values(DEPARTURE_AIRPORT_CITY_MAP).map(c => String(c).toLowerCase())
        );
        const driveGuard = shouldForceDrive(prevCity, currentCity, {
          maxMiles: 250,
          majorAirportCities: majorAirportCitiesSet,
        });
        if (driveGuard.shouldForceDrive) {
          debugLog(`🛑 Task C suppressed flight: ${driveGuard.reason}. Forcing drive for ${prevCity} → ${currentCity}.`);
          transportMode = 'car';
        }
      }

      // Smart flight duration estimate — web wrapper with cache/dynamic layers on top of shared heuristics
      const getSmartFlightDuration = (fromCity, toCity) => {
        const fromLower = fromCity?.toLowerCase().trim();
        const toLower = toCity?.toLowerCase().trim();

        // B4: Priority order — validated > dynamic prefetch > shared heuristics
        // 1. Check dynamically validated cache first (populated by post-gen validation)
        const validatedKey = `${fromLower}-${toLower}`;
        const validatedReverse = `${toLower}-${fromLower}`;
        if (validatedFlightDurations.current[validatedKey]) {
          debugLog(`✈️ Using dynamically validated duration: ${validatedKey} = ${validatedFlightDurations.current[validatedKey]}`);
          return validatedFlightDurations.current[validatedKey];
        }
        if (validatedFlightDurations.current[validatedReverse]) {
          debugLog(`✈️ Using dynamically validated duration (reverse): ${validatedReverse} = ${validatedFlightDurations.current[validatedReverse]}`);
          return validatedFlightDurations.current[validatedReverse];
        }

        // 2. Check dynamic transport modes from Haiku prefetch
        const pairKey = `${fromLower}|${toLower}`;
        const pairKeyReverse = `${toLower}|${fromLower}`;
        const dynamicData = dynamicTransportModes[pairKey] || dynamicTransportModes[pairKeyReverse];
        if (dynamicData?.duration) {
          debugLog(`✈️ Using dynamic prefetch duration: ${fromCity}→${toCity} = ${dynamicData.duration}`);
          return dynamicData.duration;
        }

        // 3. Shared heuristics: static route map → coords (Haversine) → cross-continental → domestic
        // Pass coord + airport-code helpers so JFK/MEX/etc. resolve correctly
        // and the heuristic uses real distance instead of the ~4h fallback.
        return estimateFlightDurationHeuristic(fromCity, toCity, {
          getCityCountrySync,
          getContinentForCountry,
          getCityCoords,
          greatCircleMiles,
          reverseLookupAirportCity,
        });
      };

      const departureTime = legData?.departureTime || flightData?.departureTime || travelData?.options?.[0]?.departureTime || '09:00';

      // BUG 8: Resort name resolution now happens at prevCity/currentCity assignment (line ~10950)
      // so prevCity and currentCity are already resolved to geographic cities here.

      // Use AI-provided duration, dynamic transport data, or smart estimate
      // BUG A FIX: If Haiku overrode the AI's mode (Bug 3 fix), also prefer Haiku's duration
      // since the AI's duration was for a different mode or is unreliable
      const pairKeyForDuration = `${prevCity}|${currentCity}`.toLowerCase();
      const haikuDurationData = dynamicTransportModes[pairKeyForDuration];
      const dynamicDuration = haikuDurationData?.duration;
      const aiDuration = legData?.duration || flightData?.duration || travelData?.options?.find(o => o.mode === transportMode)?.duration;
      // If Haiku overrode the mode, prefer Haiku's duration too
      const haikuOverrodeMode = haikuData?.mode && aiMode && haikuData.mode !== aiMode;
      let travelDuration = haikuOverrodeMode ? (dynamicDuration || aiDuration) : (aiDuration || dynamicDuration);
      if (!travelDuration) {
        if (transportMode === 'flight') {
          travelDuration = getSmartFlightDuration(prevCity, currentCity);
        } else if (transportMode === 'train') {
          travelDuration = '~3h'; // Default for train
        } else {
          travelDuration = '~3h'; // Default for car/bus (was 4h, but most inter-city drives are 2-3h)
        }
      }
      // Validate flight duration against known routes - LLM often gets duration wrong
      if (transportMode === 'flight' && travelDuration) {
        // Sanity clamp: LLM sometimes emits "-4h" when it tries to timezone-
        // correct a same-day transatlantic arrival (e.g. SPR 09:00 → LIS 13:00
        // treated as -7h rollback). Drop obviously-bogus negative values and
        // fall back to the lookup table before we propagate "-4h total." into
        // the UI description string.
        if (/^\s*-/.test(String(travelDuration)) || /-\s*\d/.test(String(travelDuration))) {
          const fallback = getSmartFlightDuration(prevCity, currentCity);
          if (fallback) {
            debugLog(`✈️ Negative flight duration detected (${travelDuration}) for ${prevCity}→${currentCity}; using fallback ${fallback}`);
            travelDuration = fallback;
          } else {
            travelDuration = '';
          }
        }
        const parseHours = (str) => {
          const h = str.match(/(\d+(?:\.\d+)?)\s*h/i);
          const m = str.match(/(\d+)\s*m/i);
          return (h ? parseFloat(h[1]) : 0) + (m ? parseInt(m[1]) / 60 : 0);
        };
        // BUG 8: prevCity/currentCity are already resolved (hotel names → real cities) at assignment
        const bestKnown = getSmartFlightDuration(prevCity, currentCity);

        if (bestKnown) {
          const knownHrs = parseHours(bestKnown);
          const currentHrs = parseHours(travelDuration);

          if (!bestKnown.startsWith('~')) {
            // B4: Precise lookup — if LLM is >30% off, use the known duration (was 50%)
            if (knownHrs > 0 && currentHrs > knownHrs * 1.3) {
              debugLog(`✈️ Correcting flight duration ${prevCity}→${currentCity}: ${travelDuration} → ${bestKnown} (lookup override)`);
              travelDuration = bestKnown;
            }
          } else {
            // Cross-continental estimate (starts with ~) — use as MINIMUM floor
            // The AI sometimes says "8h 30m" for a route that's ~12h non-stop (and actually
            // requires connections making it 30-40h). If AI's duration is significantly LESS
            // than the non-stop minimum, the AI is wrong — use the estimate instead.
            // Also flag multi-connection routes where real travel time >> non-stop estimate.
            if (knownHrs > 0 && currentHrs < knownHrs * 0.75) {
              debugLog(`✈️ Correcting underestimated flight ${prevCity}→${currentCity}: ${travelDuration} → ${bestKnown} (below cross-continental minimum)`);
              travelDuration = bestKnown;
            }
          }
        }
      }
      const carrier = legData?.carrier || flightData?.airline || '';
      // REGION_TO_STATION and resolveStation now imported from shared transportConstants
      const fromStation = legData?.from?.station || legData?.from?.airport || resolveStation(prevCity, `${prevCity} Station`);
      const toStation = legData?.to?.station || legData?.to?.airport || resolveStation(currentCity, `${currentCity} Station`);
      // B9B: Resolve IATA codes for airport fallbacks
      const fromIata = lookupCityAirportCode(prevCity);
      const toIata = lookupCityAirportCode(currentCity);

      // TASK B: Honor user-selected airports from the trip-builder per-leg transport picks.
      // When the user picks e.g. DEN as the arrival airport for Aspen, we must override
      // the LLM/default (ASE) so the rendered flight card matches what the user chose.
      // Leg keys take the form `${fromStopId}→${toStopId}`, `dept→${firstStopId}`, or
      // `${lastStopId}→return`. We resolve the matching leg by walking tripBuilderStops
      // using the dayData.baseCity boundaries on either side of this inter-city hop.
      const _findUserLegPick = () => {
        if (!perLegTransport || typeof perLegTransport !== 'object') return null;
        const entries = Object.entries(perLegTransport);
        if (!entries.length) return null;
        const prevLower = (prevCity || '').toLowerCase();
        const currLower = (currentCity || '').toLowerCase();

        // Try to match using tripBuilderStops ids based on city names
        if (Array.isArray(tripBuilderStops) && tripBuilderStops.length) {
          const nameMatches = (stop, city) => {
            if (!stop || !city) return false;
            const c = city.toLowerCase();
            const cand = [stop.resolvedLocation, stop.name]
              .filter(Boolean)
              .map(s => s.toLowerCase());
            return cand.some(s => s === c || s.includes(c) || c.includes(s.split(',')[0].trim()));
          };
          const fromIdx = tripBuilderStops.findIndex(s => nameMatches(s, prevCity));
          const toIdx = tripBuilderStops.findIndex(s => nameMatches(s, currentCity));
          const fromId = fromIdx >= 0 ? tripBuilderStops[fromIdx].id : null;
          const toId = toIdx >= 0 ? tripBuilderStops[toIdx].id : null;
          for (const [key, val] of entries) {
            if (!val || typeof val !== 'object') continue;
            // Inter-stop leg
            if (fromId && toId && key.includes(String(fromId)) && key.includes(String(toId))) return val;
            // Departure leg (home city → first stop): prevCity is home, currentCity is first stop
            if (toId && key.startsWith('dept') && key.includes(String(toId))) {
              // Only accept if there's no earlier stop in the builder that matches prevCity
              if (fromIdx < 0) return val;
            }
            // Return leg (last stop → home): prevCity is last stop, currentCity is home
            if (fromId && key.endsWith('return') && key.includes(String(fromId))) {
              if (toIdx < 0) return val;
            }
          }
        }

        // Fallback: substring match against raw city names in the key
        for (const [key, val] of entries) {
          if (!val || typeof val !== 'object') continue;
          const kl = key.toLowerCase();
          if (prevLower && currLower && kl.includes(prevLower.split(',')[0].trim()) && kl.includes(currLower.split(',')[0].trim())) {
            return val;
          }
        }
        return null;
      };
      const _userLegPick = _findUserLegPick();
      const _userDepCode = _userLegPick?.departureAirport?.code || null;
      const _userArrCode = _userLegPick?.arrivalAirport?.code || null;
      const _userDepName = _userLegPick?.departureAirport?.name || null;
      const _userArrName = _userLegPick?.arrivalAirport?.name || null;
      if (_userDepCode || _userArrCode) {
        debugLog(`🎯 User-picked airports for ${prevCity}→${currentCity}: ${_userDepCode || '?'} → ${_userArrCode || '?'}`);
      }

      const fromAirport = (_userDepCode
        ? `${_userDepCode}${_userDepName ? ` (${_userDepName})` : ''}`
        : null)
        || legData?.from?.airport || flightData?.from?.airport || (fromIata ? `${fromIata} International Airport` : `${prevCity} Airport`);
      const toAirport = (_userArrCode
        ? `${_userArrCode}${_userArrName ? ` (${_userArrName})` : ''}`
        : null)
        || legData?.to?.airport || flightData?.to?.airport || (toIata ? `${toIata} International Airport` : `${currentCity} Airport`);
      // BUG 1 FIX: extractAirportCode and validateAirportCode now imported from shared displayGuardrails
      const fromAirportCode = _userDepCode || validateAirportCode(
        extractAirportCode(fromAirport) || extractAirportCode(legData?.from?.airport),
        prevCity || fromAirport
      ) || lookupCityAirportCode(prevCity) || lookupCityAirportCode(fromAirport) || '';
      const toAirportCode = _userArrCode || validateAirportCode(
        extractAirportCode(toAirport) || extractAirportCode(legData?.to?.airport),
        currentCity || toAirport
      ) || lookupCityAirportCode(currentCity) || lookupCityAirportCode(toAirport) || '';
      const travelNote = legData?.note || '';
      const connections = legData?.connections || []; // Connection flight data
      const isOvernightFromData = legData?.isOvernight || false;

      const [depHours, depMins] = departureTime.split(':').map(Number);
      const departureMinutes = (depHours || 8) * 60 + (depMins || 0);

      // Clamp cutoff for ANY inter-city departure (flight, train, bus, ferry):
      // No AI activities should show after the user departs
      if (isInterCityTravelDay && departureMinutes > 0) {
        cutoffEndTime = Math.min(cutoffEndTime, departureMinutes);
        isOvernightDepartureDay = true;
        debugLog(`🚀 Inter-city departure (${transportMode}): clamping cutoffEndTime to ${Math.floor(cutoffEndTime/60)}:${String(cutoffEndTime%60).padStart(2,'0')}`);
      }

      // For flights, calculate arrival time with timezone awareness
      // Parse flight duration from string like "14h 30m" or "~2 hours"
      let flightDurationHours = 2; // default
      if (travelDuration) {
        const hourMatch = travelDuration.match(/(\d+(?:\.\d+)?)\s*h/i);
        const minMatch = travelDuration.match(/(\d+)\s*m/i);
        if (hourMatch) {
          flightDurationHours = parseFloat(hourMatch[1]);
          if (minMatch) {
            flightDurationHours += parseInt(minMatch[1]) / 60;
          }
        }
      }

      // Calculate timezone-aware arrival time for flights
      let arrivalTime;
      let arrivalMinutes;
      let arrivalDayOffset = 0;
      let timezoneNote = '';

      // Store timezone difference for jet lag calculations
      let timezoneDiff = 0;

      if (transportMode === 'flight') {
        const flightArrival = calculateFlightArrival(departureTime, flightDurationHours, prevCity, currentCity);
        arrivalTime = flightArrival.localArrivalTime;
        arrivalDayOffset = flightArrival.dayOffset;
        timezoneNote = flightArrival.timezoneNote;
        timezoneDiff = flightArrival.timezoneDiff || 0;

        const [arrHours, arrMins] = arrivalTime.split(':').map(Number);
        arrivalMinutes = (arrHours || 10) * 60 + (arrMins || 0);

        debugLog(`✈️ Flight ${prevCity} → ${currentCity}: depart ${departureTime}, arrive ${arrivalTime} ${arrivalDayOffset !== 0 ? `(+${arrivalDayOffset} day)` : ''} ${timezoneNote}`);
      } else {
        // For non-flight transport (train, bus, car, ferry):
        // 1. Use provided arrival time if available
        // 2. Otherwise CALCULATE from departure + duration (don't use hardcoded fallback!)
        const providedArrival = legData?.arrivalTime || flightData?.arrivalTime;
        if (providedArrival) {
          arrivalTime = providedArrival;
          const [arrHours, arrMins] = arrivalTime.split(':').map(Number);
          arrivalMinutes = (arrHours || 10) * 60 + (arrMins || 0);
        } else {
          // Calculate arrival from departure + duration
          const durationMinutes = Math.round(flightDurationHours * 60); // flightDurationHours is already parsed from travelDuration
          arrivalMinutes = departureMinutes + durationMinutes;
          // Handle day wraparound
          if (arrivalMinutes >= 1440) {
            arrivalDayOffset = Math.floor(arrivalMinutes / 1440);
            arrivalMinutes = arrivalMinutes % 1440;
            debugLog(`⚠️ ${transportMode} travel crosses midnight: +${arrivalDayOffset} day(s)`);
          }
          const arrH = Math.floor(arrivalMinutes / 60);
          const arrM = arrivalMinutes % 60;
          arrivalTime = `${String(arrH).padStart(2, '0')}:${String(arrM).padStart(2, '0')}`;
          debugLog(`🚄 ${transportMode} ${prevCity} → ${currentCity}: depart ${departureTime} + ${travelDuration} = arrive ${arrivalTime}${arrivalDayOffset ? ` (+${arrivalDayOffset} day)` : ''}`);
        }
      }

      // D3: Store transport departure/arrival minutes for meal blocking
      icTransportDepartureMinutes = departureMinutes;
      icTransportArrivalMinutes = arrivalMinutes;

      // Calculate checkout time based on transport mode
      // For flights: derive checkout from transit-to-airport time so the order is always
      // breakfast → checkout → transit to airport → flight
      let checkoutMinutes;
      // Pre-compute flight transit time so checkout can be placed before it
      let precomputedTransitToAirportMinutes = null;
      if (transportMode === 'flight') {
        const toAirportTransitKeyForCheckout = `day${dayIndex}-to-departure-airport`;
        const cachedToAirportForCheckout = transitTimes[toAirportTransitKeyForCheckout] || {};
        const toAirportModeForCheckout = transportModes[toAirportTransitKeyForCheckout] || 'driving';
        const cachedToAirportDataForCheckout = cachedToAirportForCheckout[toAirportModeForCheckout];

        let preEstimatedTransitMins = 45; // Default for city hotels
        if (cachedToAirportDataForCheckout?.durationMinutes) {
          preEstimatedTransitMins = cachedToAirportDataForCheckout.durationMinutes;
          debugLog(`📍 Dynamic checkout buffer: ${prevCity} → airport transit = ${preEstimatedTransitMins} min`);
        } else {
          debugLog(`📍 No cached transit for checkout buffer calc, using default ${preEstimatedTransitMins} min`);
        }
        // Airport buffer: 2.5h for international, 1.5h for domestic (pre-compute for checkout placement)
        const prevCountryForCheckout = CITY_TO_COUNTRY[prevCity?.toLowerCase()] || '';
        const currCountryForCheckout = CITY_TO_COUNTRY[currentCity?.toLowerCase()] || '';
        const isIntlForCheckout = prevCountryForCheckout && currCountryForCheckout
          ? prevCountryForCheckout !== currCountryForCheckout
          : prevCity?.toLowerCase() !== currentCity?.toLowerCase();
        const airportBufferForCheckout = isIntlForCheckout ? 150 : 90;
        precomputedTransitToAirportMinutes = departureMinutes - preEstimatedTransitMins - airportBufferForCheckout;
        // Checkout = 15 min before transit to airport (time to gather luggage and leave)
        checkoutMinutes = precomputedTransitToAirportMinutes - 15;
        debugLog(`📍 Flight checkout timing: transit@${precomputedTransitToAirportMinutes} → checkout@${checkoutMinutes}`);
      } else {
        const checkoutBuffer = 60; // 1 hour before non-flight departure
        checkoutMinutes = departureMinutes - checkoutBuffer;
      }

      // Handle very early departures (checkout would be negative = previous day)
      const isOvernightCheckout = checkoutMinutes < 0;
      if (isOvernightCheckout) {
        checkoutMinutes = Math.max(0, checkoutMinutes); // Clamp to midnight
        debugWarn(`⚠️ Very early departure (${departureTime}) requires late-night checkout`);
      }

      const checkoutHours = Math.floor(checkoutMinutes / 60);
      const checkoutMins = checkoutMinutes % 60;

      // Flag very early checkouts (before 5 AM)
      const isVeryEarlyCheckout = checkoutMinutes < 300 && checkoutMinutes >= 0;

      // Store checkout time for pre-checkout activity filtering
      preCheckoutCutoffEnd = checkoutMinutes;

      const checkoutDescription = isOvernightCheckout
        ? `Very early checkout from ${prevHotel?.name || 'hotel'} - consider airport hotel`
        : isVeryEarlyCheckout
        ? `Early checkout from ${prevHotel?.name || 'hotel'}`
        : `Check out of ${prevHotel?.name || 'hotel'}`;

      // Inject breakfast before checkout on inter-city travel days (if none exists yet)
      // This mirrors the last-day departure logic — ensures breakfast → checkout → transit → flight
      if (!isDayTrip && !isVeryEarlyCheckout && !isOvernightCheckout && checkoutMinutes > 90) {
        const hasBreakfastAlready = timeline.some(item =>
          (item.type === 'meal' && (item.meal || item.activity || '').toLowerCase().includes('breakfast')) ||
          (item.activity || '').toLowerCase().includes('breakfast')
        );
        if (!hasBreakfastAlready) {
          const icBreakfastMinutes = checkoutMinutes - 60;
          if (icBreakfastMinutes >= 300) { // Only if breakfast would be at 5:00 AM or later
            const icBreakfastHours = Math.floor(icBreakfastMinutes / 60);
            const icBreakfastMins = icBreakfastMinutes % 60;
            timeline.push({
              type: 'meal', mealType: 'breakfast', originalType: 'breakfast',
              meal: 'Breakfast',
              time: `${String(icBreakfastHours).padStart(2, '0')}:${String(icBreakfastMins).padStart(2, '0')}`,
              activity: 'Breakfast',
              restaurant: prevHotel?.name ? `${prevHotel.name} Restaurant` : 'Hotel Restaurant',
              description: `Enjoy breakfast before checking out for your ${transportMode === 'flight' ? 'flight' : 'journey'}`,
              duration: '45 minutes',
              location: prevHotel?.name || 'Hotel',
              sortTime: icBreakfastMinutes,
              isDisplayInjected: true,
            });
            debugLog(`🍳 Injected breakfast at ${icBreakfastHours}:${String(icBreakfastMins).padStart(2, '0')} on inter-city travel day (before checkout@${checkoutHours}:${String(checkoutMins).padStart(2, '0')})`);
          }
        }
      }

      // Add checkout from previous hotel (skip for day trips — user keeps the same room)
      if (!isDayTrip) {
        // T7: subtitle must show the city we're actually checking OUT of
        // (prevCity — the hotel we slept in last night), not any trip-wide
        // destination list. On cross-city days prevCity may differ from
        // dayData.baseCity; still use prevCity because that's where the
        // hotel physically is.
        const checkoutHotelForSubtitle = prevHotel?.name || 'Hotel';
        const checkoutCityForSubtitle = sanitizeBaseCity(prevCity || prevDayData?.baseCity || dayData?.baseCity || '', prevCity || '');
        const checkoutLocation = checkoutCityForSubtitle
          ? `${checkoutHotelForSubtitle}, ${checkoutCityForSubtitle}`
          : checkoutHotelForSubtitle;
        timeline.push({
          type: 'checkout',
          time: `${String(checkoutHours).padStart(2, '0')}:${String(checkoutMins).padStart(2, '0')}`,
          activity: checkoutLabel,
          description: checkoutDescription,
          location: checkoutLocation,
          hotelName: checkoutHotelForSubtitle,
          checkoutCity: checkoutCityForSubtitle,
          duration: '15 minutes',
          sortTime: checkoutMinutes,
          isVeryEarly: isVeryEarlyCheckout || isOvernightCheckout
        });
      }

      // ISSUE 7 + BUG 15 FIX: Use the actual hotel name in transit labels whenever we have a real name.
      // For resort/hotel trips, we always have the name. For destination trips, use the name
      // if it's a recognizable property (not just generic "Hotel" or "hotel").
      const isResortProperty = tripType === 'hotel' ||
        (dayHotelName && dayHotelName !== 'Hotel' && dayHotelName !== 'hotel' && dayHotelName.length > 3);

      if (transportMode === 'flight') {
        // FLIGHT: Add transit to airport
        // Use cached Google Maps data if available, otherwise default to 45 min
        const toAirportTransitKey = `day${dayIndex}-to-departure-airport`;
        const cachedToAirport = transitTimes[toAirportTransitKey] || {};
        const toAirportMode = transportModes[toAirportTransitKey] || 'driving';
        const cachedToAirportData = cachedToAirport[toAirportMode];

        let estimatedTransitMins = 45; // Default for city hotels
        let transitDurationDisplay = '~45 min';
        let transitDescriptionExtra = '';

        if (cachedToAirportData?.durationMinutes) {
          // Use real Google Maps data
          estimatedTransitMins = cachedToAirportData.durationMinutes;
          transitDurationDisplay = cachedToAirportData.duration || `~${Math.round(estimatedTransitMins / 60 * 10) / 10} hrs`;
          if (estimatedTransitMins > 90) {
            transitDescriptionExtra = `Long drive to ${fromAirport} (${transitDurationDisplay}) - leave early! `;
          }
          debugLog(`📍 Using cached Google Maps transit: ${prevCity} → ${fromAirport} = ${transitDurationDisplay} (${estimatedTransitMins} mins)`);
        } else {
          debugLog(`📍 No cached transit data for ${toAirportTransitKey}, using default ${estimatedTransitMins} min`);
        }

        // Airport buffer: 2.5h for international, 1.5h for domestic
        const prevCountryLookup = CITY_TO_COUNTRY[prevCity?.toLowerCase()] || '';
        const currCountryLookup = CITY_TO_COUNTRY[currentCity?.toLowerCase()] || '';
        const isInternationalDeparture = prevCountryLookup && currCountryLookup
          ? prevCountryLookup !== currCountryLookup
          : prevCity?.toLowerCase() !== currentCity?.toLowerCase(); // different cities = assume international if unknown
        const airportBufferMins = isInternationalDeparture ? 150 : 90;
        const transitToAirportMinutes = departureMinutes - estimatedTransitMins - airportBufferMins;
        const transitToAirportHours = Math.floor(Math.max(0, transitToAirportMinutes) / 60);
        const transitToAirportMins = Math.max(0, transitToAirportMinutes) % 60;

        timeline.push({
          type: 'airport-transit',
          time: `${String(transitToAirportHours).padStart(2, '0')}:${String(transitToAirportMins).padStart(2, '0')}`,
          activity: '🚕 Transit to Airport',
          description: `${transitDescriptionExtra}Head to ${fromAirport} for your flight`,
          location: `${prevHotel?.name || 'Hotel'} → ${fromAirport}`,
          from: prevHotel?.name || 'Hotel',
          to: fromAirport,
          duration: transitDurationDisplay,
          sortTime: Math.max(0, transitToAirportMinutes),
          transitKey: toAirportTransitKey
        });

        // ─── Dynamic overnight/long-haul sanity net ──────────────────────
        // The three checks below (isOvernightFromData / arrivalDayOffset>0 /
        // late departure) all rely on the LLM's departureTime/arrivalTime
        // being correct. They aren't for exotic origins — Ambergris Caye →
        // Lisbon came back as "09:00 → 13:00 same day" with a 4h duration
        // in QA, because the city wasn't in any static geography table so
        // timezoneDiff computed as 0 and the duration fallback was '~4h'.
        // No per-city data will ever cover every exotic origin, so we do
        // this dynamically:
        //
        //   1. Any flight of 10+ hours that the LLM thinks is same-day
        //      gets promoted to overnight + arrivalDayOffset=1. Physically
        //      impossible to do a 10h flight without crossing a day line.
        //
        //   2. Any flight that crosses 5+ timezones (computed via IANA
        //      lookup, which works even when only ONE end of the leg is
        //      known) AND arrivalDayOffset=0 AND claimed duration >=6h
        //      gets promoted. Catches transatlantic / transpacific where
        //      the LLM forgot to add +1 day.
        //
        //   3. Any flight where the claimed arrival < 4h after departure
        //      but timezoneDiff >= 5 gets its duration bumped to a
        //      minimum 8h (short-hop floor for cross-continental). The
        //      actual arrivalTime isn't rewritten here — just the
        //      description duration string so the UI doesn't read "-4h".
        let isOvernightFlight = isOvernightFromData || arrivalDayOffset > 0 || (departureMinutes >= 1200);
        const crossesManyTimezones = Math.abs(timezoneDiff) >= 5;

        if (!isOvernightFlight && arrivalDayOffset === 0) {
          const forcedByDuration = flightDurationHours >= 10;
          const forcedByTimezones = crossesManyTimezones && flightDurationHours >= 6;
          if (forcedByDuration || forcedByTimezones) {
            debugLog(`✈️ Dynamic overnight promotion for ${prevCity}→${currentCity}: duration=${flightDurationHours}h, tzDiff=${timezoneDiff}h`);
            isOvernightFlight = true;
            arrivalDayOffset = 1;
          }
        }

        // Short-duration sanity: if the LLM gave a tiny "4h" duration on
        // a cross-continental leg (5+ timezones), bump the display string
        // to a reasonable long-haul minimum so the card doesn't show an
        // obviously-wrong flight time.
        if (crossesManyTimezones && flightDurationHours > 0 && flightDurationHours < 4) {
          debugLog(`✈️ Dynamic duration bump for ${prevCity}→${currentCity}: ${travelDuration} → ~8h (cross-continental floor)`);
          travelDuration = '~8h';
        }

        const isLongHaulFlight = flightDurationHours >= 6 || isOvernightFlight; // 6+ hours is long-haul

        // cutoffEndTime already clamped above (applies to all transport modes)
        const hasConnections = connections && connections.length > 0;

        // Build connection info string + compute total layover minutes so the
        // renderer can show "total journey" vs "flying time" per Option A of
        // the CUN→SPR layover display redesign (#3). Without this, the card
        // shows only flight-leg duration and readers can't tell the gap
        // between scheduled departure and arrival is actually longer.
        let connectionInfo = '';
        let totalLayoverMinutes = 0;
        if (hasConnections) {
          const connectionStops = connections.map(c => `${c.city || c.airport} (${c.layoverDuration || '~2h'})`).join(', ');
          connectionInfo = ` Connection${connections.length > 1 ? 's' : ''}: ${connectionStops}.`;
          totalLayoverMinutes = connections.reduce((sum, c) => {
            const raw = String(c.layoverDuration || c.layover || '');
            const hMatch = raw.match(/(\d+(?:\.\d+)?)\s*h/i);
            const mMatch = raw.match(/(\d+)\s*m(?!\w)/i); // avoid matching "meters"
            const h = hMatch ? parseFloat(hMatch[1]) : 0;
            const m = mMatch ? parseInt(mMatch[1], 10) : 0;
            const computed = h * 60 + m;
            return sum + (computed > 0 ? computed : 120); // fallback to ~2h per stop
          }, 0);
        }
        // Derive flying + total duration strings for the renderer.
        // flyingDurationStr = the raw flight-leg time (original travelDuration)
        // totalJourneyDurationStr = flight + layover(s), human-readable
        const flyingDurationStr = travelDuration;
        let totalJourneyDurationStr = null;
        if (hasConnections && totalLayoverMinutes > 0 && flightDurationHours > 0) {
          const totalHours = flightDurationHours + totalLayoverMinutes / 60;
          const h = Math.floor(totalHours);
          const m = Math.round((totalHours - h) * 60);
          totalJourneyDurationStr = m > 0 ? `${h}h ${m}m` : `${h}h`;
        }

        // Add flight card with timezone-aware arrival info
        const arrivalInfo = arrivalDayOffset > 0
          ? ` Arrives ${arrivalTime} next day ${timezoneNote}`.trim()
          : arrivalDayOffset < 0
          ? ` Arrives ${arrivalTime} same day ${timezoneNote}`.trim()
          : timezoneNote ? ` Arrives ${arrivalTime} ${timezoneNote}` : ` Arrives ${arrivalTime} local time.`;

        // Add overnight flight indicator and jet lag warning
        const overnightNote = isOvernightFlight ? ' 🌙 Overnight flight.' : '';
        const jetLagNote = isLongHaulFlight && Math.abs(timezoneDiff) >= 5
          ? ` ⚠️ Significant time zone change (${timezoneDiff > 0 ? '+' : ''}${timezoneDiff}hrs) - expect jet lag.`
          : '';

        timeline.push({
          type: 'inter-city-travel',
          time: departureTime,
          activity: `✈️ Flight to ${currentCity}${isOvernightFlight ? ' (Overnight)' : ''}${hasConnections ? ' (1 stop)' : ''}`,
          description: carrier
            ? `${carrier} flight from ${prevCity} to ${currentCity}. ${travelDuration} total.${connectionInfo}${arrivalInfo}${overnightNote}${jetLagNote}`
            : `Flight from ${prevCity} to ${currentCity}. ${travelDuration} total.${connectionInfo}${arrivalInfo}${overnightNote}${jetLagNote}`,
          duration: travelDuration,
          location: `${prevCity} → ${currentCity}`,
          from: fromAirport,
          to: toAirport,
          sortTime: departureMinutes,
          isTravel: true,
          travelMode: 'flight',
          transportData: legData || flightData || null,
          arrivalTime: arrivalTime,
          arrivalDayOffset: arrivalDayOffset,
          fromAirportCode: fromAirportCode,
          toAirportCode: toAirportCode,
          isOvernightFlight: isOvernightFlight,
          isLongHaulFlight: isLongHaulFlight,
          timezoneChange: timezoneDiff,
          connections: connections,
          // Option A layover display (see #3):
          //   duration            = flight-leg time only (travelDuration)
          //   totalJourneyDuration = flight + layover, only set when stops exist
          //   flyingDuration       = same as duration, named for renderer clarity
          flyingDuration: flyingDurationStr,
          totalJourneyDuration: totalJourneyDurationStr,
          totalLayoverMinutes: totalLayoverMinutes || undefined,
        });

        // For overnight flights arriving NEXT DAY, add a brief note on the departure day
        if (isOvernightFlight && arrivalDayOffset > 0) {
          const overnightSortTime = departureMinutes + 1;
          timeline.push({
            type: 'overnight-journey',
            time: arrivalTime || departureTime,
            activity: '✨ Overnight Journey',
            description: `Arriving at ${currentCity} tomorrow after the overnight flight. No hotel tonight — sleeping on the plane!`,
            location: `En route to ${currentCity}`,
            duration: travelDuration,
            sortTime: overnightSortTime,
            isOvernightJourney: true
          });
          // cutoffEndTime already clamped above for all inter-city departures
        }

        // For overnight flights arriving next day, add a "recovery" suggestion
        // IMPORTANT: Only add recovery note if this is NOT an overnight departure (where arrival is tomorrow)
        // The recovery note should appear on the ARRIVAL day, not the departure day
        if (isOvernightFlight && isLongHaulFlight && arrivalDayOffset === 0) {
          // Only add recovery note if we're arriving on the SAME day (not next day)
          // For overnight flights where arrivalDayOffset > 0, the recovery note will be added
          // when we process the ARRIVAL day, not the departure day
          const recoverySortTime = arrivalMinutes < departureMinutes
            ? departureMinutes + 2  // Sort after flight but before customs/transit
            : arrivalMinutes;
          timeline.push({
            type: 'recovery-note',
            time: arrivalTime,
            activity: '💤 Arrival & Recovery Time',
            description: `After a ${travelDuration} ${hasConnections ? 'connecting ' : ''}overnight flight with ${Math.abs(timezoneDiff) >= 5 ? 'significant jet lag' : 'time zone adjustment'}, consider a lighter schedule today. Stay hydrated and get sunlight to adjust faster.`,
            location: currentCity,
            duration: '2-3 hours',
            sortTime: recoverySortTime,
            isRecoveryNote: true
          });
        }

        // Jet lag adjustment note for long-haul same-day arrivals with big timezone changes
        // (e.g. HK→NYC: 16h flight but arrives same day due to -13h timezone shift)
        // These aren't "overnight" but the traveler still needs recovery time
        if (!isOvernightFlight && isLongHaulFlight && arrivalDayOffset <= 0 && Math.abs(timezoneDiff) >= 5) {
          timeline.push({
            type: 'recovery-note',
            time: arrivalTime,
            activity: '⏰ Jet Lag Adjustment',
            description: `After a ${travelDuration} flight with a ${Math.abs(timezoneDiff)}-hour time zone change, your body clock will need adjusting. Stay hydrated and get some sunlight to help reset your circadian rhythm.`,
            location: currentCity,
            duration: '2-3 hours',
            sortTime: arrivalMinutes + 1,
            isRecoveryNote: true
          });
        }
      } else if (transportMode === 'train') {
        // TRAIN: Add transit to station
        const transitToStationMinutes = Math.max(0, departureMinutes - 45); // Less buffer than flight, clamp to 0
        const transitToStationHours = Math.floor(transitToStationMinutes / 60);
        const transitToStationMins = transitToStationMinutes % 60;

        timeline.push({
          type: 'station-transit',
          time: `${String(Math.max(0, transitToStationHours)).padStart(2, '0')}:${String(transitToStationMins).padStart(2, '0')}`,
          activity: '🚕 Transit to Train Station',
          description: `Head to ${fromStation} for your train`,
          location: `${prevHotel?.name || 'Hotel'} → ${fromStation}`,
          from: prevHotel?.name || 'Hotel',
          to: fromStation,
          duration: '~30 min',
          sortTime: transitToStationMinutes,
          transitKey: `day${dayIndex}-to-station`
        });

        // Add train card
        timeline.push({
          type: 'inter-city-travel',
          time: departureTime,
          activity: `🚆 Train to ${currentCity}`,
          description: carrier
            ? `${carrier} from ${fromStation} to ${toStation}. ${travelDuration}.${travelNote ? ` ${travelNote}` : ''}`
            : `Train from ${prevCity} to ${currentCity}. ${travelDuration}.`,
          duration: travelDuration,
          location: `${fromStation} → ${toStation}`,
          from: fromStation,
          to: toStation,
          sortTime: departureMinutes,
          isTravel: true,
          travelMode: 'train',
          transportData: legData || null,
          arrivalTime: arrivalTime,
          arrivalDayOffset: arrivalDayOffset
        });

        // Add transit from station to hotel — ONLY if arriving same day
        // For overnight trains (arrivalDayOffset > 0), this is handled on the arrival day
        if (arrivalDayOffset <= 0) {
          // Use cached Google Maps data if available for station→hotel transit
          const fromStationTransitKey = `day${dayIndex}-from-station`;
          const cachedFromStation = transitTimes[fromStationTransitKey] || {};
          const fromStationMode = transportModes[fromStationTransitKey] || 'driving';
          const cachedFromStationData = cachedFromStation[fromStationMode];

          let stationTransitMins = 30; // Default
          let stationTransitDisplay = '~30 min';
          if (cachedFromStationData?.durationMinutes) {
            stationTransitMins = cachedFromStationData.durationMinutes;
            stationTransitDisplay = cachedFromStationData.duration || `~${stationTransitMins} min`;
          }

          const transitFromStationMinutes = arrivalMinutes + 15;
          const transitFromStationHours = Math.floor(transitFromStationMinutes / 60);
          const transitFromStationMins = transitFromStationMinutes % 60;

          const stationTransitLabel = isResortProperty && dayHotelName && dayHotelName !== 'Hotel'
            ? `🚕 Transit to ${dayHotelName}`
            : '🚕 Transit to Hotel';
          timeline.push({
            type: 'station-transit',
            time: `${String(transitFromStationHours).padStart(2, '0')}:${String(transitFromStationMins).padStart(2, '0')}`,
            activity: stationTransitLabel,
            description: `Transfer from ${toStation} to ${isResortProperty && dayHotelName !== 'Hotel' ? dayHotelName : 'your hotel'}`,
            location: `${toStation} → ${dayHotelName}`,
            from: toStation,
            to: dayHotelName,
            duration: stationTransitDisplay,
            sortTime: transitFromStationMinutes,
            transitKey: fromStationTransitKey
          });
        } else {
          // Overnight train — add journey note on departure day
          timeline.push({
            type: 'overnight-journey',
            time: departureTime,
            activity: '🌙 Overnight Train Journey',
            description: `Arriving at ${currentCity} tomorrow after the overnight train. No hotel tonight — sleeping on the train!`,
            location: `En route to ${currentCity}`,
            duration: travelDuration,
            sortTime: departureMinutes + 1,
            isOvernightJourney: true
          });
        }
      } else if (transportMode === 'ferry') {
        // FERRY: Transit to port + ferry + transit from port
        const ferryFromName = prevHotel?.name
          ? `${prevHotel.name}, ${prevCity}`
          : prevCity;
        const ferryToName = dayData?.hotel?.name
          ? `${dayData.hotel.name}, ${currentCity}`
          : currentCity;

        // Transit to ferry port
        const transitToPortMinutes = Math.max(0, departureMinutes - 60); // 1hr buffer for port
        const transitToPortHours = Math.floor(transitToPortMinutes / 60);
        const transitToPortMins = transitToPortMinutes % 60;

        timeline.push({
          type: 'port-transit',
          time: `${String(transitToPortHours).padStart(2, '0')}:${String(transitToPortMins).padStart(2, '0')}`,
          activity: '🚕 Transit to Ferry Port',
          description: `Head to ${prevCity} port for your ferry to ${currentCity}`,
          location: `${prevHotel?.name || 'Hotel'} → ${prevCity} Port`,
          from: prevHotel?.name || 'Hotel',
          to: `${prevCity} Port`,
          duration: '~30 min',
          sortTime: transitToPortMinutes,
          transitKey: `day${dayIndex}-to-port`
        });

        // Ferry card
        timeline.push({
          type: 'inter-city-travel',
          time: departureTime,
          activity: `⛴️ Ferry to ${currentCity}`,
          description: carrier
            ? `${carrier} from ${prevCity} to ${currentCity}. ${travelDuration}.${travelNote ? ` ${travelNote}` : ''}`
            : `Ferry from ${prevCity} to ${currentCity}. ${travelDuration}. Book in advance during peak season.`,
          duration: travelDuration,
          location: `${prevCity} → ${currentCity}`,
          from: ferryFromName,
          to: ferryToName,
          sortTime: departureMinutes,
          isTravel: true,
          travelMode: 'ferry',
          transportData: legData || null,
          arrivalTime: arrivalTime,
          arrivalDayOffset: arrivalDayOffset,
          transitKey: `day${dayIndex}-inter-city-ferry`
        });

        // Transit from port to hotel — ONLY if arriving same day
        if (arrivalDayOffset <= 0) {
          // Use cached Google Maps data if available for port→hotel transit
          const fromPortTransitKey = `day${dayIndex}-from-port`;
          const cachedFromPort = transitTimes[fromPortTransitKey] || {};
          const fromPortMode = transportModes[fromPortTransitKey] || 'driving';
          const cachedFromPortData = cachedFromPort[fromPortMode];

          let portTransitMins = 30;
          let portTransitDisplay = '~30 min';
          if (cachedFromPortData?.durationMinutes) {
            portTransitMins = cachedFromPortData.durationMinutes;
            portTransitDisplay = cachedFromPortData.duration || `~${portTransitMins} min`;
          }

          const transitFromPortMinutes = arrivalMinutes + 15;
          const transitFromPortHours = Math.floor(transitFromPortMinutes / 60);
          const transitFromPortMins = transitFromPortMinutes % 60;

          const portTransitLabel = isResortProperty && dayHotelName && dayHotelName !== 'Hotel'
            ? `🚕 Transit to ${dayHotelName}`
            : '🚕 Transit to Hotel';
          timeline.push({
            type: 'port-transit',
            time: `${String(transitFromPortHours).padStart(2, '0')}:${String(transitFromPortMins).padStart(2, '0')}`,
            activity: portTransitLabel,
            description: `Transfer from ${currentCity} port to ${isResortProperty && dayHotelName !== 'Hotel' ? dayHotelName : 'your hotel'}`,
            location: `${currentCity} Port → ${dayHotelName}`,
            from: `${currentCity} Port`,
            to: dayHotelName,
            duration: portTransitDisplay,
            sortTime: transitFromPortMinutes,
            transitKey: fromPortTransitKey
          });
        } else {
          // Overnight ferry — add journey note on departure day
          timeline.push({
            type: 'overnight-journey',
            time: departureTime,
            activity: '🌙 Overnight Ferry Journey',
            description: `Arriving at ${currentCity} tomorrow after the overnight ferry. No hotel tonight — sleeping on the ferry!`,
            location: `En route to ${currentCity}`,
            duration: travelDuration,
            sortTime: departureMinutes + 1,
            isOvernightJourney: true
          });
        }
      } else {
        // CAR/BUS: Direct drive or bus journey
        // Determine proper origin/destination names (hotel names or city names)
        const driveFromName = prevHotel?.name
          ? `${prevHotel.name}, ${prevCity}`
          : prevCity;
        const driveToName = dayData?.hotel?.name
          ? `${dayData.hotel.name}, ${currentCity}`
          : currentCity;

        // Determine transport emoji and label based on mode
        const transportLabel = transportMode === 'bus' ? `🚌 Bus to ${currentCity}`
          : `🚗 Drive to ${currentCity}`;
        const transportDescLabel = transportMode === 'bus' ? 'Bus ride'
          : 'Scenic drive';

        timeline.push({
          type: 'inter-city-travel',
          time: departureTime,
          activity: transportLabel,
          description: carrier
            ? `${carrier} from ${prevCity} to ${currentCity}. ${travelDuration}.${travelNote ? ` ${travelNote}` : ''}`
            : `${transportDescLabel} from ${prevCity} to ${currentCity}. ${travelDuration}.`,
          duration: travelDuration,
          location: `${prevCity} → ${currentCity}`,
          from: driveFromName,
          to: driveToName,
          sortTime: departureMinutes,
          isTravel: true,
          travelMode: transportMode,
          transportData: legData || null,
          arrivalTime: arrivalTime,
          arrivalDayOffset: arrivalDayOffset,
          transitKey: `day${dayIndex}-inter-city-drive`
        });

        // For overnight bus/drive journeys, add journey note
        if (arrivalDayOffset > 0) {
          const modeEmoji = transportMode === 'bus' ? '🌙' : '🌙';
          const modeLabel = transportMode === 'bus' ? 'Bus' : 'Drive';
          timeline.push({
            type: 'overnight-journey',
            time: departureTime,
            activity: `${modeEmoji} Overnight ${modeLabel} Journey`,
            description: `Arriving at ${currentCity} tomorrow after the overnight ${modeLabel.toLowerCase()}. No hotel tonight — sleeping on the ${transportMode === 'bus' ? 'bus' : 'road'}!`,
            location: `En route to ${currentCity}`,
            duration: travelDuration,
            sortTime: departureMinutes + 1,
            isOvernightJourney: true
          });
        }
      }

      // For flights only: Check if this is an international flight and add customs
      // IMPORTANT: Only add customs/transit on the DEPARTURE day if the flight arrives SAME day.
      // For overnight flights (arrivalDayOffset > 0), customs/transit/check-in are handled
      // on the ARRIVAL day by the overnight arrival processing code (see isArrivalDayAfterOvernightTravel).
      if (transportMode === 'flight' && arrivalDayOffset <= 0) {
        // Check if this is an international flight (different countries)
        // Uses getCityCountrySync which checks both static CITY_TO_COUNTRY table
        // and dynamic Haiku cache (populated by preResolveCities)
        const prevCountry = getCityCountrySync(prevCity);
        const currentCountry = getCityCountrySync(currentCity);

        // Determine if this is an international flight
        // 1. If both countries are known and different, it's international
        // 2. For multi-country itineraries with unknown cities, assume international when cities change
        let isInternationalFlight = prevCountry && currentCountry && prevCountry !== currentCountry;

        // Fallback: Check if itinerary indicates this is a multi-country trip
        // and we couldn't resolve both cities (getCityCountrySync covers all regions via Haiku cache)
        if (!isInternationalFlight && itinerary?.countriesIncluded?.length > 1) {
          // If one or both cities are unresolved but the trip spans multiple countries,
          // treat it as international when cities are clearly different
          if ((!prevCountry || !currentCountry) && prevCity?.toLowerCase() !== currentCity?.toLowerCase()) {
            isInternationalFlight = true;
          }
        }

        // Add customs/immigration for international flights
        // Skip on departure day for overnight flights — arrival cards belong on the next day
        if (isInternationalFlight && arrivalDayOffset <= 0) {
          const customsMinutes = arrivalMinutes + 15; // 15 min after landing
          const customsHours = Math.floor(customsMinutes / 60);
          const customsMins = customsMinutes % 60;

          // Check if this is Schengen-to-Schengen (no passport control)
          const isSchengenToSchengen = prevCountry && currentCountry &&
            SCHENGEN_COUNTRIES.includes(prevCountry) && SCHENGEN_COUNTRIES.includes(currentCountry);

          // Get visa info for destination country
          let visaNote = '';
          if (currentCountry) {
            if (VISA_REQUIREMENTS_US.eVisa.includes(currentCountry)) {
              visaNote = ` ⚠️ E-Visa required for ${currentCountry} - apply online before departure.`;
            } else if (VISA_REQUIREMENTS_US.visaRequired.includes(currentCountry)) {
              visaNote = ` ⚠️ Visa required for ${currentCountry} - apply at embassy before travel.`;
            }
          }

          // Get border crossing note
          let borderNote = '';
          if (isSchengenToSchengen) {
            borderNote = ' No passport control (Schengen area).';
          } else if ((prevCountry === 'UK' && SCHENGEN_COUNTRIES.includes(currentCountry)) ||
                     (currentCountry === 'UK' && SCHENGEN_COUNTRIES.includes(prevCountry))) {
            borderNote = ' Passport control required (UK is outside Schengen).';
          }

          // Get currency info
          const destCurrency = COUNTRY_TO_CURRENCY[currentCountry];
          const currencyNote = destCurrency ? ` 💱 Local currency: ${destCurrency}.` : '';

          // FIX: For timezone changes where arrival time appears "earlier" than departure,
          // we need to ensure post-arrival events sort AFTER the flight departure.
          // This happens when flying west-to-east (e.g., Tokyo 9am -> Dubai 7am due to timezone).
          // The sortTime should be: departure time + small offset to ensure correct order.
          const arrivalSortTimeBase = arrivalMinutes < departureMinutes
            ? departureMinutes + 1  // Arrival appears after departure in timeline
            : arrivalMinutes;
          const customsSortTime = arrivalSortTimeBase + 15;

          // Only show customs card if there's actual passport control
          if (!isSchengenToSchengen) {
            // Add timezone indicator for significant timezone changes
            const tzIndicator = timezoneDiff !== 0 ? ` 🕐 ${currentCity} time` : '';

            timeline.push({
              type: 'arrival-customs',
              time: `${String(customsHours).padStart(2, '0')}:${String(customsMins).padStart(2, '0')}`,
              activity: '🛂 Customs & Immigration',
              description: `International arrival processing at ${toAirport}. Have passport and any required documents ready.${visaNote}${borderNote}${currencyNote}${tzIndicator}`,
              location: toAirport,
              duration: '45 minutes',
              sortTime: customsSortTime,
              visaRequired: VISA_REQUIREMENTS_US.eVisa.includes(currentCountry) || VISA_REQUIREMENTS_US.visaRequired.includes(currentCountry),
              currency: destCurrency,
              isNewTimezone: timezoneDiff !== 0,
              timezoneCity: currentCity
            });
          }
        }

        // Add transit from airport to hotel (skip for overnight — arrival cards on next day)
        if (arrivalDayOffset <= 0) {
        // Allow more time after international flights (customs adds ~45 min)
        const transitFromAirportMinutes = isInternationalFlight
          ? arrivalMinutes + 60  // 1 hour after landing for international (includes customs)
          : arrivalMinutes + 30; // 30 min after landing for domestic
        const transitFromAirportHours = Math.floor(transitFromAirportMinutes / 60);
        const transitFromAirportMins = transitFromAirportMinutes % 60;

        // FIX: Use adjusted sortTime for timezone changes where arrival appears earlier than departure
        const transitSortTimeBase = arrivalMinutes < departureMinutes
          ? departureMinutes + 1
          : arrivalMinutes;
        const transitFromAirportSortTime = isInternationalFlight
          ? transitSortTimeBase + 60
          : transitSortTimeBase + 30;

        // Add timezone indicator for transit if there was a timezone change
        const transitTzNote = timezoneDiff !== 0 ? ` (${currentCity} local time)` : '';

        // Smart transit duration for airport-to-hotel based on destination
        // Use cached Google Maps data for airport-to-hotel transit if available
        const fromAirportTransitKey = `day${dayIndex}-from-arrival-airport`;
        const cachedFromAirport = transitTimes[fromAirportTransitKey] || {};
        const fromAirportMode = transportModes[fromAirportTransitKey] || 'driving';
        const cachedFromAirportData = cachedFromAirport[fromAirportMode];

        let arrivalTransitMins = 45; // Default
        let arrivalTransitDisplay = '~45 min';
        let arrivalTransitDescExtra = '';

        if (cachedFromAirportData?.durationMinutes) {
          arrivalTransitMins = cachedFromAirportData.durationMinutes;
          arrivalTransitDisplay = cachedFromAirportData.duration || `~${Math.round(arrivalTransitMins / 60 * 10) / 10} hrs`;
          if (arrivalTransitMins > 90) {
            arrivalTransitDescExtra = `Long transfer (${arrivalTransitDisplay}) - `;
          }
          debugLog(`📍 Using cached Google Maps transit: ${toAirport} → ${dayHotelName} = ${arrivalTransitDisplay} (${arrivalTransitMins} mins)`);
        } else {
          debugLog(`📍 No cached transit data for ${fromAirportTransitKey}, using default ${arrivalTransitMins} min`);
        }

        // ISSUE 7 FIX: Use resort/property name in transit label for resort trips
        const transitToLabel = isResortProperty && dayHotelName && dayHotelName !== 'Hotel'
          ? `🚕 Transit to ${dayHotelName}`
          : '🚕 Transit to Hotel';
        const arrivalTransitNote = `${arrivalTransitDescExtra}Transfer from ${toAirport} to ${isResortProperty && dayHotelName !== 'Hotel' ? dayHotelName : 'your hotel'}${transitTzNote}`;

        timeline.push({
          type: 'airport-transit',
          time: `${String(transitFromAirportHours).padStart(2, '0')}:${String(transitFromAirportMins).padStart(2, '0')}`,
          activity: transitToLabel,
          description: arrivalTransitNote,
          location: `${toAirport} → ${dayHotelName}`,
          from: toAirport,
          to: dayHotelName,
          duration: arrivalTransitDisplay,
          sortTime: transitFromAirportSortTime,
          transitKey: fromAirportTransitKey,
          isNewTimezone: timezoneDiff !== 0,
          timezoneCity: currentCity
        });
        } // End of arrivalDayOffset <= 0 guard
      } // End of flight block

      // Add hotel check-in at new city (for all transport modes)
      // BUT NOT on the last day - user is leaving, not checking in!
      // AND NOT if the flight arrives next day (overnight/red-eye) - check-in happens on arrival day
      // AND NOT if a check-in already exists for this day (prevent duplicates)
      const isOvernightArrival = arrivalDayOffset > 0;
      const hasExistingCheckin = timeline.some(item => item.type === 'hotel-checkin');

      if (!isLastDay && !isOvernightArrival && !hasExistingCheckin) {
        // Use cached Google Maps data for airport-to-hotel transit to compute dynamic check-in time
        let dynamicArrivalTransitMins = 45; // Default
        if (transportMode === 'flight') {
          const fromAirportTransitKeyForCheckin = `day${dayIndex}-from-arrival-airport`;
          const cachedFromAirportForCheckin = transitTimes[fromAirportTransitKeyForCheckin] || {};
          const fromAirportModeForCheckin = transportModes[fromAirportTransitKeyForCheckin] || 'driving';
          const cachedFromAirportDataForCheckin = cachedFromAirportForCheckin[fromAirportModeForCheckin];

          if (cachedFromAirportDataForCheckin?.durationMinutes) {
            dynamicArrivalTransitMins = cachedFromAirportDataForCheckin.durationMinutes;
            debugLog(`📍 Dynamic check-in: ${toAirport} → ${dayHotelName} transit = ${dynamicArrivalTransitMins} min`);
          } else {
            debugLog(`📍 No cached transit for check-in calc, using default ${dynamicArrivalTransitMins} min`);
          }
        }
        // Check-in time: landing + customs (international) + transit + settle-in buffer
        const arrivalCountryPrev = CITY_TO_COUNTRY[prevCity?.toLowerCase()] || '';
        const arrivalCountryCurr = CITY_TO_COUNTRY[currentCity?.toLowerCase()] || '';
        const isInternationalArrival = arrivalCountryPrev && arrivalCountryCurr
          ? arrivalCountryPrev !== arrivalCountryCurr
          : prevCity?.toLowerCase() !== currentCity?.toLowerCase();
        const customsBufferMins = isInternationalArrival ? 60 : 15; // 60 min customs+baggage intl, 15 min domestic
        const checkinMinutes = transportMode === 'flight'
          ? arrivalMinutes + customsBufferMins + dynamicArrivalTransitMins + 30 // customs + transit + settle-in
          : transportMode === 'train'
            ? arrivalMinutes + 45 // 45 min after train arrival (already have station transit)
            : departureMinutes + parseDurationToMinutesTravel(travelDuration, prevCity, currentCity) + 15; // After drive + 15min buffer
        const checkinHours = Math.floor(checkinMinutes / 60);
        const checkinMins = checkinMinutes % 60;

        // FIX: Use adjusted sortTime for timezone changes where arrival appears earlier than departure
        const checkinSortTimeBase = transportMode === 'flight' && arrivalMinutes < departureMinutes
          ? departureMinutes + 1
          : (transportMode === 'flight' ? arrivalMinutes : checkinMinutes);
        const checkinSortTime = transportMode === 'flight'
          ? checkinSortTimeBase + 75
          : checkinMinutes;

        // Add timezone indicator for check-in if there was a timezone change (flights only)
        const checkinTzNote = transportMode === 'flight' && timezoneDiff !== 0 ? ` (${currentCity} local time)` : '';

        if (isDayTrip) {
          // Day trip — no hotel check-in at destination, add "Return" card instead
          const returnTime = Math.max(arrivalMinutes + parseDurationToMinutes(travelDuration || '2h'), 1080); // At least 6pm
          const returnHours = Math.floor((returnTime % 1440) / 60);
          const returnMins = (returnTime % 1440) % 60;
          const prevBaseCity = itinerary?.days?.[dayIndex - 1]?.baseCity || 'base city';
          timeline.push({
            type: 'day-trip-return',
            time: `${String(returnHours).padStart(2, '0')}:${String(returnMins).padStart(2, '0')}`,
            activity: `🔙 Return to ${prevBaseCity}`,
            description: `Head back to ${prevHotel?.name || prevBaseCity} after your day trip to ${currentCity}`,
            location: `${currentCity} → ${prevBaseCity}`,
            duration: travelDuration || '~2h',
            sortTime: returnTime
          });
          postCheckinCutoffStart = null; // Day trip — no separate post-checkin window
        } else {
          timeline.push({
            type: 'hotel-checkin',
            time: `${String(checkinHours).padStart(2, '0')}:${String(checkinMins).padStart(2, '0')}`,
            activity: checkinLabel,
            description: `Check in to ${dayHotelName}, get settled${checkinTzNote}`,
            location: dayHotelName,
            duration: '30 minutes',
            sortTime: checkinSortTime,
            isNewTimezone: transportMode === 'flight' && timezoneDiff !== 0,
            timezoneCity: currentCity
          });
        }

        // Set cutoff so afternoon activities don't start before check-in complete
        // But keep cutoffStartTime at 0 to allow morning activities before checkout
        // BUG 5 FIX: Cap at 22:00 (1320 mins) so late arrivals still have an evening activity window
        if (!isDayTrip) postCheckinCutoffStart = Math.min(checkinMinutes + 30, 1320);
        // Don't override cutoffStartTime - we want to allow pre-checkout activities
        debugLog(`📍 Inter-city Day ${dayIndex + 1}: Check-in at ${checkinHours}:${String(checkinMins).padStart(2, '0')}, postCheckinCutoffStart=${postCheckinCutoffStart} mins, preCheckoutCutoffEnd=${preCheckoutCutoffEnd} mins`);
      } else if (isOvernightArrival) {
        debugLog(`🌙 Day ${dayIndex + 1}: Overnight flight - hotel check-in will be on arrival day (Day ${dayIndex + 2})`);
        // Fix 5: Removed duplicate 'overnight-note' card. The 'overnight-journey' card
        // at line ~11067 (inside the inter-city transport block) already provides this info.
        // Keep only the critical activity-blocking logic below.

        // CRITICAL: Set postCheckinCutoffStart to VERY HIGH so NO activities are allowed after the flight
        // This prevents the LLM-generated activities (which are for the DESTINATION city) from appearing
        // on the DEPARTURE day. The user spends this day in the OLD city until departure, then sleeps on plane.
        postCheckinCutoffStart = 9999; // No post-checkin activities allowed on overnight departure day
        debugLog(`🌙 Day ${dayIndex + 1}: Overnight flight - blocking all post-departure activities (postCheckinCutoffStart=${postCheckinCutoffStart})`);
      }

      // DEFENSIVE: Verify an inter-city-travel card was actually added to the timeline.
      // If not (e.g., AI forgot interCityTransport entry and smart detection failed),
      // inject a fallback transport card so the user isn't confused by a missing flight/train.
      const hasTransportCard = timeline.some(item => item.type === 'inter-city-travel');
      if (!hasTransportCard) {
        debugWarn(`⚠️ Day ${dayIndex + 1}: Inter-city travel day but no transport card found! Adding fallback for ${prevCity} → ${currentCity}`);

        const fallbackMode = getSmartTransportMode(prevCity, currentCity);
        const fallbackDuration = fallbackMode === 'flight'
          ? getSmartFlightDuration(prevCity, currentCity)
          : fallbackMode === 'train' ? '~3h' : '~4h';

        timeline.push({
          type: 'inter-city-travel',
          time: '09:00',
          activity: fallbackMode === 'flight'
            ? `✈️ Flight to ${currentCity}`
            : fallbackMode === 'train'
            ? `🚆 Train to ${currentCity}`
            : `🚗 Drive to ${currentCity}`,
          description: `Travel from ${prevCity} to ${currentCity}. ${fallbackDuration}.`,
          duration: fallbackDuration,
          location: `${prevCity} → ${currentCity}`,
          from: prevCity,
          to: currentCity,
          sortTime: 540, // 9:00 AM
          isTravel: true,
          travelMode: fallbackMode,
          isFallbackTransport: true
        });
      }
    }

  // END OF PASS 2b — remaining passes will append below this point.
  // Pass 2c picks up at web line 12987.

    // Debug: Log final cutoff times
    debugLog(`⏰ Day ${dayIndex + 1} cutoff times: start=${cutoffStartTime} (${Math.floor(cutoffStartTime/60)}:${String(cutoffStartTime%60).padStart(2,'0')}), end=${cutoffEndTime} (${Math.floor(cutoffEndTime/60)}:${String(cutoffEndTime%60).padStart(2,'0')})`);

    // Get the current day's city for filtering
    const currentDayCity = dayData?.baseCity?.toLowerCase() || '';
    const prevDayCity = dayIndex > 0 ? (itinerary?.days?.[dayIndex - 1]?.baseCity?.toLowerCase() || '') : '';

    // Collect all activities and meals
    const allItems = [];

    // Activity types to filter out on Day 1 (we add these ourselves)
    const arrivalActivityPatterns = [
      'arrival', 'immigration', 'customs', 'landing', 'check-in to hotel',
      'hotel check-in', 'transit to hotel', 'transfer to hotel'
    ];

    // Activity types to filter out on Last Day (we add these ourselves)
    const departureActivityPatterns = [
      'transit to airport', 'transfer to airport', 'airport departure',
      'hotel checkout', 'check out', 'check-out', 'departure'
    ];

    // BUG 4 FIX: Inter-city travel activity patterns — use regex word boundaries
    // to catch "Flight Auckland To Queenstown" where city name is between keywords.
    // Flight/fly patterns use broad .* since flights are always inter-city.
    // Drive/bus/train/transfer/travel patterns keep contiguous match to avoid
    // false positives on local activities like "Drive to the beach".
    const interCityTravelPatterns = [
      /\bflight\b.*\bto\b/i, /\bfly\b.*\bto\b/i,       // Flights: broad match (always inter-city)
      /\bflight\s+from\b/i, /\bfly\s+from\b/i,           // "Flight from X"
      /\btransfer\s+to\b/i, /\btravel\s+to\b/i,          // Contiguous only (avoid local activity FPs)
      /\bdrive\s+to\b/i, /\bdriving\s+to\b/i,            // Driving
      /\bbus\s+to\b/i, /\btrain\s+to\b/i,                // Surface transport
      /\bferry\s+to\b/i, /\bboat\s+to\b/i,               // Water transport (from shared)
      /\bdepart\s+for\b/i, /\bhead\s+to\s+airport\b/i,
      /\broad\s+trip\b/i, /\ben\s+route\b/i, /\bjourney\s+to\b/i, // From shared
    ];

    // Helper to check if activity location is in wrong city
    const isWrongCityActivity = (item) => {
      if (!isInterCityTravelDay || !prevDayCity || !currentDayCity) return false;

      const location = (item.location || item.address || '').toLowerCase();
      const activity = (item.activity || '').toLowerCase();

      // Check if location mentions the previous city (wrong city for this day)
      if (location.includes(prevDayCity) || activity.includes(prevDayCity)) {
        // But make sure it's not just a "Flight FROM prevCity" description
        if (!activity.includes('flight') && !activity.includes('travel')) {
          debugLog(`🚫 Filtering wrong-city activity: ${item.activity} (${prevDayCity} activity on ${currentDayCity} day)`);
          return true;
        }
      }

      // Check for common Cappadocia/Göreme locations on non-Cappadocia days
      const cappadociaIndicators = ['göreme', 'goreme', 'cappadocia', 'kapadokya', 'uchisar', 'urgup', 'avanos', 'derinkuyu', 'ihlara'];
      if (!currentDayCity.includes('cappadocia') && !currentDayCity.includes('göreme') && !currentDayCity.includes('goreme')) {
        for (const indicator of cappadociaIndicators) {
          if (location.includes(indicator) || activity.includes(indicator)) {
            debugLog(`🚫 Filtering Cappadocia activity on non-Cappadocia day: ${item.activity}`);
            return true;
          }
        }
      }

      return false;
    };

    // NOTE: isUnrealisticDayTrip and isMismatchedCity filters are now handled by normalizeDay (Stages 5-6)

    // Cap activities on travel days to prevent overpacked schedules
    let travelDayActivityCount = 0;
    const TRAVEL_DAY_ACTIVITY_CAP = 4;

    // Dynamic departure day cap based on available hours before cutoff
    let departureDayCap = 2;
    if (isLastDay) {
      const dayStartMin = parseTime(userProfile?.preferences?.preferredStartTime || '09:00') || 540;
      const availableHours = Math.max(0, (cutoffEndTime - dayStartMin) / 60);
      if (availableHours < 2) departureDayCap = 1;
      else if (availableHours < 4) departureDayCap = 2;
      else if (availableHours < 6) departureDayCap = 3;
      else departureDayCap = 4;
    }

    const effectiveActivityCap = isLastDay ? departureDayCap : TRAVEL_DAY_ACTIVITY_CAP;
    const isTravelCapDay = isInterCityTravelDay || isFirstDay || isLastDay;

    // D5: Clean overnight arrival day — strip AI departure logistics, pre-11 AM items, post-9 PM items
    if (isArrivalDayAfterOvernightTravel && dayData.schedule) {
      const overnightCleaned = cleanOvernightArrivalDay(dayData.schedule, dayData.meals, { isOvernightArrival: true });
      dayData.schedule = overnightCleaned.schedule;
      dayData.meals = overnightCleaned.meals;
      debugLog(`🌅 D5: Cleaned overnight arrival day ${dayIndex + 1}: ${overnightCleaned.schedule.length} schedule items, ${overnightCleaned.meals.length} meals remain`);
    }

    if (dayData.schedule) {
      dayData.schedule.forEach((item, index) => {
        // NOTE: AM/PM time correction, suspicious time filtering, and geographic validation
        // are now handled by normalizeDay() before data enters state.
        // This render layer only handles cutoff windows and arrival/departure filtering.
        let itemTime = parseTime(item.time);

        // SAFETY NET: If time falls 1:00-5:59 AM and activity isn't an early-morning one,
        // it's almost certainly a PM time that slipped through normalization
        if (itemTime >= 60 && itemTime < 360) {
          const actLower = (item.activity || '').toLowerCase();
          const earlyKeywords = ['sunrise', 'dawn', 'early morning', 'airport', 'flight', 'departure', 'checkout', 'check-out', 'game drive'];
          const isEarlyMorning = earlyKeywords.some(kw => actLower.includes(kw));
          if (!isEarlyMorning) {
            itemTime += 12 * 60; // Shift to PM
            item.time = `${String(Math.floor(itemTime / 60)).padStart(2, '0')}:${String(itemTime % 60).padStart(2, '0')}`;
            timelineLog(`  ⚠️ AM→PM safety fix: "${item.activity}" at ${item.time} (was ${itemTime - 720} mins)`);
          }
        }

        // For inter-city travel days: allow activities either before checkout OR after check-in
        if (isInterCityTravelDay && preCheckoutCutoffEnd !== null && postCheckinCutoffStart !== null) {
          const isPreCheckoutActivity = itemTime >= cutoffStartTime && itemTime < preCheckoutCutoffEnd;
          const isPostCheckinActivity = itemTime >= postCheckinCutoffStart && itemTime < cutoffEndTime;
          if (!isPreCheckoutActivity && !isPostCheckinActivity) {
            debugLog(`🚫 Filtering activity outside travel day windows: ${item.activity} at ${item.time} (${itemTime} mins)`);
            return;
          }
        } else {
          // Normal cutoff logic for non-travel days
          if (itemTime < cutoffStartTime || itemTime >= cutoffEndTime) return;

          // On last day, also filter activities that would END after checkout
          // This prevents activities from overlapping with airport transit
          if (isLastDay && cutoffEndTime < 24 * 60) {
            // Parse activity duration to calculate end time
            const durationStr = item.duration || '';
            let durationMinutes = 60; // Default 1 hour
            const hourMatch = durationStr.match(/(\d+\.?\d*)\s*(?:h|hour)/i);
            const minMatch = durationStr.match(/(\d+)\s*(?:m|min)/i);
            if (hourMatch) durationMinutes = parseFloat(hourMatch[1]) * 60;
            if (minMatch) durationMinutes += parseInt(minMatch[1]);

            const activityEndTime = itemTime + durationMinutes;
            if (activityEndTime > cutoffEndTime) {
              debugLog(`🚫 Filtering last day activity that would end after checkout: ${item.activity} ends at ${Math.floor(activityEndTime/60)}:${String(activityEndTime%60).padStart(2,'0')} (checkout at ${Math.floor(cutoffEndTime/60)}:${String(cutoffEndTime%60).padStart(2,'0')})`);
              return;
            }
          }
        }

        const activityLower = (item.activity || '').toLowerCase();

        // On Day 1, filter out AI-generated arrival/airport activities
        if (isFirstDay) {
          const isArrivalActivity = arrivalActivityPatterns.some(pattern =>
            activityLower.includes(pattern)
          );
          if (isArrivalActivity) {
            debugLog(`🚫 Filtering out Day 1 arrival activity: ${item.activity}`);
            return; // Skip this activity
          }
        }

        // On Last Day, filter out AI-generated departure activities and filler activities
        if (isLastDay) {
          const isDepartureActivity = departureActivityPatterns.some(pattern =>
            activityLower.includes(pattern)
          );
          if (isDepartureActivity) {
            debugLog(`🚫 Filtering out Last Day departure activity: ${item.activity}`);
            return; // Skip this activity
          }
          // Filter filler activities (pack bags, travel journaling, etc.)
          const FILLER_PATTERNS = [
            'travel journaling', 'pack luggage', 'pack bags', 'packing',
            'rest at hotel', 'relax at hotel', 'hotel rest', 'nap at hotel',
            'free time at hotel', 'settle in', 'freshen up',
          ];
          if (FILLER_PATTERNS.some(p => activityLower.includes(p))) {
            debugLog(`🚫 Filtering out Last Day filler activity: ${item.activity}`);
            return;
          }
        }

        // On inter-city travel days, filter out travel activities (we add our own) and wrong city activities
        if (isInterCityTravelDay) {
          // BUG 4 FIX: Filter out inter-city travel activities using regex matching
          const isTravelActivity = interCityTravelPatterns.some(pattern =>
            pattern.test(activityLower)
          );
          if (isTravelActivity) {
            debugLog(`🚫 Filtering out travel activity: ${item.activity}`);
            return;
          }

          // For pre-checkout activities, skip wrong-city filtering (they're in the OLD city)
          // For post-checkin activities, apply wrong-city filtering (they should be in NEW city)
          if (itemTime >= postCheckinCutoffStart && isWrongCityActivity(item)) {
            return;
          }
        }

        // NOTE: Unrealistic day trip and mismatched city filtering now handled by normalizeDay() Stage 6.

        // Fix time-of-day mislabeling in activity names (e.g., "Morning Run" scheduled at 12:00 PM)
        let correctedActivity = item.activity;
        if (correctedActivity && itemTime !== undefined) {
          const hour = Math.floor(itemTime / 60);
          if (hour >= 12 && /\bmorning\b/i.test(correctedActivity)) {
            correctedActivity = correctedActivity.replace(/\bmorning\b/gi, hour < 17 ? 'Afternoon' : 'Evening');
          } else if (hour < 12 && /\bevening\b/i.test(correctedActivity)) {
            correctedActivity = correctedActivity.replace(/\bevening\b/gi, 'Morning');
          } else if ((hour < 12 || hour >= 17) && /\bafternoon\b/i.test(correctedActivity)) {
            correctedActivity = correctedActivity.replace(/\bafternoon\b/gi, hour < 12 ? 'Morning' : 'Evening');
          }
        }

        // Cap activities on travel/arrival/departure days to prevent overpacked schedules
        if (isTravelCapDay) {
          travelDayActivityCount++;
          if (travelDayActivityCount > effectiveActivityCap) {
            debugLog(`🚫 Capping travel day activity #${travelDayActivityCount}: ${item.activity} (max ${effectiveActivityCap})`);
            return;
          }
        }

        allItems.push({
          ...item,
          activity: correctedActivity || item.activity,
          time: item.time,
          type: 'activity',
          originalIndex: index,
          sortTime: itemTime
        });
      });
    }

    if (dayData.meals) {
      timelineLog(`🍽️ Day ${dayIndex + 1}: Processing ${dayData.meals.length} meals (cutoff: ${cutoffStartTime}-${cutoffEndTime})`);
      dayData.meals.forEach((item, index) => {
        // Times are already normalized by itineraryNormalizer (Stage 1-2: field completeness + time normalization)
        let itemTime = parseTime(item.time);

        // SAFETY NET: If meal time falls 1:00-5:59 AM, fix it
        if (itemTime >= 60 && itemTime < 360) {
          const mealLower = (item.meal || item.type || '').toLowerCase();
          if (mealLower.includes('breakfast')) {
            // Breakfast at 1-5 AM is wrong — clamp to 7:30 AM
            itemTime = 7 * 60 + 30;
            item.time = '07:30';
            timelineLog(`  ⚠️ Breakfast time fix: "${item.restaurant || item.meal}" clamped to ${item.time}`);
          } else {
            itemTime += 12 * 60;
            item.time = `${String(Math.floor(itemTime / 60)).padStart(2, '0')}:${String(itemTime % 60).padStart(2, '0')}`;
            timelineLog(`  ⚠️ AM→PM safety fix (meal): "${item.restaurant || item.meal}" shifted to ${item.time}`);
          }
        }

        const effectiveTime = itemTime || 720; // Fallback to noon if still missing (shouldn't happen after normalization)

        // For inter-city travel days: allow meals either before checkout OR after check-in
        let isInAllowedWindow = false;
        if (isInterCityTravelDay && preCheckoutCutoffEnd !== null && postCheckinCutoffStart !== null) {
          const isPreCheckoutMeal = effectiveTime >= cutoffStartTime && effectiveTime < preCheckoutCutoffEnd;
          const isPostCheckinMeal = effectiveTime >= postCheckinCutoffStart && effectiveTime < cutoffEndTime;
          isInAllowedWindow = isPreCheckoutMeal || isPostCheckinMeal;
          timelineLog(`  Meal ${index}: "${item.meal}" at ${item.time} (${effectiveTime} mins), pre-checkout: ${isPreCheckoutMeal}, post-checkin: ${isPostCheckinMeal}`);
        } else {
          isInAllowedWindow = effectiveTime >= cutoffStartTime && effectiveTime < cutoffEndTime;

          // On last day, also check if meal would END after checkout (meals typically 1 hour)
          if (isLastDay && cutoffEndTime < 24 * 60) {
            const mealEndTime = effectiveTime + 60; // Meals are typically 1 hour
            if (mealEndTime > cutoffEndTime) {
              isInAllowedWindow = false;
              debugLog(`  Meal ${index}: "${item.meal}" at ${item.time} would end after checkout - filtering`);
            }
          }

          timelineLog(`  Meal ${index}: "${item.meal}" at ${item.time} (${effectiveTime} mins), cutoff check: ${isInAllowedWindow}`);
        }

        if (isInAllowedWindow) {
          // In-flight meals, airport meals, and cross-city meals are already filtered by normalizeDay (Stages 6-7)

          // On inter-city travel days, filter out post-checkin meals from wrong city
          // This depends on render-time inter-city transport state so must remain here
          if (isInterCityTravelDay && postCheckinCutoffStart !== null && itemTime >= postCheckinCutoffStart) {
            if (isWrongCityActivity({
              activity: item.restaurant,
              location: item.address || item.restaurant
            })) {
              debugLog(`🚫 Filtering wrong-city meal: ${item.restaurant}`);
              return;
            }
          }

          // Times are already normalized — use item.time directly
          // Preserve meal type: AI schema uses `type: "breakfast"` but timeline needs `type: "meal"`.
          // Store original meal type in MULTIPLE fields so hasMealType() can always detect it.
          const mealTypeLabel = item.meal || item.type || item.mealType || '';
          allItems.push({
            ...item,
            type: 'meal',
            meal: mealTypeLabel, // e.g., "Breakfast", "Lunch", "Dinner" — needed by hasMealType()
            mealType: (item.mealType || item.type || '').toLowerCase(), // lowercase: "breakfast", "lunch", "dinner"
            originalType: (item.type || '').toLowerCase(), // preserve LLM's original type before overwrite
            originalIndex: index,
            sortTime: effectiveTime
          });
        }
      });
    } else {
      debugLog(`⚠️ Day ${dayIndex + 1}: No meals array found in dayData`);
    }

    // BUG 5 FIX: If all activities were filtered on an inter-city travel day (late arrival),
    // add a fallback evening-at-hotel activity so the day isn't empty.
    // Skip on departure day (isLastDay) — empty activity list is expected, departure logistics handle it.
    if (allItems.length === 0 && isInterCityTravelDay && !isLastDay && !isOvernightDepartureDay && dayHotelName) {
      const fallbackTime = postCheckinCutoffStart ? Math.max(postCheckinCutoffStart, 1200) : 1200;
      const fallbackHours = Math.floor(fallbackTime / 60);
      const fallbackMins = fallbackTime % 60;
      allItems.push({
        activity: 'Settle In & Explore the Neighborhood',
        description: `Arrive at ${dayHotelName}, freshen up, and take a leisurely walk to explore the area`,
        location: dayHotelName,
        time: `${String(fallbackHours).padStart(2, '0')}:${String(fallbackMins).padStart(2, '0')}`,
        duration: '2 hours',
        type: 'activity',
        originalIndex: 0,
        sortTime: fallbackTime
      });
      debugLog(`⚠️ Day ${dayIndex + 1}: All activities filtered — added fallback evening activity`);
    }

    // timelineSortComparator and TIMELINE_TYPE_PRIORITY now imported from shared displayGuardrails

    // Sort items by time
    allItems.sort(timelineSortComparator);

    // Debug: Log collected items (verbose only — fires 20+ times per render)
    timelineLog(`📋 Day ${dayIndex + 1}: Collected ${allItems.length} items (activities + meals)`);
    allItems.forEach((item, i) => {
      const itemName = item.activity || item.restaurant || item.meal || 'Unknown';
      timelineLog(`  ${i}: ${item.type} - "${itemName}" at ${item.time} (${item.sortTime} mins)`);
    });

    // Add transit from hotel to first activity (for ALL days, not just first day)
    // NOTE: This is the transit FROM hotel TO first activity - needed even after check-in
    // The only exception is if first activity is at the hotel itself (handled below)
    // ALSO skip if inter-city travel already injected a transit-to-hotel card (avoids double transit)
    const hasExistingTransitToHotel = isInterCityTravelDay && timeline.some(
      item => (item.type === 'airport-transit' || item.type === 'station-transit') &&
              (item.activity?.includes('Transit to Hotel') || item.to?.toLowerCase().includes(dayHotelName.toLowerCase()))
    );
    if (allItems.length > 0 && !hasExistingTransitToHotel) {
      const firstItem = allItems[0];
      const firstLocation = firstItem.location || firstItem.restaurant || '';
      const firstActivity = (firstItem.activity || firstItem.meal || '').toLowerCase();
      const firstRestaurant = (firstItem.restaurant || '').toLowerCase();

      // For morning activities, check against PREVIOUS night's hotel (where user slept)
      // For other activities, use current day's hotel
      const firstItemHour = parseInt((firstItem.time || '9:00').split(':')[0]);
      const isMorningActivity = firstItemHour < 12;
      const hotelToCheck = (isMorningActivity && !isFirstDay) ? prevDayHotelName : dayHotelName;
      const hotelLower = hotelToCheck.toLowerCase();

      // Check if first activity is at the hotel (breakfast, hotel restaurant, etc)
      const isAtHotel = (location, activity, restaurant, address) => {
        return isHotelActivityValidator(location, activity, restaurant, hotelToCheck, dayData?.baseCity || '', address || '');
      };

      // Only add transit if first activity isn't at the hotel
      const firstAddress = firstItem.address || firstItem.location || '';
      if (firstLocation && firstLocation !== hotelToCheck && !isAtHotel(firstLocation, firstActivity, firstRestaurant, firstAddress)) {
        const transitKey = `day${dayIndex}-hotel-to-first`;
        const selectedMode = transportModes[transitKey] || 'driving';
        const cachedTransitData = transitTimes[transitKey] || {};

        timelineLog(`🚗 Transit to first activity: key=${transitKey}, hasCachedData=${Object.keys(cachedTransitData).length > 0}, modes=${Object.keys(cachedTransitData).join(',') || 'none'}`);
        if (Object.keys(cachedTransitData).length > 0) {
          timelineLog(`   Cached driving: ${cachedTransitData.driving?.duration || 'N/A'}`);
          timelineLog(`   Cached transit: ${cachedTransitData.transit?.duration || 'N/A'}`);
          timelineLog(`   Cached walking: ${cachedTransitData.walking?.duration || 'N/A'}`);
        }

        // Get actual transit duration from Google Maps API cache.
        // FALLBACK SCALING: when the cache misses, 30min is too generous
        // for in-city movement. The same city's hotel→activity is typically
        // 10-15min by car, 5-10min walking. The 30min default produced
        // "Drive ~30 mins" between Hotel da Baixa and Rua Augusta — those
        // are literally adjacent blocks in central Lisbon.
        //
        // Smart fallback:
        //   - driving: 15min (in-city, post-hotel-checkin baseline)
        //   - walking: 12min (~1km walk)
        //   - transit: 20min (bus/metro inside a city)
        // Truly long routes (airport→city, cross-city) get their own
        // higher defaults at their own call sites.
        const getTransitDurationMinutes = () => {
          const modeData = cachedTransitData[selectedMode] || cachedTransitData.driving || cachedTransitData.transit;
          // SYSTEMIC FIX: Only use API duration if it's a valid positive number
          if (modeData?.durationMinutes && modeData.durationMinutes > 0) {
            return modeData.durationMinutes;
          }
          if (modeData?.duration) {
            // Parse duration string like "25 mins" or "1 hr 10 mins"
            const duration = modeData.duration;
            // Skip "0 mins" which signals route not found
            if (duration === '0 mins' || duration === '0 min' || duration === 'N/A') {
              return selectedMode === 'walking' ? 12 : selectedMode === 'transit' ? 20 : 15;
            }
            if (duration.includes('hr') || duration.includes('hour')) {
              const match = duration.match(/(\d+)\s*hr?\s*(\d*)/);
              if (match) {
                const parsed = (parseInt(match[1]) || 0) * 60 + (parseInt(match[2]) || 0);
                if (parsed > 0) return parsed;
              }
            }
            const mins = parseInt(duration);
            if (!isNaN(mins) && mins > 0) return mins;
          }
          return selectedMode === 'walking' ? 12 : selectedMode === 'transit' ? 20 : 15;
        };

        const transitDurationMins = getTransitDurationMinutes();

        // For first day, use cutoffStartTime (after hotel check-in)
        // For other days, start transit before first activity based on actual duration
        let transitStartMinutes;
        if (isFirstDay) {
          transitStartMinutes = cutoffStartTime;
        } else {
          // Start transit before first activity, using actual duration + 5 min buffer
          transitStartMinutes = firstItem.sortTime - transitDurationMins - 5;
          if (transitStartMinutes < 0) transitStartMinutes = Math.max(0, firstItem.sortTime - 15);
        }

        const transitStartHours = Math.floor(transitStartMinutes / 60);
        const transitStartMins = transitStartMinutes % 60;

        // Use previous night's hotel for morning transit (that's where they slept)
        const transitFromHotel = (isMorningActivity && !isFirstDay) ? prevDayHotelName : dayHotelName;

        // Format duration for display
        const formatDuration = (mins) => {
          if (mins >= 60) {
            const hrs = Math.floor(mins / 60);
            const remainMins = mins % 60;
            return remainMins > 0 ? `${hrs}hr ${remainMins}min` : `${hrs}hr`;
          }
          return `${mins} mins`;
        };

        // SYSTEMIC FIX: Don't display "0 mins" - use formatted duration instead
        const cachedDuration = cachedTransitData[selectedMode]?.duration;
        const displayDuration = (cachedDuration && cachedDuration !== '0 mins' && cachedDuration !== '0 min' && cachedDuration !== 'N/A')
          ? cachedDuration
          : `~${formatDuration(transitDurationMins)}`;

        timeline.push({
          type: 'transit',
          time: `${String(transitStartHours).padStart(2, '0')}:${String(transitStartMins).padStart(2, '0')}`,
          activity: 'Transit',
          from: addLocationContext(transitFromHotel),
          to: addLocationContext(firstLocation),
          duration: displayDuration,
          selectedMode: selectedMode,
          transitKey: transitKey,
          sortTime: transitStartMinutes
        });
      }
    }

    // Add items with transit cards between them
    // IMPORTANT: Use a separate transit counter that matches the useEffect fetch indexing
    // The useEffect fetches transit-0 for first→second activity, transit-1 for second→third, etc.
    // We need to match this indexing exactly for the cached times to display correctly.
    let transitCardCounter = 0;
    debugLog(`🚏 Day ${dayIndex + 1}: Building timeline with ${allItems.length} items`);
    allItems.forEach((item, idx) => {
      // Add the activity/meal
      timeline.push(item);

      // Add transit card to next activity (always, for every item except the last)
      // Skip if current or next item is already a travel/transit type (prevents double transit cards)
      const travelTypes = ['inter-city-travel', 'transit', 'station-transit', 'airport-transit', 'port-transit', 'flight', 'hotel-checkout', 'hotel-checkin', 'overnight-journey', 'overnight-arrival', 'recovery-note', 'arrival-customs'];
      const isCurrentTravel = travelTypes.includes(item.type);
      const nextItem = allItems[idx + 1];
      const isNextTravel = nextItem && travelTypes.includes(nextItem.type);

      debugLog(`  📍 Item ${idx}: ${item.activity || item.restaurant || item.meal} (type=${item.type}), isCurrentTravel=${isCurrentTravel}, hasNext=${!!nextItem}, isNextTravel=${isNextTravel}`);

      if (idx < allItems.length - 1 && !isCurrentTravel && !isNextTravel) {
        const currentLocation = item.location || item.restaurant || item.activity || '';
        const nextLocation = nextItem.location || nextItem.restaurant || nextItem.activity || '';
        const currentActivity = (item.activity || item.meal || '').toLowerCase();
        const nextActivity = (nextItem.activity || nextItem.meal || '').toLowerCase();

        // Helper to check if an activity is at the hotel/resort
        const isHotelActivity = (location, activity, restaurant, address) => {
          return isHotelActivityValidator(location, activity, restaurant, dayHotelName, dayData?.baseCity || '', address || '');
        };

        // Skip transit if both activities are at the hotel or same location
        const currentAtHotel = isHotelActivity(currentLocation, currentActivity, item.restaurant, item.address);
        const nextAtHotel = isHotelActivity(nextLocation, nextActivity, nextItem.restaurant, nextItem.address);

        // Normalize location strings for comparison — use ADDRESS for precision, not location name
        // Previously matched by normalized name which caused false positives (e.g., "Shinjuku Gyoen" matched "Shinjuku" hotel)
        const currentAddr = (item.address || '').toLowerCase().trim();
        const nextAddr = (nextItem.address || '').toLowerCase().trim();
        const sameLocation = currentAddr && nextAddr && currentAddr.length > 10 && currentAddr === nextAddr;

        // Skip transit if both activities are at the same hotel/location
        // NOTE: We removed the "sameCity" check because it incorrectly skipped transit
        // between different locations within the same city. For example, "Gornergrat" and
        // "Restaurant Whymper-Stube" are both in Zermatt but require 30+ min travel.
        // Instead, we show transit cards for all location changes and let the real
        // distance data determine the actual travel time.

        // Skip transit when both activities are at the hotel/same location
        // NOTE: Previously had an aggressive "eitherAtHotel" check for resort trips that
        // skipped transit when EITHER activity was at the hotel. This caused false positives
        // when hotel names contain neighborhood words (e.g., "Hotel Fortaleza do Guincho"
        // matched "Praia do Guincho", "Guincho Beach", etc.). Removed in favor of the more
        // precise "both at hotel" check + near-zero transit filter for truly close locations.
        if ((currentAtHotel && nextAtHotel) || sameLocation) {
          debugLog(`  ⏭️ Skipping transit: both at hotel or same location`);
          return;
        }

        // ISSUE 6 FIX: Skip transit cards around yacht/boat/sailing activities
        // These activities ARE the transport themselves (e.g., "Private Yacht to Spetses")
        // Transit before = boarding the yacht (you're already at the port/marina)
        // Transit after = returning from the yacht (you're already back at port/marina or island)
        // NOTE: Must distinguish actual water TRANSPORT ("yacht to Spetses", "boat tour of islands")
        // from venues/landmarks ("dinner at The Yacht Club", "Ferry Building walk")
        const isWaterTransportActivity = (activity) => {
          if (!activity) return false;
          const lower = activity.toLowerCase();
          // Must have a water transport keyword...
          const hasWaterKeyword = /\byacht\b|\bboat\b|\bsailing\b|\bcatamaran\b|\bferry\b|\bspeedboat\b|\bwater\s*taxi\b/.test(lower);
          if (!hasWaterKeyword) return false;
          // ...AND a transport/activity context word (to/from/trip/tour/charter/private/ride/transfer/cruise/excursion)
          // This filters out "The Yacht Club" (restaurant), "Ferry Building" (landmark)
          const hasTransportContext = /\bto\b|\bfrom\b|\btrip\b|\btour\b|\bcharter\b|\bprivate\b|\bride\b|\btransfer\b|\bcruise\b|\bexcursion\b|\baround\b|\bacross\b|\bisland/i.test(lower);
          return hasTransportContext;
        };
        const currentIsWaterTransport = isWaterTransportActivity(currentActivity);
        const nextIsWaterTransport = isWaterTransportActivity(nextActivity);
        if (currentIsWaterTransport || nextIsWaterTransport) {
          debugLog(`  ⏭️ Skipping transit: water transport activity detected (current="${currentActivity}", next="${nextActivity}")`);
          transitCardCounter++;
          return;
        }

        // Skip transit cards around "Drive to X" / "Bus to X" day-trip activities
        // These activities ARE the transport themselves (e.g., "Drive to Sintra" is a 30-min drive).
        // Transit card before it would be redundant (same journey), transit after it is also redundant
        // since the drive activity ends at the destination.
        const isDriveToDayTrip = (activity) => {
          if (!activity) return false;
          const lower = activity.toLowerCase();
          return /^(🚗\s*)?drive\s+to\b/i.test(lower) || /^(🚌\s*)?bus\s+to\b/i.test(lower);
        };
        const currentIsDriveTo = isDriveToDayTrip(currentActivity);
        const nextIsDriveTo = isDriveToDayTrip(nextActivity);
        if (currentIsDriveTo || nextIsDriveTo) {
          debugLog(`  ⏭️ Skipping transit: drive/bus-to day trip activity detected (current="${currentActivity}", next="${nextActivity}")`);
          transitCardCounter++;
          return;
        }

        // BUG 6 FIX: For activities going TO a ferry/boat destination, resolve the transit
        // destination to the ferry terminal instead of the island. The transit card should show
        // "walk to Ferry Terminal" not "drive to Rangitoto Island"
        let resolvedNextLocation = nextLocation;
        let resolvedCurrentLocation = currentLocation;
        const isFerryActivity = (activity) => {
          if (!activity) return false;
          const lower = activity.toLowerCase();
          return /\bferry\b/i.test(lower) && /\bto\b|\bfrom\b|\bcrossing\b|\bride\b/i.test(lower);
        };
        if (isFerryActivity(nextActivity)) {
          // Next activity is a ferry — transit should go to the ferry terminal, not the island
          const ferryCity = dayData?.baseCity || '';
          resolvedNextLocation = ferryCity ? `${ferryCity} Ferry Terminal` : 'Ferry Terminal';
          debugLog(`  🚢 Bug 6: Resolved ferry transit destination: "${nextLocation}" → "${resolvedNextLocation}"`);
        }
        if (isFerryActivity(currentActivity)) {
          // Current activity was a ferry — transit FROM should be from the return terminal
          const ferryCity = dayData?.baseCity || '';
          resolvedCurrentLocation = ferryCity ? `${ferryCity} Ferry Terminal` : 'Ferry Terminal';
          debugLog(`  🚢 Bug 6: Resolved ferry return transit origin: "${currentLocation}" → "${resolvedCurrentLocation}"`);
        }

        // Calculate transit time and end time of current activity.
        // Guard against NaN: if duration is unparseable, fall back to 60min.
        // If sortTime is missing, fall back to previous card's sortTime or a
        // mid-day default so the transit pill never renders empty.
        const _rawCurrentDur = parseDurationToMinutes(item.duration);
        const currentDurationMins = Number.isFinite(_rawCurrentDur) ? _rawCurrentDur : 60;
        const _rawItemSortTime = Number.isFinite(item.sortTime) ? item.sortTime : null;
        const transitStartMinutes = _rawItemSortTime != null
          ? _rawItemSortTime + currentDurationMins
          : (Number.isFinite(timeline[timeline.length - 1]?.sortTime)
              ? timeline[timeline.length - 1].sortTime + (currentDurationMins || 30)
              : 12 * 60);

        // NOTE: Previously we skipped transit when gap >= 60 min (assuming free time would be inserted).
        // But this caused missing transit cards — users still need to travel between locations even with free time.
        // Transit cards are now ALWAYS inserted. Free time is added separately in a later pass.

        // Check if we have real distance data
        // Use transitCardCounter (not idx) to match useEffect fetch indexing
        const transitKey = `day${dayIndex}-transit-${transitCardCounter}`;
        // Default mode: 'driving' everywhere EXCEPT explicitly car-free islands
        // Uses dynamicGeographyCache (populated by Haiku AI) — noCarAccess=true for places like Santorini, Capri, etc.
        // Large driveable islands (Hawaii, Bali, Sicily) are NOT marked noCarAccess, so they default to driving
        const baseCityLower = dayData?.baseCity?.toLowerCase?.()?.trim?.() || '';
        const geoCache = dynamicGeographyCache[baseCityLower];
        // Only default to walking for explicitly car-free islands (from dynamic Haiku geography cache)
        // Large driveable islands (Hawaii, Bali, Sicily, etc.) will NOT have noCarAccess=true
        // so they correctly default to driving — no hardcoded list needed
        const isExplicitlyCarFree = geoCache?.noCarAccess === true;
        const defaultTransitMode = isExplicitlyCarFree ? 'walking' : 'driving';
        const selectedMode = transportModes[transitKey] || defaultTransitMode;
        const cachedTransitData = transitTimes[transitKey] || {};

        let transitDuration = selectedMode === 'transit' ? 20 : selectedMode === 'walking' ? 15 : 10; // Default
        let distanceInfo = '';

        // First check transitTimes from Google Maps API (most accurate)
        const modeData = cachedTransitData[selectedMode] || cachedTransitData.driving;

        // DEBUG: Log what's in the cached data (verbose only — very high frequency)
        timelineLog(`  🔍 Transit ${transitKey}: mode=${selectedMode}, modeData=`, modeData ? { duration: modeData.duration, durationMinutes: modeData.durationMinutes, distance: modeData.distance } : 'NONE');

        if (modeData?.durationMinutes && modeData.durationMinutes > 0) {
          // Only use API duration if it's a valid positive number
          transitDuration = modeData.durationMinutes;
          if (modeData.distance) distanceInfo = ` • ${modeData.distance}`;
          timelineLog(`  ✅ Using durationMinutes: ${transitDuration}`);
        } else if (modeData?.duration) {
          // Parse duration string like "25 mins" or "1 hr 10 mins"
          const duration = modeData.duration;

          // SYSTEMIC FIX: Skip "0 mins" which signals route not found - keep default instead
          if (duration === '0 mins' || duration === '0 min' || duration === 'N/A') {
            timelineLog(`  ⚠️ Transit ${transitKey}: API returned "${duration}", using default ${transitDuration} mins`);
          } else if (duration.includes('hr') || duration.includes('hour')) {
            const match = duration.match(/(\d+)\s*hr?\s*(\d*)/);
            if (match) {
              const parsedDuration = (parseInt(match[1]) || 0) * 60 + (parseInt(match[2]) || 0);
              if (parsedDuration > 0) transitDuration = parsedDuration;
            }
            if (modeData.distance) distanceInfo = ` • ${modeData.distance}`;
            timelineLog(`  ✅ Parsed hr duration: ${transitDuration}`);
          } else {
            const mins = parseInt(duration);
            if (!isNaN(mins) && mins > 0) transitDuration = mins;
            if (modeData.distance) distanceInfo = ` • ${modeData.distance}`;
            timelineLog(`  ✅ Parsed mins duration: ${transitDuration} from "${duration}"`);
          }
        }
        // Fallback to itinerary.transitData if no Google Maps data
        if (!modeData && itinerary.transitData) {
          // Get location keys - for meals use restaurant, for activities use location
          const fromKey = item.type === 'meal' ? item.restaurant : (item.location || item.restaurant);
          const toKey = nextItem.type === 'meal' ? nextItem.restaurant : (nextItem.location || nextItem.restaurant);

          const realData = findTransitData(itinerary.transitData, fromKey, toKey);
          if (realData) {
            transitDuration = selectedMode === 'transit'
              ? Math.ceil(realData.duration * 1.5) // Transit takes longer
              : selectedMode === 'walking'
              ? Math.ceil(realData.duration * 3) // Walking takes even longer
              : realData.duration;
            distanceInfo = ` • ${realData.distanceText}`;
          }
        }

        // Cap intra-day transit duration at 120 minutes (2 hours)
        // Anything longer is almost certainly a data error (e.g., 599 mins, 762 mins)
        const MAX_INTRA_DAY_TRANSIT = 120;
        if (transitDuration > MAX_INTRA_DAY_TRANSIT) {
          timelineLog(`  ⚠️ Capping transit duration from ${transitDuration}min to ${MAX_INTRA_DAY_TRANSIT}min: ${currentLocation} → ${nextLocation}`);
          transitDuration = MAX_INTRA_DAY_TRANSIT;
        }

        const transitStartHours = Math.floor(transitStartMinutes / 60);
        const transitStartMins = transitStartMinutes % 60;

        // Skip zero-distance or very short transits (same location or < 500m)
        const distanceMeters = (() => {
          const match = distanceInfo.match(/([\d.]+)\s*(km|m)\b/);
          if (!match) return Infinity; // unknown distance, keep it
          const val = parseFloat(match[1]);
          return match[2] === 'km' ? val * 1000 : val;
        })();
        if (distanceMeters < 500 && transitDuration <= 2) {
          timelineLog(`  ⏭️ Skipping near-zero transit: ${currentLocation} → ${nextLocation} (${distanceMeters}m, ${transitDuration}min)`);
          transitCardCounter++;
          return;
        }

        timelineLog(`  ✅ Adding transit card #${transitCardCounter}: ${resolvedCurrentLocation} → ${resolvedNextLocation} (key=${transitKey})`);
        timeline.push({
          type: 'transit',
          time: `${String(transitStartHours).padStart(2, '0')}:${String(transitStartMins).padStart(2, '0')}`,
          activity: 'Transit',
          from: addLocationContext(resolvedCurrentLocation),
          to: addLocationContext(resolvedNextLocation),
          duration: `~${transitDuration} mins${distanceInfo}`,
          selectedMode: selectedMode,
          transitKey: transitKey,
          sortTime: transitStartMinutes
        });

        // Increment counter for next transit card
        transitCardCounter++;
      }
    });

    // Add transit back to hotel at end of day (except last day and departure days with overnight flights)
    // On travel days ending with a flight, you're sleeping on the plane — no hotel return
    const hasDepartureFlightTonight = timeline.some(item =>
      (item.type === 'inter-city-travel' || item.type === 'overnight-journey') &&
      (item.isOvernightFlight || item.travelMode === 'flight')
    );
    if (!isLastDay && !hasDepartureFlightTonight && allItems.length > 0) {
      const lastItem = allItems[allItems.length - 1];
      const lastLocation = lastItem.location || lastItem.restaurant || '';
      const lastActivity = (lastItem.activity || lastItem.meal || '').toLowerCase();
      const hotelName = dayHotelName;
      const hotelLower = hotelName.toLowerCase();

      // Check if last activity is already at the hotel
      const lastLocLower = lastLocation.toLowerCase();
      const hotelKeyWords = hotelLower.split(/\s+/).filter(w =>
        w.length > 3 && !['hotel', 'suites', 'resort', 'inn', 'lodge', 'spa', 'the', 'and'].includes(w)
      );

      const lastActivityAtHotel = lastLocLower.includes(hotelLower) ||
        hotelKeyWords.some(w => lastLocLower.includes(w)) ||
        (lastItem.restaurant && hotelKeyWords.some(w => lastItem.restaurant.toLowerCase().includes(w)));

      // Skip return transit if already at hotel
      if (lastActivityAtHotel) {
        // Just add the "Arrive back at hotel" card with adjusted time
        const lastDurationMins = parseDurationToMinutes(lastItem.duration);
        const arrivalMinutes = lastItem.sortTime + lastDurationMins;
        const arrivalHours = Math.floor(arrivalMinutes / 60);
        const arrivalMins = arrivalMinutes % 60;

        timeline.push({
          type: 'hotel-return',
          time: `${String(arrivalHours).padStart(2, '0')}:${String(arrivalMins).padStart(2, '0')}`,
          activity: returnLabel,
          location: hotelName,
          description: 'Rest, freshen up, or explore the hotel amenities',
          duration: 'End of day',
          sortTime: arrivalMinutes
        });
      } else {
        // Calculate transit start time (defensive against NaN)
        const _rawLastDur = parseDurationToMinutes(lastItem.duration);
        const lastDurationMins = Number.isFinite(_rawLastDur) ? _rawLastDur : 60;
        const _rawLastSort = Number.isFinite(lastItem.sortTime) ? lastItem.sortTime : null;
        const transitStartMinutes = _rawLastSort != null
          ? _rawLastSort + lastDurationMins
          : (Number.isFinite(timeline[timeline.length - 1]?.sortTime)
              ? timeline[timeline.length - 1].sortTime + lastDurationMins
              : 20 * 60);

        const transitStartHours = Math.floor(transitStartMinutes / 60);
        const transitStartMins = transitStartMinutes % 60;

        const transitKey = `day${dayIndex}-last-to-hotel`;
        const selectedMode = transportModes[transitKey] || 'driving';
        const cachedReturnTransit = transitTimes[transitKey] || {};

        // Get actual transit duration from Google Maps API cache
        let transitDuration = selectedMode === 'transit' ? 25 : selectedMode === 'walking' ? 20 : 15; // Default
        const returnModeData = cachedReturnTransit[selectedMode] || cachedReturnTransit.driving;
        if (returnModeData?.durationMinutes) {
          transitDuration = returnModeData.durationMinutes;
        } else if (returnModeData?.duration) {
          const duration = returnModeData.duration;
          if (duration.includes('hr') || duration.includes('hour')) {
            const match = duration.match(/(\d+)\s*hr?\s*(\d*)/);
            if (match) transitDuration = (parseInt(match[1]) || 0) * 60 + (parseInt(match[2]) || 0);
          } else {
            const mins = parseInt(duration);
            if (!isNaN(mins)) transitDuration = mins;
          }
        }

        // Small-island clamp: on a small island (Ambergris Caye, Caye Caulker,
        // Gili Islands, etc.) return-to-hotel shouldn't take 27 minutes —
        // whole-island traversal is typically <10 min by golf cart or bike.
        // User saw a 27-min "Return to Hotel" on Day 6 in San Pedro in QA.
        // If the day's baseCity is an island and the engine has no real
        // Google Maps data for this transit key, clamp to 10 min.
        const dayBaseCity = dayData?.baseCity || '';
        const isOnSmallIsland = dayBaseCity && isIslandCity && isIslandCity(dayBaseCity);
        if (isOnSmallIsland && transitDuration > 10 && !returnModeData?.durationMinutes) {
          debugLog(`🏝️ Small-island clamp: return-transit ${transitDuration}m → 10m for ${dayBaseCity}`);
          transitDuration = 10;
        }

        // Add transit to hotel card
        timeline.push({
          type: 'return-transit',
          time: `${String(transitStartHours).padStart(2, '0')}:${String(transitStartMins).padStart(2, '0')}`,
          activity: 'Return to Hotel',
          from: addLocationContext(lastLocation || 'Last activity'),
          to: addLocationContext(hotelName),
          duration: returnModeData?.duration || `~${transitDuration} mins`,
          selectedMode: selectedMode,
          transitKey: transitKey,
          sortTime: transitStartMinutes
        });

        // Add "Arrive back at hotel" card
        const arrivalMinutes = transitStartMinutes + transitDuration;
        const arrivalHours = Math.floor(arrivalMinutes / 60);
        const arrivalMins = arrivalMinutes % 60;

        timeline.push({
          type: 'hotel-return',
          time: `${String(arrivalHours).padStart(2, '0')}:${String(arrivalMins).padStart(2, '0')}`,
          activity: returnLabel,
          location: hotelName,
          description: 'Rest, freshen up, or explore the hotel amenities',
          duration: 'End of day',
          sortTime: arrivalMinutes
        });
      }
    }

    // Add departure logistics for last day
    if (isLastDay) {
      const departTime = effectiveReturnMode === 'fly'
        ? (departureFlight.time || '18:00')
        : (driveDetails.returnDepartureTime || '10:00');
      const [hours, minutes] = departTime.split(':').map(Number);
      const departMinutes = hours * 60 + minutes;

      if (effectiveReturnMode === 'fly') {
        // FLYING: Airport departure logistics
        // Remove any AI-generated checkout/hotel-checkin AND airport departure items to prevent duplicates —
        // departure logistics will handle checkout timing and airport cards properly
        const checkoutPatterns = ['hotel checkout', 'check out', 'check-out', 'hotel check-in', 'hotel checkin', 'transit to hotel', 'return to hotel', 'arrive back at hotel'];
        // Also filter LLM-generated airport activities — the programmatic cards below handle these.
        // Added 'transfer to' to catch "Transfer to Lisbon Airport" / "Transfer to LIS" patterns
        // that slipped past the previous list (seen in Day 10 QA — 8:45pm "Transfer to Lisbon
        // Airport" rendered 10 hours AFTER a 10:15am flight departure).
        const airportPatterns = ['check-in at', 'check in at', 'airport check-in', 'airport checkin', 'departure from', 'depart from', 'head to airport', 'transit to airport', 'drive to airport', 'taxi to airport', 'transfer to airport', 'transfer to lisbon airport', 'transfer to '];
        const checkoutTypes = ['checkout', 'hotel-return', 'return-transit'];
        const beforeFilterCount = timeline.length;
        timeline = timeline.filter(item => {
          const act = (item.activity || '').toLowerCase();
          const desc = (item.description || '').toLowerCase();
          const isCheckoutOrCheckin = checkoutPatterns.some(p => act.includes(p)) || checkoutTypes.includes(item.type);
          // Also catch LLM-generated airport activities (e.g., "Check-In At JFK International Airport",
          // "Transfer to Lisbon Airport", etc.)
          const isAirportActivity = airportPatterns.some(p => act.includes(p)) && (act.includes('airport') || desc.includes('airport') || desc.includes('flight'));

          // Post-flight filter: on a flight-departure day the LLM sometimes
          // schedules lunch/activities AFTER the departure time (Day 10 QA
          // had a 10:15am flight and a 1:30pm lunch card + 8:45pm airport
          // transfer). Drop any non-flight, non-transit item whose time is
          // after the flight's scheduled departure — the user is airborne.
          const itemHHMM = (item.time || '').match(/^(\d{1,2}):(\d{2})/);
          const itemMinutes = itemHHMM ? (parseInt(itemHHMM[1], 10) * 60 + parseInt(itemHHMM[2], 10)) : -1;
          const isFlightItem = item.type === 'inter-city-travel' || item.type === 'flight' || item.travelMode === 'flight';
          const isAirportTransitType = item.type === 'airport-transit' || item.type === 'transit-departure';
          const isPostFlight = itemMinutes >= 0 && itemMinutes > departMinutes && !isFlightItem && !isAirportTransitType;

          if (isCheckoutOrCheckin || isAirportActivity || isPostFlight) {
            debugLog(`  🗑️ Removing item on departure day: "${item.activity}" (type: ${item.type}, reason: ${isPostFlight ? 'post-flight time' : isAirportActivity ? 'airport pattern' : 'checkout/checkin'})`);
          }
          return !isCheckoutOrCheckin && !isAirportActivity && !isPostFlight;
        });
        if (timeline.length < beforeFilterCount) {
          debugLog(`  ✅ Removed ${beforeFilterCount - timeline.length} AI-generated checkout/checkin items from departure day`);
        }

        // Get selected transport mode for airport transit
        const selectedMode = transportModes['last-day-airport'] || 'drive';

        // Check if we have a dynamically calculated departure airport distance
        const hasCalculatedDepartureDistance = airportDistances?.departure?.durationMinutes > 0;

        // SAFETY CHECK: Verify departure airport is NOT the home airport
        // This catches any case where the guardrails in generation/post-processing missed it
        // The departure airport should be near the DESTINATION (where you fly FROM), not home
        const rawHomeAirportsTl = profile?.home_airports;
        const homeAirportsListTl = Array.isArray(rawHomeAirportsTl) ? rawHomeAirportsTl
          : typeof rawHomeAirportsTl === 'string' ? (() => { try { return JSON.parse(rawHomeAirportsTl); } catch { return []; } })()
          : [];
        const homeCodesTl = new Set();
        if (homeAirportsListTl[0]?.code) homeCodesTl.add(homeAirportsListTl[0].code.toUpperCase());
        const homeCityCodeTl = lookupCityAirportCode(userProfile?.preferences?.homeCity);
        if (homeCityCodeTl) homeCodesTl.add(homeCityCodeTl.toUpperCase());

        let effectiveDepartureAirport = itinerary?.departureAirport;
        if (effectiveDepartureAirport?.code && homeCodesTl.has(effectiveDepartureAirport.code.toUpperCase())) {
          // Departure airport is the HOME airport — wrong! Fall back to arrival airport or transport info
          debugLog(`⚠️ Timeline safety: departureAirport (${effectiveDepartureAirport.code}) is home airport! Falling back...`);
          const fallbackAirport = itinerary?.arrivalAirport || null;
          if (fallbackAirport?.code && fallbackAirport.code !== '???' && !homeCodesTl.has(fallbackAirport.code.toUpperCase())) {
            effectiveDepartureAirport = { ...fallbackAirport, city: dayData?.baseCity || fallbackAirport.city };
            debugLog(`  → Using arrival airport as fallback: ${effectiveDepartureAirport.code}`);
          } else {
            // Try transport info for the current city
            const currentCity = dayData?.baseCity || selectedDestination?.name || '';
            const transportInfo = itinerary?.transportInfo?.[currentCity];
            if (transportInfo?.nearestAirport?.code) {
              effectiveDepartureAirport = {
                code: transportInfo.nearestAirport.code,
                name: transportInfo.nearestAirport.name || `${transportInfo.nearestAirport.code} Airport`,
                city: transportInfo.nearestAirport.city || currentCity
              };
              debugLog(`  → Using transport info airport: ${effectiveDepartureAirport.code}`);
            }
          }
        }

        // Check if departure airport is in a different city from the hotel/destination
        // DEPARTURE_AIRPORT_CITY_MAP imported from @wander/shared via ./utils/transportConstants
        const departureCode = effectiveDepartureAirport?.code || '';
        const resolvedDepartureCity = DEPARTURE_AIRPORT_CITY_MAP[departureCode] || safeAirportCity(effectiveDepartureAirport) || '';
        const departureAirportCity = resolvedDepartureCity.toLowerCase();
        const currentHotelCity = (dayData?.baseCity || selectedDestination?.name || '').toLowerCase();
        const isLongDistanceDeparture = hasCalculatedDepartureDistance
          ? airportDistances.departure.isFar
          : (departureAirportCity && currentHotelCity &&
              !departureAirportCity.includes(currentHotelCity) && !currentHotelCity.includes(departureAirportCity.split(',')[0]));

        // Estimate transit duration based on destination type
        // National parks often require long drives to airports
        const destName = (selectedDestination?.name || '').toLowerCase();
        const hotelAddress = (dayHotelName || '').toLowerCase();
        const isNationalParkTrip = destName.includes('national park') || destName.includes('mighty five') ||
                                    destName.includes('utah') || destName.includes('zion') ||
                                    hotelAddress.includes('springdale') || hotelAddress.includes('moab') ||
                                    hotelAddress.includes('bryce') || hotelAddress.includes('kanab');

        // Calculate transit duration - PRIORITY: Use dynamically calculated distance from Google Maps
        let transitDuration;
        let transitDescription = 'Arrive early for check-in and security';

        // Priority 1: Use real-time hotel-to-airport transit data (most accurate — uses actual hotel address)
        const realTimeAirportTransit = transitTimesRef.current['last-day-airport-transit'];
        const hasRealTimeAirportTransit = realTimeAirportTransit?.driving?.durationMinutes > 0;

        if (hasRealTimeAirportTransit) {
          transitDuration = realTimeAirportTransit.driving.durationMinutes;
          if (transitDuration > 90) {
            transitDescription = `Long drive to ${effectiveDepartureAirport?.code || 'airport'} (${realTimeAirportTransit.driving.duration}) - leave early!`;
          }
          debugLog(`📍 Using real-time hotel-to-airport transit: ${realTimeAirportTransit.driving.duration} (${transitDuration} mins)`);
        } else if (hasCalculatedDepartureDistance) {
          // Priority 2: Fallback to calculated airport distance
          transitDuration = airportDistances.departure.durationMinutes;
          if (airportDistances.departure.isFar) {
            transitDescription = `Long drive to ${effectiveDepartureAirport?.code || 'airport'} (${airportDistances.departure.duration}) - leave early!`;
          }
          debugLog(`📍 Using calculated departure distance (fallback): ${airportDistances.departure.duration} (${transitDuration} mins)`);
        } else if (isLongDistanceDeparture) {
          // Fallback: Long distance drive to departure airport (e.g., Key West to MCO)
          let estimatedDriveHours = 4; // Default estimate
          if (destName.includes('key west') || destName.includes('keys')) {
            // Key West to MCO is about 5-6 hours
            if (departureAirportCity.includes('orlando') || effectiveDepartureAirport?.code === 'MCO') {
              estimatedDriveHours = 5.5;
            } else if (departureAirportCity.includes('miami') || effectiveDepartureAirport?.code === 'MIA') {
              estimatedDriveHours = 3.5;
            } else if (departureAirportCity.includes('fort lauderdale') || effectiveDepartureAirport?.code === 'FLL') {
              estimatedDriveHours = 4;
            }
          }
          transitDuration = estimatedDriveHours * 60;
          transitDescription = `Long drive to ${effectiveDepartureAirport?.code || 'airport'} - leave early!`;
        } else if (isNationalParkTrip) {
          transitDuration = selectedMode === 'transit' ? 300 : 240; // 4-5 hours for national parks
          transitDescription = 'Long drive to airport - leave early!';
        } else {
          transitDuration = selectedMode === 'transit' ? 60 : 45; // 45-60 min for cities
        }

        // Work backwards from flight time:
        // Flight time - airport buffer - transit time - 30 min checkout
        const isDomesticDeparture = checkIfDomestic(selectedDestination);
        const airportBuffer = isDomesticDeparture ? 120 : 180; // 2hr domestic / 3hr international
        const totalLeadTime = airportBuffer + transitDuration + 30; // airport buffer + transit + checkout
        let checkoutMinutes = departMinutes - totalLeadTime;

        // Handle edge case: checkout time would be negative (previous day)
        // This happens with very early morning flights (e.g., 2:30 AM) + long transit
        // In this case, the checkout is effectively "late night" the previous day
        const isOvernightCheckout = checkoutMinutes < 0;
        if (isOvernightCheckout) {
          // Convert negative to late-night time (e.g., -60 becomes 23:00 previous day)
          // But for display purposes, we'll show it as a very early time and add context
          checkoutMinutes = Math.max(0, checkoutMinutes); // Clamp to midnight minimum
          debugWarn(`⚠️ Very early flight (${departTime}) requires late-night/early morning checkout`);
        }

        const checkoutHours = Math.floor(checkoutMinutes / 60);
        const checkoutMins = checkoutMinutes % 60;

        // Flag very early checkouts (before 5 AM) for display purposes
        const isVeryEarlyCheckout = checkoutMinutes < 300 && checkoutMinutes >= 0;

        // Flag late checkouts (after 2 PM / 14:00 = 840 minutes)
        // Standard checkout is usually 11am-12pm, so anything after 2pm needs late checkout
        const isLateCheckout = checkoutMinutes > 840;

        const checkoutDescription = isOvernightCheckout
          ? 'Very early checkout for red-eye flight - consider staying near airport'
          : isVeryEarlyCheckout
          ? 'Early checkout required for your flight'
          : isLateCheckout
          ? 'Late checkout needed - request in advance or use luggage storage'
          : 'Check out and gather luggage';

        // Inject breakfast if none exists on departure day (AI sometimes omits it)
        const hasBreakfastFly = timeline.some(item =>
          (item.type === 'meal' && (item.meal || item.activity || '').toLowerCase().includes('breakfast')) ||
          (item.activity || '').toLowerCase().includes('breakfast')
        );
        if (!hasBreakfastFly && !isVeryEarlyCheckout && !isOvernightCheckout && checkoutMinutes > 90) {
          const breakfastMinutes = checkoutMinutes - 90;
          const breakfastHours = Math.floor(breakfastMinutes / 60);
          const breakfastMins = breakfastMinutes % 60;
          timeline.push({
            type: 'meal', mealType: 'breakfast', originalType: 'breakfast',
            meal: 'Breakfast',
            time: `${String(breakfastHours).padStart(2, '0')}:${String(breakfastMins).padStart(2, '0')}`,
            activity: 'Breakfast',
            restaurant: dayHotelName ? `${dayHotelName} Restaurant` : 'Hotel Restaurant',
            description: 'Enjoy breakfast before checking out',
            duration: '1 hour',
            location: dayHotelName || 'Hotel',
            sortTime: breakfastMinutes,
            isDisplayInjected: true,
          });
          debugLog(`🍳 Injected breakfast at ${breakfastHours}:${String(breakfastMins).padStart(2, '0')} on departure day (fly)`);
        }

        // T7: subtitle = "{hotel}, {city we're actually leaving}"
        // dayData.baseCity is today's base city; on departure days this is
        // the city whose hotel we slept in last night.
        const departHotelForSubtitle = dayHotelName || 'Hotel';
        const departCityForSubtitle = sanitizeBaseCity(dayData?.baseCity || selectedDestination?.name || '', selectedDestination?.name || '');
        const departCheckoutLocation = departCityForSubtitle
          ? `${departHotelForSubtitle}, ${departCityForSubtitle}`
          : departHotelForSubtitle;
        timeline.push({
          type: 'checkout',
          time: `${String(checkoutHours).padStart(2, '0')}:${String(checkoutMins).padStart(2, '0')}`,
          activity: 'Hotel Checkout',
          description: checkoutDescription,
          location: departCheckoutLocation,
          hotelName: departHotelForSubtitle,
          checkoutCity: departCityForSubtitle,
          duration: '30 minutes',
          sortTime: checkoutMinutes,
          isVeryEarly: isVeryEarlyCheckout || isOvernightCheckout
        });

        // Transit to Airport (starts after checkout)
        const transitStartMinutes = checkoutMinutes + 30;
        const transitStartHours = Math.floor(transitStartMinutes / 60);
        const transitStartMins = transitStartMinutes % 60;

        const airportTransitKey = 'last-day-airport-transit';
        const airportTransitMode = transportModes[airportTransitKey] || 'driving';

        // Format transit duration nicely
        const formatTransitDuration = (mins) => {
          if (mins >= 60) {
            const h = Math.floor(mins / 60);
            const m = mins % 60;
            return m > 0 ? `~${h} hr ${m} mins` : `~${h} hr`;
          }
          return `~${mins} mins`;
        };

        // Use appropriate activity name and description based on distance
        const airportCode = effectiveDepartureAirport?.code || 'Airport';

        // For long-distance departures, determine best transport mode
        // European mountain destinations often have good train connections to airports
        let longDistanceMode = 'car';
        let longDistanceModeEmoji = '🚗';
        const hotelCityLower = currentHotelCity.toLowerCase();
        const airportCityLower = departureAirportCity.toLowerCase();

        // BUG B FIX: Detect island locations — you can't drive from islands to mainland airports!
        // Faroe Islands → Copenhagen, Hawaii → mainland US, Bali → Jakarta, etc.
        // Uses shared ISLAND_LOCATION_KEYWORDS constant and isIslandCity() helper
        const isIslandLocation = isIslandCity(currentHotelCity);
        const isIslandBaseCity = isIslandCity(dayData?.baseCity);

        if (isIslandLocation || isIslandBaseCity) {
          // Can't drive from an island — must fly to the departure airport
          longDistanceMode = 'flight';
          longDistanceModeEmoji = '✈️';
          // Override transit description and duration for island flights
          // Typical island-to-mainland connecting flight: 1-3 hours
          transitDuration = 150; // ~2.5h estimated connecting flight
          transitDescription = `Flight to ${effectiveDepartureAirport?.code || 'airport'} - book connecting flight in advance`;
          debugLog(`🏝️ Island departure detected: ${currentHotelCity} is an island, must fly to ${airportCode}`);
        }

        // TRAIN_FRIENDLY_AIRPORT_ROUTES imported from @wander/shared via ./utils/transportConstants

        // Only check train routes if not already determined to be a flight (island)
        if (longDistanceMode !== 'flight') {
          // Priority 1: Use Haiku-determined mode from pre-fetch (most accurate — knows real rail service)
          const depPairKey = `${hotelCityLower}|${airportCityLower}`;
          const dynamicDepTransport = dynamicTransportModes[depPairKey];

          if (dynamicDepTransport?.mode) {
            longDistanceMode = dynamicDepTransport.mode; // 'train', 'car', 'flight', 'ferry'
            if (dynamicDepTransport.mode === 'train') longDistanceModeEmoji = '🚆';
            else if (dynamicDepTransport.mode === 'ferry') longDistanceModeEmoji = '⛴️';
            else if (dynamicDepTransport.mode === 'flight') longDistanceModeEmoji = '✈️';
            // Use real duration from Haiku if available
            if (dynamicDepTransport.durationMinutes) {
              transitDuration = dynamicDepTransport.durationMinutes;
              transitDescription = `${dynamicDepTransport.mode === 'train' ? 'Train' : dynamicDepTransport.mode === 'ferry' ? 'Ferry' : 'Transit'} to ${airportCode} (${dynamicDepTransport.duration})`;
            }
            debugLog(`🚀 Haiku departure transport: ${depPairKey} = ${dynamicDepTransport.mode} (${dynamicDepTransport.duration})`);
          } else {
            // Priority 2: Fallback to shared TRAIN_FRIENDLY_AIRPORT_ROUTES
            const isTrainFriendlyRoute = TRAIN_FRIENDLY_AIRPORT_ROUTES.some(([from, to]) =>
              (hotelCityLower.includes(from) && airportCityLower.includes(to)) ||
              (hotelCityLower.includes(to) && airportCityLower.includes(from))
            );

            if (isTrainFriendlyRoute) {
              longDistanceMode = 'train';
              longDistanceModeEmoji = '🚆';
            }
          }
        }

        const longDistanceModeLabel = longDistanceMode === 'train' ? 'Train'
          : longDistanceMode === 'flight' ? 'Flight' : 'Drive';
        const transitActivityName = isLongDistanceDeparture
          ? `${longDistanceModeEmoji} ${longDistanceModeLabel} to ${airportCode}`
          : 'Transit to Airport';

        // BUG FIX: Recalculate cutoffEndTime after all transit duration refinements
        // (island detection, long-distance routes, national parks, real-time transit data)
        // The initial cutoff at line ~11183 uses a default 45-min transit, but the actual
        // transit duration may be much longer (e.g., 150 min for island connecting flights)
        const refinedTotalLeadTime = airportBuffer + transitDuration + 30;
        const refinedCutoff = Math.max(0, departMinutes - refinedTotalLeadTime);
        if (refinedCutoff !== cutoffEndTime) {
          debugLog(`🔄 Recalculating cutoffEndTime: ${cutoffEndTime} → ${refinedCutoff} (transit refined to ${transitDuration} mins)`);
          cutoffEndTime = refinedCutoff;
        }

        // Use departure hotel (may be previous day's hotel if user travels to a different city to fly out)
        const transitFromHotel = departureHotelName || dayHotelName;
        const transitFromCity = (!dayData?.hotel?.name && prevDayData?.baseCity)
          ? prevDayData.baseCity.toLowerCase()
          : currentHotelCity;

        timeline.push({
          type: isLongDistanceDeparture ? 'long-distance-departure' : 'airport-transit',
          time: `${String(transitStartHours).padStart(2, '0')}:${String(transitStartMins).padStart(2, '0')}`,
          activity: transitActivityName,
          from: `${transitFromHotel}, ${transitFromCity}`,
          to: addLocationContext(`${airportCode} Airport, ${departureAirportCity}`),
          description: transitDescription,
          duration: formatTransitDuration(transitDuration),
          selectedMode: airportTransitMode,
          transitKey: airportTransitKey,
          sortTime: transitStartMinutes,
          isLongDistance: isLongDistanceDeparture,
          travelMode: isLongDistanceDeparture ? longDistanceMode : null,
          fromCity: transitFromCity,
          toCity: departureAirportCity
        });

        // Airport Arrival (for context)
        const arrivalAtAirportMinutes = transitStartMinutes + transitDuration;
        const arrivalAtAirportHours = Math.floor(arrivalAtAirportMinutes / 60);
        const arrivalAtAirportMins = arrivalAtAirportMinutes % 60;
        const timeAtAirport = departMinutes - arrivalAtAirportMinutes;

        const depAirportCode = effectiveDepartureAirport?.code || '';
        const depAirportName = effectiveDepartureAirport?.name || effectiveDepartureAirport?.city || '';
        const depAirportDisplay = depAirportName || depAirportCode;
        timeline.push({
          type: 'airport-arrival',
          time: `${String(arrivalAtAirportHours).padStart(2, '0')}:${String(arrivalAtAirportMins).padStart(2, '0')}`,
          activity: depAirportDisplay ? `✈️ Check-in at ${depAirportDisplay}` : '✈️ Airport Check-in',
          description: depAirportCode ? `Security, check-in, boarding at ${depAirportCode}` : 'Security, check-in, boarding',
          duration: `${Math.floor(timeAtAirport / 60)}h ${timeAtAirport % 60}m buffer`,
          location: depAirportName ? `${depAirportName}` : (depAirportCode ? `${depAirportCode} Airport` : undefined),
          sortTime: arrivalAtAirportMinutes,
          airportCode: depAirportCode || null
        });

        // Departure Flight — full card with pricing (mirrors inter-city-travel card)
        // Use effectiveDepartureAirport (already home-airport-checked) with fallback chain
        const departureAirportCode = (
          effectiveDepartureAirport?.code ||
          itinerary?.arrivalAirport?.code ||
          ''
        );
        const departureAirportName = effectiveDepartureAirport?.name || effectiveDepartureAirport?.city || '';
        const departureAirportDisplay = departureAirportName || departureAirportCode;
        // Get home airport for pricing route (departure airport → home airport)
        // Priority: trip-specific departure city → permanent profile home airport
        const homeAirportsForDep = (() => { const raw = profile?.home_airports; if (Array.isArray(raw)) return raw; if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return []; } } return []; })();
        const homeAirportCodeForDep = lookupCityAirportCode(userProfile?.preferences?.homeCity) || homeAirportsForDep?.[0]?.code || '';
        timeline.push({
          type: 'departure',
          time: departTime,
          activity: departureAirportDisplay ? `✈️ Departure from ${departureAirportDisplay}` : '✈️ Departure Flight',
          description: homeAirportCodeForDep && departureAirportCode
            ? `Flight from ${departureAirportCode} to ${homeAirportCodeForDep}. Safe travels! 🌍`
            : 'Safe travels! 🌍',
          location: departureAirportCode && homeAirportCodeForDep
            ? `${departureAirportCode} → ${homeAirportCodeForDep}`
            : (departureAirportName ? `${departureAirportName}` : (departureAirportCode ? `${departureAirportCode} Airport` : 'Airport')),
          sortTime: departMinutes,
          airportCode: departureAirportCode || null,
          homeAirportCode: homeAirportCodeForDep || null,
          isDepartureFlightCard: true
        });
      } else if (effectiveReturnMode === 'train' || effectiveReturnMode === 'ferry' || effectiveReturnMode === 'custom') {
        // TRAIN/FERRY/CUSTOM: Non-fly, non-drive departure
        const homeCity = userProfile.preferences.homeCity || 'Home';
        const emoji = effectiveReturnMode === 'train' ? '🚆' : effectiveReturnMode === 'ferry' ? '⛴️' : '🚌';
        const modeLabel = effectiveReturnMode === 'train' ? 'Train' : effectiveReturnMode === 'ferry' ? 'Ferry' : 'Departure';
        const transitDuration = quickTransportResult?.duration || '';

        // Remove AI-generated checkout duplicates
        const checkoutPatterns = ['hotel checkout', 'check out', 'check-out'];
        timeline = timeline.filter(item => {
          const act = (item.activity || '').toLowerCase();
          return !checkoutPatterns.some(p => act.includes(p));
        });

        // Checkout 30 min before departure
        const checkoutMinutes = departMinutes - 30;
        const checkoutHours = Math.floor(Math.max(0, checkoutMinutes) / 60);
        const checkoutMins = Math.max(0, checkoutMinutes) % 60;

        // Inject breakfast if none exists
        const hasBreakfast = timeline.some(item =>
          (item.type === 'meal' && (item.meal || item.activity || '').toLowerCase().includes('breakfast')) ||
          (item.activity || '').toLowerCase().includes('breakfast')
        );
        if (!hasBreakfast && checkoutMinutes > 90) {
          const breakfastMinutes = checkoutMinutes - 90;
          timeline.push({
            type: 'meal', mealType: 'breakfast', originalType: 'breakfast',
            meal: 'Breakfast',
            time: `${String(Math.floor(breakfastMinutes / 60)).padStart(2, '0')}:${String(breakfastMinutes % 60).padStart(2, '0')}`,
            activity: 'Breakfast',
            restaurant: dayHotelName ? `${dayHotelName} Restaurant` : 'Hotel Restaurant',
            description: 'Enjoy breakfast before checking out',
            duration: '1 hour',
            location: dayHotelName || 'Hotel',
            sortTime: breakfastMinutes,
            isDisplayInjected: true,
          });
        }

        // T7: subtitle reflects the city we're leaving today
        const trainHotelForSubtitle = dayHotelName || 'Hotel';
        const trainCityForSubtitle = sanitizeBaseCity(dayData?.baseCity || selectedDestination?.name || '', selectedDestination?.name || '');
        const trainCheckoutLocation = trainCityForSubtitle
          ? `${trainHotelForSubtitle}, ${trainCityForSubtitle}`
          : trainHotelForSubtitle;
        timeline.push({
          type: 'checkout',
          time: `${String(checkoutHours).padStart(2, '0')}:${String(checkoutMins).padStart(2, '0')}`,
          activity: 'Hotel Checkout',
          description: effectiveReturnMode === 'custom' ? 'Check out and prepare for departure' : `Check out and head to the ${effectiveReturnMode === 'train' ? 'station' : 'port'}`,
          location: trainCheckoutLocation,
          hotelName: trainHotelForSubtitle,
          checkoutCity: trainCityForSubtitle,
          duration: '30 minutes',
          sortTime: Math.max(0, checkoutMinutes)
        });

        // Departure
        const departHours = Math.floor(departMinutes / 60);
        const departMins = departMinutes % 60;

        timeline.push({
          type: 'transit-departure',
          time: `${String(departHours).padStart(2, '0')}:${String(departMins).padStart(2, '0')}`,
          activity: `${emoji} ${modeLabel} Back to ${homeCity}`,
          duration: transitDuration,
          description: effectiveReturnMode === 'custom'
            ? `${customTransportDesc || 'Departure'} — safe travels! 🏠`
            : `${modeLabel} from ${selectedDestination?.name || 'Destination'} to ${homeCity}${transitDuration ? ` (${transitDuration})` : ''}. Safe travels! 🏠`,
          location: `${selectedDestination?.name || 'Destination'} → ${homeCity}`,
          sortTime: departMinutes
        });

      } else {
        // DRIVING: Road trip home logistics
        // Remove any AI-generated checkout items to prevent duplicates — we add our own below
        const driveCheckoutPatterns = ['hotel checkout', 'check out', 'check-out'];
        const beforeDriveFilterCount = timeline.length;
        timeline = timeline.filter(item => {
          const act = (item.activity || '').toLowerCase();
          const isCheckout = driveCheckoutPatterns.some(p => act.includes(p));
          if (isCheckout) {
            debugLog(`  🗑️ Removing duplicate AI-generated checkout on drive departure day: "${item.activity}"`);
          }
          return !isCheckout;
        });
        if (timeline.length < beforeDriveFilterCount) {
          debugLog(`  ✅ Removed ${beforeDriveFilterCount - timeline.length} AI-generated checkout items from drive departure day`);
        }

        // Priority: 1) Google Maps calculated time, 2) destination driveHours, 3) estimate from flight hours
        const driveHours = calculatedDriveTime?.hours || selectedDestination?.driveHours || estimateDriveHours(selectedDestination?.flightHours || 3);
        const homeCity = userProfile.preferences.homeCity || 'Home';
        const isRental = effectiveReturnMode === 'drive-rental';

        // Work backwards from desired arrival time:
        // departTime is when they want to leave the destination
        // Add drive time to get home arrival
        const homeArrivalMinutes = departMinutes + (driveHours * 60);
        const homeArrivalHours = Math.floor((homeArrivalMinutes % 1440) / 60);
        const homeArrivalMins = (homeArrivalMinutes % 1440) % 60;

        // Checkout 30 min before departure
        const checkoutMinutes = departMinutes - 30;
        const checkoutHours = Math.floor(checkoutMinutes / 60);
        const checkoutMins = checkoutMinutes % 60;

        // Inject breakfast if none exists on departure day (AI often skips it for drive-home)
        const hasBreakfast = timeline.some(item =>
          (item.type === 'meal' && (item.meal || item.activity || '').toLowerCase().includes('breakfast')) ||
          (item.activity || '').toLowerCase().includes('breakfast')
        );
        if (!hasBreakfast && checkoutMinutes > 90) {
          // Place breakfast 1.5 hours before checkout
          const breakfastMinutes = checkoutMinutes - 90;
          const breakfastHours = Math.floor(breakfastMinutes / 60);
          const breakfastMins = breakfastMinutes % 60;
          timeline.push({
            type: 'meal', mealType: 'breakfast', originalType: 'breakfast',
            meal: 'Breakfast',
            time: `${String(breakfastHours).padStart(2, '0')}:${String(breakfastMins).padStart(2, '0')}`,
            activity: 'Breakfast',
            restaurant: dayHotelName ? `${dayHotelName} Restaurant` : 'Hotel Restaurant',
            description: 'Enjoy breakfast before checking out',
            duration: '1 hour',
            location: dayHotelName || 'Hotel',
            sortTime: breakfastMinutes,
            isDisplayInjected: true,
          });
          debugLog(`🍳 Injected breakfast at ${breakfastHours}:${String(breakfastMins).padStart(2, '0')} on departure day (drive home)`);
        }

        // T7: subtitle reflects today's hotel/city, not the trip's stops
        const driveHotelForSubtitle = dayHotelName || 'Hotel';
        const driveCityForSubtitle = sanitizeBaseCity(dayData?.baseCity || selectedDestination?.name || '', selectedDestination?.name || '');
        const driveCheckoutLocation = driveCityForSubtitle
          ? `${driveHotelForSubtitle}, ${driveCityForSubtitle}`
          : driveHotelForSubtitle;
        timeline.push({
          type: 'checkout',
          time: `${String(checkoutHours).padStart(2, '0')}:${String(checkoutMins).padStart(2, '0')}`,
          activity: 'Hotel Checkout',
          description: 'Check out and pack the car',
          location: driveCheckoutLocation,
          hotelName: driveHotelForSubtitle,
          checkoutCity: driveCityForSubtitle,
          duration: '30 minutes',
          sortTime: checkoutMinutes
        });

        // Road trip home
        const departHours = Math.floor(departMinutes / 60);
        const departMins = departMinutes % 60;

        timeline.push({
          type: 'road-trip-return',
          time: `${String(departHours).padStart(2, '0')}:${String(departMins).padStart(2, '0')}`,
          arrivalTime: `${String(homeArrivalHours).padStart(2, '0')}:${String(homeArrivalMins).padStart(2, '0')}`,
          activity: `🚗 Road Trip Back to ${homeCity}`,
          duration: `${driveHours} hours`,
          description: `Drive home${isRental ? ' and return rental car' : ''}. Safe travels! 🏠`,
          location: `${selectedDestination?.name || 'Destination'} → ${homeCity}`,
          sortTime: departMinutes
        });

        // Rental car return if applicable
        if (isRental) {
          const returnMinutes = homeArrivalMinutes + 15; // 15 min after arriving home
          const returnHours = Math.floor((returnMinutes % 1440) / 60);
          const returnMins = (returnMinutes % 1440) % 60;

          timeline.push({
            type: 'rental-return',
            time: `${String(returnHours).padStart(2, '0')}:${String(returnMins).padStart(2, '0')}`,
            activity: '🚗 Return Rental Car',
            duration: '30 minutes',
            description: `Drop off rental car near ${homeCity}`,
            location: `Rental Location near ${homeCity}`,
            sortTime: returnMinutes
          });
        }
      }
    }

    // Sort by time first (with type tiebreaker)
    timeline.sort(timelineSortComparator);

    // FIX: On last day, resolve overlaps between meals and departure transit
    // If a meal and departure transit/long-distance-departure share the same time slot,
    // move the meal earlier so it finishes before transit starts
    if (isLastDay) {
      const departureTransitItem = timeline.find(t => t.type === 'airport-transit' || t.type === 'long-distance-departure');
      if (departureTransitItem) {
        const transitStartTime = departureTransitItem.sortTime;
        timeline.forEach(item => {
          if (item.type === 'meal' && item.sortTime >= 0) {
            const mealDuration = 60; // Meals are ~1 hour
            const mealEndTime = item.sortTime + mealDuration;
            if (mealEndTime > transitStartTime && item.sortTime < transitStartTime + 60) {
              // Meal overlaps with or runs into departure transit — push meal earlier
              const newMealStart = transitStartTime - mealDuration - 15; // 15 min buffer before transit
              if (newMealStart >= 600) { // Don't push before 10 AM
                debugLog(`🔧 Rescheduling last-day meal to avoid departure overlap: "${item.activity}" ${item.sortTime} mins → ${newMealStart} mins`);
                item.sortTime = newMealStart;
                const newHours = Math.floor(newMealStart / 60);
                const newMins = newMealStart % 60;
                item.time = `${String(newHours).padStart(2, '0')}:${String(newMins).padStart(2, '0')}`;
              } else {
                // Can't fit meal before transit — filter it out
                debugLog(`🚫 Removing last-day meal that overlaps with departure transit: "${item.activity}"`);
                item._remove = true;
              }
            }
          }
        });
        // Remove any meals marked for removal
        const removeCount = timeline.filter(t => t._remove).length;
        if (removeCount > 0) {
          const filtered = timeline.filter(t => !t._remove);
          timeline.length = 0;
          timeline.push(...filtered);
        }
        // Re-sort after adjustments (with type tiebreaker)
        timeline.sort(timelineSortComparator);
      }
    }

    // Detect gaps and insert free time cards
    // Skip free time detection entirely on departure day — any gaps are for
    // packing, checkout prep, or transit buffer, not real free time
    const freeTimeCards = [];
    const MIN_FREE_TIME_GAP = 60; // Minimum 60 minutes to show free time card
    const skipFreeTimeDetection = isLastDay && effectiveReturnMode === 'fly';

    // SYSTEMIC FIX: Track used suggestion labels to avoid showing duplicates across free time slots
    const usedSuggestionLabels = new Set();

    if (skipFreeTimeDetection) {
      debugLog(`⏭️ Skipping free time detection on departure day`);
    }

    for (let i = 0; !skipFreeTimeDetection && i < timeline.length - 1; i++) {
      const currentItem = timeline[i];
      const nextItem = timeline[i + 1];

      // Define travel types
      const travelTypes = ['transit', 'inter-city-travel', 'airport-transit', 'station-transit', 'port-transit', 'airport-arrival', 'departure', 'transit-departure', 'hotel-transit', 'long-distance-transfer', 'long-distance-departure', 'road-trip', 'road-trip-return', 'transit-arrival'];

      // Major travel types - never show free time right before or after these (packing, preparing, or just arrived)
      const majorTravelTypes = ['inter-city-travel', 'airport-transit', 'station-transit', 'port-transit', 'departure', 'transit-departure', 'long-distance-transfer', 'long-distance-departure', 'road-trip', 'road-trip-return', 'airport-arrival', 'transit-arrival', 'hotel-checkin', 'hotel-checkout'];
      // BUGFIX (free-time gap suppression): the previous logic skipped free-time whenever
      // either side of the gap was a major travel item. This produced a 5+ hour blank void
      // on travel days like "Arrive 11am → Lunch 12:30 → Dinner 7:30" because the lunch→dinner
      // gap was suppressed as "near travel". Rules now:
      //   - Only skip free-time when BOTH sides of the gap are travel/transit items
      //     (e.g., arrival → next transit leg, or departure → airport).
      //   - Gaps adjacent to a meal (on either side) always get free-time detection —
      //     meals anchor the day rhythm, and gaps between meals/activities are real leisure.
      const currentIsMajorTravel = majorTravelTypes.includes(currentItem.type);
      const nextIsMajorTravel = majorTravelTypes.includes(nextItem.type);
      const currentIsMeal = currentItem.type === 'meal';
      const nextIsMeal = nextItem.type === 'meal';
      // Only suppress when BOTH sides are travel AND neither side is a meal
      if (currentIsMajorTravel && nextIsMajorTravel && !currentIsMeal && !nextIsMeal) continue;

      // For transit items, calculate when they END (not when they start)
      // This allows us to detect gaps AFTER transit completes
      if (['transit', 'hotel-transit', 'return-transit'].includes(currentItem.type)) {
        // Transit items have duration like "~20 mins" - extract the number
        const transitDurationMatch = currentItem.duration?.match(/(\d+)/);
        if (transitDurationMatch) {
          const transitDuration = parseInt(transitDurationMatch[1]);
          const transitEndMinutes = currentItem.sortTime + transitDuration;
          const nextStartMinutes = nextItem.sortTime;
          const gapMinutes = nextStartMinutes - transitEndMinutes;

          // If there's a significant gap AFTER transit, show free time
          // D1: Cap individual free time cards at 2 hours — but still show something for larger gaps
          const cappedTransitGap = Math.min(gapMinutes, 120);
          if (cappedTransitGap >= MIN_FREE_TIME_GAP && !majorTravelTypes.includes(nextItem.type)) {
            const freeTimeStartHours = Math.floor(transitEndMinutes / 60);
            const freeTimeStartMins = transitEndMinutes % 60;
            const gapHours = Math.floor(cappedTransitGap / 60);
            const gapMins = cappedTransitGap % 60;

            const durationStr = gapHours > 0
              ? (gapMins > 0 ? `${gapHours}hr ${gapMins}min` : `${gapHours}hr`)
              : `${gapMins}min`;

            const baseCity = sanitizeBaseCity(dayData?.baseCity || cityContext || '', cityContext || '');
            const aiSuggestions = dayData?.freeTimeSuggestions;

            // Get the "from" location for distance calculations
            // For transit items, the "to" is where free time starts
            const fromLocation = currentItem.to || currentItem.location || baseCity;
            // The "to" location is the next scheduled activity
            const toLocation = nextItem.location || nextItem.restaurant || nextItem.to || '';

            // Determine time of day for filtering suggestions
            const isBeforeNoon = freeTimeStartHours < 12;
            const isAfternoon = freeTimeStartHours >= 12 && freeTimeStartHours < 17;
            const isEvening = freeTimeStartHours >= 17;

            // Determine current time slot for structured timeOfDay filtering
            const currentTimeSlot = isBeforeNoon ? 'morning' : (isAfternoon ? 'afternoon' : 'evening');

            // Time-inappropriate keywords to filter (fallback for suggestions without timeOfDay)
            const eveningKeywords = ['evening', 'sunset', 'night', 'nightlife', 'cocktail', 'bar hopping', 'dinner spot'];
            const morningKeywords = ['sunrise', 'breakfast spot', 'morning market', 'early'];

            let suggestions;
            if (aiSuggestions && aiSuggestions.length > 0) {
              // Map suggestions and include timeOfDay if available
              // Filter out vague hotel-centric suggestions (e.g., "Family Suite Relaxation", "Hotel Pool Time")
              const VAGUE_SUGGESTION_PATTERN = /^(hotel|suite|room|lobby|relaxation|rest|nap|pack|unpack|settle|freshen|check.?in|check.?out)/i;
              let filtered = aiSuggestions
                .filter(s => s.activity && !VAGUE_SUGGESTION_PATTERN.test(s.activity.trim()))
                .map(s => ({
                  icon: 'map-pin',
                  label: s.activity,
                  description: s.description,
                  timeOfDay: s.timeOfDay || null
                }));
              // If all got filtered, use originals
              if (filtered.length === 0) {
                filtered = aiSuggestions.map(s => ({
                  icon: 'map-pin',
                  label: s.activity,
                  description: s.description,
                  timeOfDay: s.timeOfDay || null
                }));
              }

              // First, filter by timeOfDay field if available (new format)
              // Then fallback to keyword filtering for suggestions without timeOfDay
              filtered = filtered.filter(s => {
                // If suggestion has timeOfDay, use structured filtering
                if (s.timeOfDay) {
                  if (s.timeOfDay === 'any') return true;
                  if (currentTimeSlot === 'morning') return s.timeOfDay === 'morning' || s.timeOfDay === 'any';
                  if (currentTimeSlot === 'afternoon') return s.timeOfDay === 'afternoon' || s.timeOfDay === 'any';
                  if (currentTimeSlot === 'evening') return s.timeOfDay === 'evening' || s.timeOfDay === 'any';
                  return true;
                }

                // Fallback: keyword-based filtering for old format suggestions
                const text = `${s.label} ${s.description}`.toLowerCase();
                if (isBeforeNoon) {
                  return !eveningKeywords.some(kw => text.includes(kw));
                } else if (isAfternoon) {
                  return !eveningKeywords.some(kw => text.includes(kw)) &&
                         !morningKeywords.some(kw => text.includes(kw));
                } else if (isEvening) {
                  return !morningKeywords.some(kw => text.includes(kw));
                }
                return true;
              });

              // Filter out already-used suggestions
              const unusedFiltered = filtered.filter(s => !usedSuggestionLabels.has(s.label));
              if (unusedFiltered.length > 0) {
                suggestions = unusedFiltered;
              } else {
                // If all time-appropriate suggestions are used, fall back to "any" time suggestions
                let fallback = aiSuggestions
                  .map(s => ({ icon: 'map-pin', label: s.activity, description: s.description, timeOfDay: s.timeOfDay }))
                  .filter(s => !usedSuggestionLabels.has(s.label) && (s.timeOfDay === 'any' || !s.timeOfDay));

                suggestions = fallback;
              }
            } else {
              // Fallback: time-appropriate location-specific suggestions
              const nearbyContext = fromLocation ? ` near ${fromLocation.split(',')[0]}` : (baseCity ? ` in ${baseCity}` : '');
              const cityContext = baseCity || 'the area';
              let fallbackSuggestions;
              if (isBeforeNoon) {
                fallbackSuggestions = [
                  { icon: 'coffee', label: `Morning café${nearbyContext}`, description: `Discover a local café in ${cityContext}` },
                  { icon: 'camera', label: `Photo walk in ${cityContext}`, description: 'Morning light is perfect for photos' },
                  { icon: 'shopping-bag', label: `Local market${nearbyContext}`, description: `Browse morning markets and street vendors` }
                ];
              } else if (isAfternoon) {
                fallbackSuggestions = [
                  { icon: 'landmark', label: `Visit a landmark${nearbyContext}`, description: `Explore a nearby attraction in ${cityContext}` },
                  { icon: 'coffee', label: `Afternoon café${nearbyContext}`, description: `Relax at a cozy spot in ${cityContext}` },
                  { icon: 'shopping-bag', label: `Shopping in ${cityContext}`, description: 'Browse local shops and boutiques' }
                ];
              } else {
                fallbackSuggestions = [
                  { icon: 'sunset', label: `Sunset viewpoint${nearbyContext}`, description: `Catch golden hour in ${cityContext}` },
                  { icon: 'wine', label: `Nightlife in ${cityContext}`, description: 'Discover bars and restaurants locals love' },
                  { icon: 'moon', label: `Night walk${nearbyContext}`, description: `Experience ${cityContext} lit up at night` }
                ];
              }
              suggestions = fallbackSuggestions.filter(s => !usedSuggestionLabels.has(s.label));
            }

            // Take up to 3 suggestions and mark them as used
            const selectedSuggestions = suggestions.slice(0, 3);
            selectedSuggestions.forEach(s => usedSuggestionLabels.add(s.label));

            freeTimeCards.push({
              type: 'free-time',
              time: `${String(freeTimeStartHours).padStart(2, '0')}:${String(freeTimeStartMins).padStart(2, '0')}`,
              activity: 'Free Time',
              description: `Time to explore ${baseCity || 'at your own pace'}`,
              duration: durationStr,
              durationMinutes: cappedTransitGap,
              sortTime: transitEndMinutes,
              suggestions: selectedSuggestions,
              baseCity: baseCity,
              fromLocation: fromLocation,
              toLocation: toLocation
            });
          }
        }
        continue; // Still skip for the regular gap detection
      }

      // Don't add free time after airport check-in (airport-arrival) or before departure
      if (currentItem.type === 'airport-arrival' || nextItem.type === 'departure') continue;

      // Calculate end time of current item
      let currentEndMinutes = currentItem.sortTime;

      // For travel items with arrivalTime (road trips, inter-city), use that as end time
      if (travelTypes.includes(currentItem.type) && currentItem.arrivalTime) {
        const [h, m] = currentItem.arrivalTime.split(':').map(Number);
        currentEndMinutes = h * 60 + m;
      } else if (currentItem.type === 'hotel-checkin') {
        // After hotel check-in, add 60 min settling time before considering it "free time"
        currentEndMinutes += 60;
      } else if (currentItem.duration) {
        const durationMatch = currentItem.duration.match(/(\d+(?:\.\d+)?)\s*(hour|hr|min|minute)/i);
        if (durationMatch) {
          const value = parseFloat(durationMatch[1]);
          const unit = durationMatch[2].toLowerCase();
          if (unit.startsWith('hour') || unit === 'hr') {
            currentEndMinutes += value * 60;
          } else {
            currentEndMinutes += value;
          }
        }
      } else {
        // Default duration estimate based on type
        if (currentItem.type === 'activity') currentEndMinutes += 90;
        else if (currentItem.type === 'meal') currentEndMinutes += 60;
        else currentEndMinutes += 30;
      }

      const nextStartMinutes = nextItem.sortTime;
      const gapMinutes = nextStartMinutes - currentEndMinutes;

      // If there's a significant gap, add a free time card
      // D1: Cap individual free time display at 2 hours — but still fill larger gaps
      const cappedGapMinutes = Math.min(gapMinutes, 120);
      if (cappedGapMinutes >= MIN_FREE_TIME_GAP) {
        const freeTimeStartHours = Math.floor(currentEndMinutes / 60);
        const freeTimeStartMins = currentEndMinutes % 60;
        const gapHours = Math.floor(cappedGapMinutes / 60);
        const gapMins = cappedGapMinutes % 60;

        const durationStr = gapHours > 0
          ? (gapMins > 0 ? `${gapHours}hr ${gapMins}min` : `${gapHours}hr`)
          : `${gapMins}min`;

        // Get location-specific suggestions from AI-generated itinerary data
        const baseCity = sanitizeBaseCity(dayData?.baseCity || cityContext || '', cityContext || '');

        // Get the "from" location for distance calculations.
        //
        // Bug fix (2026-05-07): previously used `currentItem.to` as a
        // fallback. For TRANSIT items in this branch (less common), .to is
        // the destination — using it as fromLocation pointed both "Near"
        // and "Next" at the same place. Lisbon Day 1 modal showed
        // "Near: Belcanto / Next: Belcanto". Use `.from` for transits;
        // activities don't typically have `.to` so this is safe.
        const fromLocation = currentItem.location || currentItem.restaurant || currentItem.from || baseCity;
        // The "to" location is the next scheduled activity
        const toLocation = nextItem.location || nextItem.restaurant || nextItem.to || '';

        // Use AI-generated suggestions from the itinerary if available
        // These are specific real places generated for this exact city/day
        const aiSuggestions = dayData?.freeTimeSuggestions;

        // Determine time of day for filtering suggestions
        const isBeforeNoon = freeTimeStartHours < 12;
        const isAfternoon = freeTimeStartHours >= 12 && freeTimeStartHours < 17;
        const isEvening = freeTimeStartHours >= 17;

        // Determine current time slot for structured timeOfDay filtering
        const currentTimeSlot = isBeforeNoon ? 'morning' : (isAfternoon ? 'afternoon' : 'evening');

        // Time-inappropriate keywords to filter (fallback for suggestions without timeOfDay)
        const eveningKeywords = ['evening', 'sunset', 'night', 'nightlife', 'cocktail', 'bar hopping', 'dinner spot'];
        const morningKeywords = ['sunrise', 'breakfast spot', 'morning market', 'early'];

        let suggestions;
        if (aiSuggestions && aiSuggestions.length > 0) {
          // Convert AI suggestions to the format expected by the UI, including timeOfDay
          let filtered = aiSuggestions.map(s => ({
            emoji: s.emoji || '📍',
            label: s.activity,
            description: s.description,
            timeOfDay: s.timeOfDay || null
          }));

          // First, filter by timeOfDay field if available (new format)
          // Then fallback to keyword filtering for suggestions without timeOfDay
          filtered = filtered.filter(s => {
            // If suggestion has timeOfDay, use structured filtering
            if (s.timeOfDay) {
              if (s.timeOfDay === 'any') return true;
              if (currentTimeSlot === 'morning') return s.timeOfDay === 'morning' || s.timeOfDay === 'any';
              if (currentTimeSlot === 'afternoon') return s.timeOfDay === 'afternoon' || s.timeOfDay === 'any';
              if (currentTimeSlot === 'evening') return s.timeOfDay === 'evening' || s.timeOfDay === 'any';
              return true;
            }

            // Fallback: keyword-based filtering for old format suggestions
            const text = `${s.label} ${s.description}`.toLowerCase();
            if (isBeforeNoon) {
              return !eveningKeywords.some(kw => text.includes(kw));
            } else if (isAfternoon) {
              return !eveningKeywords.some(kw => text.includes(kw)) &&
                     !morningKeywords.some(kw => text.includes(kw));
            } else if (isEvening) {
              return !morningKeywords.some(kw => text.includes(kw));
            }
            return true;
          });

          // Filter out already-used suggestions
          const unusedFiltered = filtered.filter(s => !usedSuggestionLabels.has(s.label));
          if (unusedFiltered.length > 0) {
            suggestions = unusedFiltered;
          } else {
            // If all time-appropriate suggestions are used, fall back to "any" time suggestions
            let fallback = aiSuggestions
              .map(s => ({ emoji: s.emoji || '📍', label: s.activity, description: s.description, timeOfDay: s.timeOfDay }))
              .filter(s => !usedSuggestionLabels.has(s.label) && (s.timeOfDay === 'any' || !s.timeOfDay));
            suggestions = fallback;
          }
        } else {
          // Fallback: time-appropriate location-specific suggestions
          const nearbyCtx = fromLocation ? ` near ${fromLocation.split(',')[0]}` : (baseCity ? ` in ${baseCity}` : '');
          const cityCtx = baseCity || 'the area';
          let fallbackSuggestions;
          if (isBeforeNoon) {
            fallbackSuggestions = [
              { emoji: '☕', label: `Morning café${nearbyCtx}`, description: `Discover a local café in ${cityCtx}` },
              { emoji: '📸', label: `Photo walk in ${cityCtx}`, description: 'Morning light is perfect for photos' },
              { emoji: '🏪', label: `Local market${nearbyCtx}`, description: `Browse morning markets and street vendors` }
            ];
          } else if (isAfternoon) {
            fallbackSuggestions = [
              { emoji: '🏛️', label: `Visit a landmark${nearbyCtx}`, description: `Explore a nearby attraction in ${cityCtx}` },
              { emoji: '☕', label: `Afternoon café${nearbyCtx}`, description: `Relax at a cozy spot in ${cityCtx}` },
              { emoji: '🛍️', label: `Shopping in ${cityCtx}`, description: 'Browse local shops and boutiques' }
            ];
          } else {
            // Evening
            fallbackSuggestions = [
              { emoji: '🌅', label: `Sunset viewpoint${nearbyCtx}`, description: `Catch golden hour in ${cityCtx}` },
              { emoji: '🍷', label: `Nightlife in ${cityCtx}`, description: 'Discover bars and restaurants locals love' },
              { emoji: '🌃', label: `Night walk${nearbyCtx}`, description: `Experience ${cityCtx} lit up at night` }
            ];
          }
          suggestions = fallbackSuggestions.filter(s => !usedSuggestionLabels.has(s.label));
        }

        // Take up to 3 suggestions and mark them as used
        const selectedSuggestions = suggestions.slice(0, 3);
        selectedSuggestions.forEach(s => usedSuggestionLabels.add(s.label));

        freeTimeCards.push({
          type: 'free-time',
          time: `${String(freeTimeStartHours).padStart(2, '0')}:${String(freeTimeStartMins).padStart(2, '0')}`,
          activity: 'Free Time',
          description: `Time to explore ${baseCity || 'at your own pace'}`,
          duration: durationStr,
          durationMinutes: cappedGapMinutes,
          sortTime: currentEndMinutes,
          suggestions: selectedSuggestions,
          baseCity: baseCity,
          fromLocation: fromLocation,
          toLocation: toLocation
        });
      }
    }

    // BUGFIX (meal→meal fallback pass): guarantee that a large gap between two
    // consecutive MEALS always gets a free-time card, even if upstream logic (transit
    // branches, early-continue conditions, adjacency filters) happened to skip it.
    // This is the safety net for cases like "Lunch 12:30 → Dinner 19:30" leaving a
    // 5+ hour blank void on travel days.
    if (!skipFreeTimeDetection) {
      const mealItems = timeline
        .filter(t => t && t.type === 'meal' && typeof t.sortTime === 'number')
        .sort((a, b) => a.sortTime - b.sortTime);
      for (let i = 0; i < mealItems.length - 1; i++) {
        const mealA = mealItems[i];
        const mealB = mealItems[i + 1];
        // Estimate end of mealA (default 60min)
        let mealAEnd = mealA.sortTime + 60;
        if (mealA.duration) {
          const m = mealA.duration.match(/(\d+(?:\.\d+)?)\s*(hour|hr|min|minute)/i);
          if (m) {
            const v = parseFloat(m[1]);
            mealAEnd = mealA.sortTime + (m[2].toLowerCase().startsWith('hour') || m[2].toLowerCase() === 'hr' ? v * 60 : v);
          }
        }
        const mealGap = mealB.sortTime - mealAEnd;
        if (mealGap < MIN_FREE_TIME_GAP) continue;

        // Check if a free-time card already lives in this window (either existing in
        // timeline or queued in freeTimeCards).
        const windowStart = mealAEnd;
        const windowEnd = mealB.sortTime;
        const alreadyHas = [...timeline, ...freeTimeCards].some(t =>
          t && t.type === 'free-time' &&
          typeof t.sortTime === 'number' &&
          t.sortTime >= windowStart - 1 && t.sortTime < windowEnd
        );
        if (alreadyHas) continue;

        const cappedGap = Math.min(mealGap, 120);
        const startH = Math.floor(mealAEnd / 60);
        const startM = mealAEnd % 60;
        const gapH = Math.floor(cappedGap / 60);
        const gapM = cappedGap % 60;
        const durationStr = gapH > 0
          ? (gapM > 0 ? `${gapH}hr ${gapM}min` : `${gapH}hr`)
          : `${gapM}min`;

        const baseCity = sanitizeBaseCity(dayData?.baseCity || cityContext || '', cityContext || '');
        const fromLocation = mealA.location || mealA.restaurant || baseCity;
        const toLocation = mealB.location || mealB.restaurant || '';

        // Reuse AI suggestions if available, lightly filtered to unused labels.
        const aiSuggestions = dayData?.freeTimeSuggestions;
        let selectedSuggestions = [];
        if (aiSuggestions && aiSuggestions.length > 0) {
          const candidates = aiSuggestions
            .map(s => ({ emoji: s.emoji || '📍', icon: 'map-pin', label: s.activity, description: s.description, timeOfDay: s.timeOfDay || null }))
            .filter(s => s.label && !usedSuggestionLabels.has(s.label));
          selectedSuggestions = candidates.slice(0, 3);
          selectedSuggestions.forEach(s => usedSuggestionLabels.add(s.label));
        }

        debugLog(`🛟 Meal→meal fallback free-time: ${mealA.activity || 'meal'} → ${mealB.activity || 'meal'} (${mealGap}min gap)`);

        freeTimeCards.push({
          type: 'free-time',
          time: `${String(startH).padStart(2, '0')}:${String(startM).padStart(2, '0')}`,
          activity: 'Free Time',
          description: `Time to explore ${baseCity || 'at your own pace'}`,
          duration: durationStr,
          durationMinutes: cappedGap,
          sortTime: mealAEnd,
          suggestions: selectedSuggestions,
          baseCity,
          fromLocation,
          toLocation,
          _source: 'meal-meal-fallback'
        });
      }
    }

    // Add free time cards to timeline and re-sort (with type tiebreaker)
    timeline.push(...freeTimeCards);
    timeline.sort(timelineSortComparator);

    // Remove transit cards that are directly adjacent to free time blocks,
    // but ONLY if no real activity occupies the same time slot.
    // When a user adds an activity via "Ask Arrivo", the activity replaces the free time
    // at the same time. In that case, keep the transit to connect activity → next item.
    const freeTimeAdjacentTransitTypes = ['transit', 'hotel-transit', 'return-transit'];
    // Check if any real activity/meal exists at the same time as a free-time card
    const freeTimeHasActivity = new Set();
    timeline.forEach(item => {
      if (item.type === 'free-time') {
        const hasRealItem = timeline.some(other =>
          other !== item &&
          (other.type === 'activity' || other.type === 'meal') &&
          Math.abs((other.sortTime || 0) - (item.sortTime || 0)) < 30 // within 30 min
        );
        if (hasRealItem) freeTimeHasActivity.add(item.sortTime);
      }
    });

    // First: remove free-time cards that have been replaced by actual activities
    const timelineWithoutReplacedFreeTime = timeline.filter(item => {
      if (item.type === 'free-time' && freeTimeHasActivity.has(item.sortTime)) return false;
      return true;
    });

    // Then: remove transit adjacent to remaining (unoccupied) free-time cards
    const timelineAfterFreeTimeCleanup = timelineWithoutReplacedFreeTime.filter((item, idx, arr) => {
      if (!freeTimeAdjacentTransitTypes.includes(item.type)) return true;
      const prevItem = idx > 0 ? arr[idx - 1] : null;
      const nextItem = idx < arr.length - 1 ? arr[idx + 1] : null;
      if (nextItem?.type === 'free-time') return false;
      if (prevItem?.type === 'free-time') return false;
      return true;
    });
    timeline.length = 0;
    timeline.push(...timelineAfterFreeTimeCleanup);

    // De-duplicate transit cards - check BOTH transitKey AND from/to locations
    // This catches cases where different transit keys (e.g., 'transit-1' vs 'post-freetime-3')
    // represent the same physical transit segment
    const seenTransitKeys = new Set();
    const seenFromToSegments = new Set(); // Track unique from/to combinations
    const transitTypes = ['transit', 'return-transit', 'hotel-transit', 'airport-transit', 'station-transit', 'port-transit', 'inter-city-travel', 'drive'];

    const deduplicatedTimeline = timeline.filter(item => {
      if (transitTypes.includes(item.type)) {
        // First check: transitKey-based deduplication
        if (item.transitKey && seenTransitKeys.has(item.transitKey)) {
          timelineLog(`  🔄 Duplicate transit (key): ${item.transitKey}`);
          return false; // Skip duplicate
        }

        // Second check: from/to segment deduplication (catches different keys for same segment)
        // Normalize location names for comparison
        const fromNorm = (item.from || '').toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 40);
        const toNorm = (item.to || '').toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 40);
        const segmentKey = `${fromNorm}-->${toNorm}`;

        if (seenFromToSegments.has(segmentKey)) {
          timelineLog(`  🔄 Duplicate transit (segment): ${item.from} → ${item.to} (key: ${item.transitKey})`);
          return false; // Skip duplicate - same from/to already exists
        }

        // Add to tracking sets
        if (item.transitKey) {
          seenTransitKeys.add(item.transitKey);
        }
        seenFromToSegments.add(segmentKey);
      }
      return true;
    });

    // ADDITIONAL: Remove consecutive transit cards (can't have two transits back-to-back)
    // Keep the transit that has valid data OR connects to the next activity
    // IMPORTANT: Don't filter out inter-city-travel cards - they should appear between station-transit cards
    const localTransitTypes = ['transit', 'return-transit', 'hotel-transit', 'airport-transit', 'station-transit', 'port-transit', 'drive'];
    const finalTimeline = deduplicatedTimeline.filter((item, idx, arr) => {
      if (idx === 0) return true;
      const prevItem = arr[idx - 1];
      // Only filter LOCAL transit types (not inter-city-travel which is the main travel between cities)
      const currentIsLocalTransit = localTransitTypes.includes(item.type);
      const prevIsLocalTransit = localTransitTypes.includes(prevItem.type);

      if (currentIsLocalTransit && prevIsLocalTransit) {
        // Two transits back-to-back - decide which to keep

        // Check if either has N/A or invalid data (indicates bad routing)
        const prevHasInvalidData = (prevItem.duration || '').includes('N/A') ||
          prevItem.duration === '~15 mins' && (!prevItem.transitKey || prevItem.transitKey.includes('post-freetime'));
        const currentHasInvalidData = (item.duration || '').includes('N/A') ||
          item.duration === '~15 mins' && (!item.transitKey || item.transitKey.includes('post-freetime'));

        // If prev has invalid data but current doesn't, remove prev (keep current)
        if (prevHasInvalidData && !currentHasInvalidData) {
          timelineLog(`  🔄 Keeping current transit (prev had invalid data): ${item.from} → ${item.to}`);
          return true;
        }

        // If current has invalid data but prev doesn't, remove current
        if (currentHasInvalidData && !prevHasInvalidData) {
          timelineLog(`  🔄 Removing current transit (invalid data): ${item.from} → ${item.to}`);
          return false;
        }

        // Look ahead to find the next non-transit item
        let nextNonTransit = null;
        for (let j = idx + 1; j < arr.length; j++) {
          if (!localTransitTypes.includes(arr[j].type)) {
            nextNonTransit = arr[j];
            break;
          }
        }

        // If this transit's "to" matches the next activity better, keep it
        // Otherwise, skip it (the previous transit will handle it)
        if (nextNonTransit) {
          const nextLocation = (nextNonTransit.location || nextNonTransit.restaurant || '').toLowerCase();
          const currentTo = (item.to || '').toLowerCase();
          const prevTo = (prevItem.to || '').toLowerCase();

          // If current transit goes directly to the next activity and prev doesn't, keep current
          if (nextLocation && currentTo.includes(nextLocation.slice(0, 15)) && !prevTo.includes(nextLocation.slice(0, 15))) {
            return true;
          }
        }

        debugLog(`  🔄 Removing consecutive transit: ${item.from} → ${item.to} (prev was also transit)`);
        return false;
      }
      return true;
    });

    // CLEANUP: Remove any transit/free-time items that appear AFTER the end-of-day hotel return
    // The "Arrive Back at Hotel" or "End of Day at Hotel" should be the last meaningful item
    const endOfDayTypes = ['hotel-return'];
    const endOfDayIdx = finalTimeline.findLastIndex(item => endOfDayTypes.includes(item.type));
    let cleanedTimeline = finalTimeline;
    if (endOfDayIdx >= 0 && endOfDayIdx < finalTimeline.length - 1) {
      const afterEndOfDay = finalTimeline.slice(endOfDayIdx + 1);
      const hasOnlyTransitOrFreeTime = afterEndOfDay.every(item =>
        transitTypes.includes(item.type) || item.type === 'free-time'
      );
      if (hasOnlyTransitOrFreeTime) {
        cleanedTimeline = finalTimeline.slice(0, endOfDayIdx + 1);
        debugLog(`  🧹 Removed ${afterEndOfDay.length} phantom items after end-of-day hotel return`);
      }
    }

    // CHRONOLOGICAL VALIDATION PASS — fix overlapping items and ensure correct order
    const validatedTimeline = (() => {
      const sorted = [...cleanedTimeline].sort(timelineSortComparator);

      // Check for time overlaps and shift later items
      for (let i = 1; i < sorted.length; i++) {
        const prev = sorted[i - 1];
        const curr = sorted[i];
        const prevDuration = parseDurationToMinutes(prev.duration);
        const prevEndMinutes = prev.sortTime + prevDuration;

        // If current item starts before previous item ends, shift it
        // Allow meals to overlap with activities by up to 15 min (parallel scheduling)
        // but fix larger overlaps to prevent double-stacking
        const isMealOverlap = (prev.type === 'meal' || curr.type === 'meal') ||
          (prev.type === 'activity' && curr.type === 'meal') ||
          (prev.type === 'meal' && curr.type === 'activity');
        const mealOverlapThreshold = isMealOverlap ? 15 : 0;

        if (curr.sortTime < prevEndMinutes - mealOverlapThreshold && prevEndMinutes > 0) {
          // Only shift if the gap is significant (> 5 min overlap)
          const overlap = prevEndMinutes - curr.sortTime;
          if (overlap > 5) {
            let newSortTime = prevEndMinutes + 5; // 5 min buffer
            // Day-rollover guard: never silently push an item past midnight.
            // If the shift would cross the day boundary, clamp to 23:45 so the
            // renderer doesn't show "12:10 AM" on a day that ends before midnight.
            // Callers that genuinely need cross-midnight scheduling should set
            // _dayOffset explicitly — this branch only handles overlap-fix sort.
            if (newSortTime >= 1440) {
              debugLog(`⏱ Chronological shift would cross midnight for "${curr.activity || curr.restaurant || curr.meal}" — clamping to 23:45`);
              newSortTime = 1425; // 23:45
            }
            const newHours = Math.floor(newSortTime / 60);
            const newMins = newSortTime % 60;
            curr.sortTime = newSortTime;
            curr.time = `${String(newHours).padStart(2, '0')}:${String(newMins).padStart(2, '0')}`;
          }
        }
      }

      return sorted;
    })();

    // C1: MEAL COVERAGE — ensure every day has breakfast, lunch, and dinner
    // AI sometimes omits meals, especially on multi-stop days with lots of transit
    // hasMealType now uses shared helper from displayGuardrails
    const hasThisMealType = (type) => hasMealType(validatedTimeline, type);
    // Don't inject meals on departure days with very early flights (before 7am) or arrival days
    const hasVeryEarlyDeparture = validatedTimeline.some(item =>
      (item.type === 'departure' || item.type === 'transit-departure') && item.sortTime < 420
    );
    const hasLateDeparture = validatedTimeline.some(item =>
      (item.type === 'departure' || item.type === 'transit-departure') && item.sortTime > 1200
    );
    // Find arrival time on this day (airport arrival, checkin, etc.) — don't inject meals BEFORE arrival
    const arrivalEvent = validatedTimeline.find(item =>
      item.type === 'airport-arrival' || item.type === 'transit-arrival' ||
      (item.type === 'arrival' || (item.activity || '').toLowerCase().includes('arrival'))
    );
    const arrivalSortTime = arrivalEvent?.sortTime || 0;
    const isArrivalDay = isFirstDay || !!arrivalEvent;
    const dayCity = dayData?.baseCity || '';
    const dayHotelForMeals = dayData?.hotel?.name || 'Hotel';

    // D2: On travel days, breakfast uses previous day's hotel (user wakes up there)
    const breakfastCtx = getBreakfastHotelContext({
      isInterCityTravelDay: isInterCityTravelDay && !isFirstDay,
      prevHotelName: prevDayHotelName,
      hotelName: dayHotelForMeals,
      prevCity: prevDayData?.baseCity,
      currentCity: dayCity,
    });
    const breakfastHotel = breakfastCtx.hotel;
    const breakfastCity = breakfastCtx.city;

    // D3: Build transport context for meal blocking
    const mealTransportCtx = {
      transportDepartureMinutes: icTransportDepartureMinutes,
      transportArrivalMinutes: icTransportArrivalMinutes,
    };

    // D10: isResortStay now uses shared helper from displayGuardrails.
    // Pass dayCity so cruise-only destinations (Antarctica, Galapagos) get
    // ship-based meal names instead of "Lunch in Antarctica" placeholders.
    const isResortStayFlag = isResortStay(dayHotelForMeals, accommodationType, dayCity);
    // Safety labels: if resort=true but hotel is missing, avoid "null Restaurant"
    const _isCruiseOnly = isCruiseOnlyDestination(dayCity);
    const _resortRestaurantLabel = dayHotelForMeals && dayHotelForMeals !== 'Hotel'
      ? `${dayHotelForMeals} Restaurant`
      : (_isCruiseOnly ? 'Ship Dining' : 'Hotel Dining');
    const _resortLocationLabel = dayHotelForMeals && dayHotelForMeals !== 'Hotel'
      ? dayHotelForMeals
      : (_isCruiseOnly ? 'Ship' : (dayCity || 'Hotel'));
    const _resortMealHint = dayHotelForMeals && dayHotelForMeals !== 'Hotel'
      ? `at ${dayHotelForMeals}`
      : (_isCruiseOnly ? 'on the ship' : 'at the hotel');

    // Fix 2: Skip ALL meal injection on overnight arrival days.
    // User is in-flight until arrival — display layer shouldn't inject breakfast/lunch/dinner
    // at the destination hotel. AI-provided post-arrival meals (surviving cleanOvernightArrivalDay) are sufficient.
    const skipMealInjectionForOvernightArrival = isArrivalDayAfterOvernightTravel;
    if (skipMealInjectionForOvernightArrival) {
      debugLog(`🍽️ Skipping all meal injection on day ${dayIndex + 1} — overnight arrival day`);
    }

    // L1: On Day 1 with arrival flight or long drive (>3h), skip placeholder meal
    // injection entirely when we have no real restaurant data. Meals in transit
    // are expected and a generic "Lunch · 1 hour · [city]" card adds noise.
    const day1HasArrivalFlight = isFirstDay && !!arrivalFlight;
    const icTransportDurationMinutes = (icTransportDepartureMinutes != null && icTransportArrivalMinutes != null)
      ? Math.max(0, icTransportArrivalMinutes - icTransportDepartureMinutes)
      : 0;
    const day1HasLongDrive = isFirstDay && (
      (transportationMode === 'drive' && (
        (calculatedDriveTime?.hours || selectedDestination?.driveHours || 0) > 3
      )) ||
      icTransportDurationMinutes > 180
    );
    const day1IsLongTravelDay = day1HasArrivalFlight || day1HasLongDrive;
    // For non-placeholder labels when rename mode applies
    const placeholderLunchLabel = 'Lunch · pick a spot';
    const placeholderBreakfastLabel = 'Breakfast · pick a spot';
    const placeholderDinnerLabel = 'Dinner · pick a spot';

    if (!hasThisMealType('breakfast') && !hasVeryEarlyDeparture && !isFirstDay && !skipMealInjectionForOvernightArrival) {
      // D3: Skip breakfast if blocked by transport (e.g., very early departure flight)
      const breakfastBlocked = isInterCityTravelDay && isMealBlockedByTransport('breakfast', mealTransportCtx);
      if (!breakfastBlocked) {
        // D2: Use correct hotel context for breakfast
        // L1: When breakfast has no real hotel/restaurant name, surface a "pick a spot" label.
        const isBreakfastPlaceholder = breakfastHotel === 'Hotel';
        const breakfastActivity = isBreakfastPlaceholder ? placeholderBreakfastLabel : 'Breakfast';
        // Cruise destinations: breakfast is on the ship too. Avoid "Breakfast in Antarctica".
        const _breakfastOnShip = isResortStayFlag && isCruiseOnlyDestination(breakfastCity || dayCity);
        validatedTimeline.push({
          type: 'meal', meal: 'Breakfast', mealType: 'breakfast', originalType: 'breakfast',
          time: '08:30', activity: breakfastActivity,
          restaurant: breakfastHotel && breakfastHotel !== 'Hotel'
            ? `${breakfastHotel} Restaurant`
            : (_breakfastOnShip ? _resortRestaurantLabel : (breakfastCity ? `Breakfast in ${breakfastCity}` : 'Hotel Breakfast')),
          description: breakfastCity ? `Start the day with breakfast in ${breakfastCity}` : 'Start the day with a local breakfast',
          duration: '45 minutes', location: breakfastHotel || _resortLocationLabel, sortTime: 510,
          isDisplayInjected: true,
          _needsReplacement: !_breakfastOnShip && (breakfastHotel === 'Hotel' || !breakfastHotel),
          _ctaHint: isBreakfastPlaceholder ? { cta: 'Search nearby', query: `breakfast in ${breakfastCity || 'area'}` } : undefined,
        });
        debugLog(`🍳 Injected missing breakfast on day ${dayIndex + 1} (hotel: ${breakfastHotel})`);
      } else {
        debugLog(`🍳 Skipped breakfast on day ${dayIndex + 1} — blocked by transport`);
      }
    }

    // D6: Shift breakfast earlier if it overlaps with next timeline item
    const injectedBreakfast = validatedTimeline.find(i => i.meal === 'Breakfast' && i.sortTime === 510);
    if (injectedBreakfast) {
      const breakfastEnd = 510 + 45; // 08:30 + 45 min = 09:15
      const earliestNonMeal = validatedTimeline
        .filter(i => i.type !== 'meal' && i.sortTime > 0)
        .sort((a, b) => a.sortTime - b.sortTime)[0];
      if (earliestNonMeal && earliestNonMeal.sortTime < breakfastEnd) {
        const newBreakfastStart = Math.max(420, earliestNonMeal.sortTime - 45); // At least 7:00 AM
        const h = Math.floor(newBreakfastStart / 60);
        const m = newBreakfastStart % 60;
        injectedBreakfast.time = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        injectedBreakfast.sortTime = newBreakfastStart;
        debugLog(`🍳 Shifted breakfast earlier to ${injectedBreakfast.time} to avoid overlap`);
      }
    }

    // Only inject lunch if: not last day, and not before arrival on arrival days
    if (!hasThisMealType('lunch') && !isLastDay && !(isArrivalDay && arrivalSortTime > 720) && !skipMealInjectionForOvernightArrival) {
      // D3: Skip lunch if blocked by transport (e.g., in-flight)
      const lunchBlocked = isInterCityTravelDay && isMealBlockedByTransport('lunch', mealTransportCtx);
      // L1: Skip placeholder lunch entirely on Day 1 long-travel days when
      // we'd only emit a generic "Lunch in <city>" card (no real restaurant).
      const skipPlaceholderForDay1Travel = day1IsLongTravelDay && !isResortStayFlag;
      if (!lunchBlocked && !skipPlaceholderForDay1Travel) {
        // Skip lunch if arrival is after noon (720 = 12:00) — meal would appear before the traveler lands
        const defaultLunchTime = isArrivalDay ? Math.max(780, arrivalSortTime + 60) : 780; // After arrival + 1hr buffer, or 13:00
        // Fix 4: Use collision-aware slot finder. force:true — always show a
        // lunch "pick a spot" card even on a tight day; user can reschedule.
        const lunchSlot = findMealSlot('lunch', validatedTimeline.filter(i => i.sortTime > 0), defaultLunchTime, { force: true });
        if (lunchSlot !== null) {
          const lunchHours = Math.floor(lunchSlot / 60);
          const lunchMins = lunchSlot % 60;
          // L1: When we're about to emit a placeholder ("Lunch in <city>"),
          // use a more honest label and flag it so the UI can show a "Search nearby" affordance.
          const isPlaceholder = !isResortStayFlag;
          const lunchActivity = isPlaceholder ? placeholderLunchLabel : 'Lunch';
          validatedTimeline.push({
            type: 'meal', meal: 'Lunch', mealType: 'lunch', originalType: 'lunch',
            time: `${String(lunchHours).padStart(2, '0')}:${String(lunchMins).padStart(2, '0')}`,
            activity: lunchActivity,
            // C1 fix: don't emit placeholder copy for unfilled slots — let the
            // "Tap for picks" renderer drive the card when _needsReplacement=true.
            restaurant: isResortStayFlag ? _resortRestaurantLabel : `Lunch in ${dayCity || 'the area'}`,
            description: isResortStayFlag ? `Lunch ${_resortMealHint}` : '',
            duration: '1 hour', location: isResortStayFlag ? _resortLocationLabel : dayCity, sortTime: lunchSlot,
            isDisplayInjected: true, _needsReplacement: !isResortStayFlag,
            _ctaHint: isPlaceholder ? { cta: 'Search nearby', query: `restaurants in ${dayCity || 'area'}` } : undefined,
          });
          debugLog(`🍽️ Injected missing lunch on day ${dayIndex + 1} at slot ${lunchSlot} (default was ${defaultLunchTime})`);
        } else {
          debugLog(`🍽️ Skipped lunch on day ${dayIndex + 1} — no collision-free slot found`);
        }
      } else if (skipPlaceholderForDay1Travel) {
        debugLog(`🍽️ L1: Skipped placeholder lunch on Day 1 long-travel day`);
      } else {
        debugLog(`🍽️ Skipped lunch on day ${dayIndex + 1} — blocked by transport`);
      }
    }
    if (!hasThisMealType('dinner') && !hasLateDeparture && !isLastDay && !skipMealInjectionForOvernightArrival) {
      // D3: Skip dinner if blocked by transport
      const dinnerBlocked = isInterCityTravelDay && isMealBlockedByTransport('dinner', mealTransportCtx);
      if (!dinnerBlocked) {
        // Fix 4: Use collision-aware slot finder. force:true — dinner is the
        // most commonly-dropped meal on tight days. Always show the card; user reschedules.
        const dinnerSlot = findMealSlot('dinner', validatedTimeline.filter(i => i.sortTime > 0), 1170, { force: true });
        if (dinnerSlot !== null) {
          const dinnerHours = Math.floor(dinnerSlot / 60);
          const dinnerMins = dinnerSlot % 60;
          // L1: "Dinner · pick a spot" label + CTA when we have no real restaurant.
          const isDinnerPlaceholder = !isResortStayFlag;
          const dinnerActivity = isDinnerPlaceholder ? placeholderDinnerLabel : 'Dinner';
          validatedTimeline.push({
            type: 'meal', meal: 'Dinner', mealType: 'dinner', originalType: 'dinner',
            time: `${String(dinnerHours).padStart(2, '0')}:${String(dinnerMins).padStart(2, '0')}`,
            activity: dinnerActivity,
            restaurant: isResortStayFlag ? _resortRestaurantLabel : '{TBD: pick a dinner spot}',
            description: isResortStayFlag ? `Dinner ${_resortMealHint}` : '',
            duration: '1 hour 30 minutes', location: isResortStayFlag ? _resortLocationLabel : dayCity, sortTime: dinnerSlot,
            isDisplayInjected: true, _needsReplacement: !isResortStayFlag,
            _ctaHint: isDinnerPlaceholder ? { cta: 'Search nearby', query: `dinner in ${dayCity || 'area'}` } : undefined,
          });
          debugLog(`🍽️ Injected missing dinner on day ${dayIndex + 1} at slot ${dinnerSlot}`);
        } else {
          debugLog(`🍽️ Skipped dinner on day ${dayIndex + 1} — no collision-free slot found`);
        }
      } else {
        debugLog(`🍽️ Skipped dinner on day ${dayIndex + 1} — blocked by transport`);
      }
    }
    // Re-sort if meals were added
    validatedTimeline.sort(timelineSortComparator);

    // MEAL DEDUPLICATION — remove duplicate meal types (e.g., two breakfasts from different injection phases)
    // Keep the first occurrence of each meal type (breakfast/lunch/dinner) and remove later duplicates
    const seenMealTypes = new Set();
    for (let i = validatedTimeline.length - 1; i >= 0; i--) {
      const item = validatedTimeline[i];
      if (item.type !== 'meal') continue;
      const mealKey = (item.meal || item.mealType || item.activity || '').toLowerCase();
      const normalizedType = mealKey.includes('breakfast') ? 'breakfast' :
        mealKey.includes('lunch') ? 'lunch' :
        mealKey.includes('dinner') ? 'dinner' :
        mealKey.includes('brunch') ? 'brunch' : mealKey;
      if (!normalizedType) continue;
      if (seenMealTypes.has(normalizedType)) {
        // Duplicate — remove the display-injected one (prefer AI-generated)
        if (item.isDisplayInjected) {
          debugLog(`🍽️ Dedup: Removed injected duplicate ${normalizedType} on day ${dayIndex + 1}`);
          validatedTimeline.splice(i, 1);
        }
      } else {
        seenMealTypes.add(normalizedType);
      }
    }
    // If duplicates remain after preferring AI-generated, do a second pass keeping earliest
    const seenMealTypes2 = new Set();
    for (let i = 0; i < validatedTimeline.length; i++) {
      const item = validatedTimeline[i];
      if (item.type !== 'meal') continue;
      const mealKey = (item.meal || item.mealType || item.activity || '').toLowerCase();
      const normalizedType = mealKey.includes('breakfast') ? 'breakfast' :
        mealKey.includes('lunch') ? 'lunch' :
        mealKey.includes('dinner') ? 'dinner' :
        mealKey.includes('brunch') ? 'brunch' : mealKey;
      if (!normalizedType) continue;
      if (seenMealTypes2.has(normalizedType)) {
        debugLog(`🍽️ Dedup: Removed extra ${normalizedType} on day ${dayIndex + 1} (keeping earlier)`);
        validatedTimeline.splice(i, 1);
        i--; // Re-check current index
      } else {
        seenMealTypes2.add(normalizedType);
      }
    }

    // Fix 6: Pre-dawn safety cap — remove non-logistics items before 6 AM
    // Belt-and-suspenders with the shared processDayForDisplay orchestrator
    const preDawnCount = validatedTimeline.length;
    const safetyCapped = filterPreDawnItems(validatedTimeline);
    if (safetyCapped.length < preDawnCount) {
      debugLog(`⚠️ Fix 6: Removed ${preDawnCount - safetyCapped.length} pre-dawn items on day ${dayIndex + 1}`);
      validatedTimeline.length = 0;
      validatedTimeline.push(...safetyCapped);
    }

    // B3: LOOK-AHEAD — If the NEXT day has overnight transport arriving,
    // inject departure logistics (checkout, airport transit, flight card) into THIS day's timeline.
    // This fixes the bug where overnight flight departure logistics were shown on the arrival day.
    if (dayIndex >= 0 && itinerary?.days?.[dayIndex + 1]) {
      const nextDayIndex = dayIndex + 1;
      const nextDayTransport = (itinerary.interCityTransport || []).find(t => t.day === nextDayIndex + 1);
      const isNextDayOvernightArrival = nextDayTransport &&
        (nextDayTransport.isOvernight || nextDayTransport.note?.toLowerCase()?.includes('overnight'));

      if (isNextDayOvernightArrival && !isLastDay) {
        const departureTime = nextDayTransport.departureTime || '22:00';
        const [depH, depM] = departureTime.split(':').map(Number);
        const depMinutes = (depH || 22) * 60 + (depM || 0);
        const transportMode = nextDayTransport.mode || 'flight';
        const isFlight = transportMode === 'flight';
        const nextCity = itinerary.days[nextDayIndex]?.baseCity || '';
        const currentCity = dayData?.baseCity || '';

        // Determine airport/station names
        const fromIata = lookupCityAirportCode(currentCity);
        const toIata = lookupCityAirportCode(nextCity);
        const fromAirport = nextDayTransport.from?.airport || (fromIata ? `${fromIata} International Airport` : `${currentCity} Airport`);
        const toAirport = nextDayTransport.to?.airport || (toIata ? `${toIata} International Airport` : `${nextCity} Airport`);
        const carrier = nextDayTransport.carrier || nextDayTransport.airline || '';
        const duration = nextDayTransport.duration || '';

        // Airport transit (2h before departure for flights)
        const transitBuffer = isFlight ? 120 : 60;
        const transitMinutes = depMinutes - transitBuffer;

        if (transitMinutes > 0) {
          // Add "Head to Airport" card
          validatedTimeline.push({
            type: 'airport-transit',
            time: `${String(Math.floor(transitMinutes / 60)).padStart(2, '0')}:${String(transitMinutes % 60).padStart(2, '0')}`,
            activity: `🚕 Head to ${isFlight ? fromAirport : `${currentCity} Station`}`,
            description: `Arrive ${isFlight ? '2 hours' : '1 hour'} before departure for your overnight ${isFlight ? 'flight' : transportMode} to ${nextCity}`,
            location: isFlight ? fromAirport : `${currentCity} Station`,
            duration: `${transitBuffer} minutes`,
            sortTime: transitMinutes,
            isOvernightDeparture: true,
          });

          // Add departure flight/train card
          validatedTimeline.push({
            type: 'inter-city-travel',
            time: departureTime,
            activity: `${isFlight ? '✈️' : '🚆'} Overnight ${isFlight ? 'Flight' : 'Train'} to ${nextCity}`,
            description: `${carrier ? `${carrier} ` : ''}${isFlight ? 'Flight' : 'Train'} departing at ${departureTime}${duration ? ` (${duration})` : ''}. Arriving ${nextCity} tomorrow.`,
            location: `${isFlight ? (fromIata || currentCity) : currentCity} → ${isFlight ? (toIata || nextCity) : nextCity}`,
            duration: duration || 'Overnight',
            sortTime: depMinutes,
            isOvernightDeparture: true,
            fromCity: currentCity,
            toCity: nextCity,
            fromAirportCode: fromIata,
            toAirportCode: toIata,
          });

          debugLog(`✈️ B3: Injected overnight departure logistics into Day ${dayIndex + 1} for ${currentCity}→${nextCity}`);
        }

        // Re-sort after injection
        validatedTimeline.sort(timelineSortComparator);
      }
    }

    // C2: Afternoon-gap guard — QA caught days where lunch ended <3pm and the
    // next non-meal item was after 5pm, leaving a dead 2-4h afternoon stretch
    // on the printed timeline. The earlier free-time detection loop misses
    // this when the lunch card is adjacent to a late dinner with no other
    // items between. Scan for that specific shape and inject a free-time card
    // covering the afternoon window.
    (function injectAfternoonGapCard() {
      const items = validatedTimeline
        .filter((it) => typeof it?.sortTime === 'number')
        .sort((a, b) => a.sortTime - b.sortTime);
      // Find lunch whose end lands before 3pm
      const lunch = items.find((it) => (it.type === 'meal' && /lunch/i.test(`${it.meal || it.mealType || it.activity || ''}`)));
      if (!lunch) return;
      const lunchDurMatch = String(lunch.duration || '1 hour').match(/(\d+(?:\.\d+)?)\s*(hour|hr|min|minute)/i);
      let lunchEnd = lunch.sortTime + 60;
      if (lunchDurMatch) {
        const v = parseFloat(lunchDurMatch[1]);
        const u = lunchDurMatch[2].toLowerCase();
        lunchEnd = lunch.sortTime + (u.startsWith('hour') || u === 'hr' ? v * 60 : v);
      }
      if (lunchEnd >= 15 * 60) return; // Lunch ended 3pm+ → no afternoon gap
      // Find the next non-meal, non-free-time, non-return-to-hotel item
      const afterLunch = items.filter((it) => it.sortTime > lunchEnd && it.type !== 'meal'
        && it.type !== 'free-time' && it.type !== 'transit' && it.type !== 'return-transit');
      const nextItem = afterLunch[0];
      const afternoonCutoff = 17 * 60; // 5pm
      if (nextItem && nextItem.sortTime <= afternoonCutoff) return; // There IS something in the afternoon
      // Find the next meal (likely dinner) or end-of-day as the bound
      const nextMeal = items.find((it) => it.sortTime > lunchEnd && it.type === 'meal');
      const gapEnd = nextMeal ? nextMeal.sortTime : (nextItem ? nextItem.sortTime : 20 * 60);
      const gapMinutes = gapEnd - lunchEnd;
      if (gapMinutes < 120) return; // Only fill ≥2h gaps
      // Already have a free-time card sitting in this window? skip.
      const alreadyCovered = items.some((it) => it.type === 'free-time'
        && it.sortTime >= lunchEnd - 30 && it.sortTime < gapEnd);
      if (alreadyCovered) return;
      const startMins = lunchEnd;
      const h = Math.floor(startMins / 60);
      const m = startMins % 60;
      const durMinutes = Math.min(gapMinutes, 180);
      const dh = Math.floor(durMinutes / 60);
      const dm = durMinutes % 60;
      const durStr = dh > 0 ? (dm > 0 ? `${dh}hr ${dm}min` : `${dh}hr`) : `${dm}min`;
      const _sanitizedDayCity = sanitizeBaseCity(dayData?.baseCity || '', '');
      validatedTimeline.push({
        type: 'free-time',
        time: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`,
        activity: 'Free Time',
        description: `Time to explore ${_sanitizedDayCity || 'at your own pace'}`,
        duration: durStr,
        durationMinutes: durMinutes,
        sortTime: startMins,
        baseCity: _sanitizedDayCity,
        suggestions: [],
        isDisplayInjected: true,
      });
      validatedTimeline.sort(timelineSortComparator);
      debugLog(`⏳ C2 injected afternoon free-time card: ${durStr} starting ${h}:${String(m).padStart(2, '0')}`);
    })();

    // C2b: Check-in → meal gap guard. On arrival days users often see a
    // Hotel Check-In around early afternoon followed directly by Dinner with
    // no activities/lunch in between, leaving a 4-6h dead stretch. The
    // primary C2 guard above keys off lunch; when there is no lunch (typical
    // arrival day) the gap slips through. Detect (checkin → meal) pairs
    // separated by >90min with nothing between them and inject a
    // "Free Time / Relax" card covering most of the window.
    (function injectCheckinToMealGap() {
      const items = validatedTimeline
        .filter((it) => typeof it?.sortTime === 'number')
        .sort((a, b) => a.sortTime - b.sortTime);
      // Find a hotel check-in card
      const checkin = items.find((it) => {
        const t = String(it.type || '').toLowerCase();
        const act = String(it.activity || '').toLowerCase();
        return t === 'hotel-checkin' || t === 'hotel-check-in' || /check-?in/.test(act);
      });
      if (!checkin) return;
      // Compute check-in end time
      const ciDurMatch = String(checkin.duration || '15 min').match(/(\d+(?:\.\d+)?)\s*(hour|hr|min|minute)/i);
      let checkinEnd = checkin.sortTime + 15;
      if (ciDurMatch) {
        const v = parseFloat(ciDurMatch[1]);
        const u = ciDurMatch[2].toLowerCase();
        checkinEnd = checkin.sortTime + (u.startsWith('hour') || u === 'hr' ? v * 60 : v);
      }
      // Find next item strictly after the check-in ends
      const nextAny = items.find((it) => it.sortTime > checkinEnd && it !== checkin);
      if (!nextAny) return;
      // Only fire when the next item is a meal (lunch/dinner) — other activity
      // types already count as "something in the afternoon"
      if (nextAny.type !== 'meal') return;
      const gapMinutes = nextAny.sortTime - checkinEnd;
      if (gapMinutes <= 90) return; // <=1.5h → no need
      // Already a free-time card sitting in this window? skip.
      const alreadyCovered = items.some((it) => it.type === 'free-time'
        && it.sortTime >= checkinEnd - 15 && it.sortTime < nextAny.sortTime);
      if (alreadyCovered) return;
      // Start free-time 15min after check-in end; end 30min before the meal
      const startMins = checkinEnd + 15;
      const durMinutes = Math.max(30, gapMinutes - 30 - 15);
      const h = Math.floor(startMins / 60);
      const m = startMins % 60;
      const dh = Math.floor(durMinutes / 60);
      const dm = durMinutes % 60;
      const durStr = dh > 0 ? (dm > 0 ? `${dh}hr ${dm}min` : `${dh}hr`) : `${dm}min`;
      const _sanitizedDayCity = sanitizeBaseCity(dayData?.baseCity || '', '');
      validatedTimeline.push({
        type: 'free-time',
        time: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`,
        activity: 'Free Time',
        description: `Relax or explore ${_sanitizedDayCity || 'at your own pace'}`,
        duration: durStr,
        durationMinutes: durMinutes,
        sortTime: startMins,
        baseCity: _sanitizedDayCity,
        suggestions: [],
        isDisplayInjected: true,
      });
      validatedTimeline.sort(timelineSortComparator);
      debugLog(`⏳ C2b injected checkin→meal free-time card: ${durStr} starting ${h}:${String(m).padStart(2, '0')} (gap=${gapMinutes}min)`);
    })();

    // C6: Round odd times like 11:12 / 9:52 / 6:47 to the nearest 5-minute
    // grid for items whose time is engine-generated (not backed by real
    // Google Directions / Amadeus / flight-pick data). Skip anything that
    // has a verifiable external time source.
    const TIME_FIXED_TYPES = new Set([
      'inter-city-travel', 'flight', 'airport-transit', 'station-transit',
      'port-transit', 'arrival-customs', 'arrival-immigration', 'baggage-claim',
      'hotel-checkout', 'transit', 'long-distance-transfer', 'overnight-journey',
      'overnight-arrival',
    ]);
    const roundTo5 = (mins) => {
      if (typeof mins !== 'number' || !Number.isFinite(mins)) return mins;
      return Math.round(mins / 5) * 5;
    };
    validatedTimeline.forEach((item) => {
      if (!item || typeof item !== 'object') return;
      if (item.isTravel) return;
      if (TIME_FIXED_TYPES.has(item.type)) return;
      if (typeof item.sortTime !== 'number' || !Number.isFinite(item.sortTime)) return;
      const originalSort = item.sortTime;
      const rounded = roundTo5(originalSort);
      if (rounded === originalSort) return;
      item.sortTime = rounded;
      const h = Math.floor(((rounded % 1440) + 1440) % 1440 / 60);
      const m = ((rounded % 1440) + 1440) % 1440 % 60;
      item.time = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    });

    // P2: Drop trivially-short intra-day transit cards (< 3 min).
    // "Transit · 1 min" / "Transit · 2 min" cards are visual clutter in both
    // the UI timeline and the PDF — the two venues are effectively at the
    // same location. We only filter SHORT-HOP transit types; never
    // inter-city-travel, flights, airport/station/port transfers, or
    // long-distance transfers, which always matter even when brief.
    const SHORT_HOP_TRANSIT_TYPES = new Set(['transit', 'return-transit', 'hotel-transit', 'drive']);
    const MIN_TRANSIT_MINUTES = 3;
    const _preFilterLen = validatedTimeline.length;
    const finalTimelineFiltered = validatedTimeline.filter((item) => {
      if (!item || !SHORT_HOP_TRANSIT_TYPES.has(item.type)) return true;
      // Prefer explicit numeric field, then parse the string
      let mins = null;
      if (typeof item.durationMinutes === 'number' && Number.isFinite(item.durationMinutes)) {
        mins = item.durationMinutes;
      } else if (item.duration) {
        mins = parseDurationToMinutes(item.duration);
      }
      if (mins == null || !Number.isFinite(mins)) return true; // unknown → keep
      return mins >= MIN_TRANSIT_MINUTES;
    });
    if (finalTimelineFiltered.length !== _preFilterLen) {
      debugLog(`🧹 Dropped ${_preFilterLen - finalTimelineFiltered.length} sub-${MIN_TRANSIT_MINUTES}min transit card(s) from Day ${dayIndex + 1}`);
    }

    debugLog(`🏁 Day ${dayIndex + 1} timeline complete: ${finalTimelineFiltered.length} items (${finalTimelineFiltered.filter(i => transitTypes.includes(i.type)).length} transit cards)`);
    return finalTimelineFiltered;
}
